"use client"

// src/pages/Examen.js
import React, { useEffect, useMemo, useRef, useState, useCallback } from "react"
import { useParams, useNavigate, useSearchParams, useOutletContext } from "react-router-dom"
import { useFuncion } from "../context/FuncionContext"
import { useAuth } from "../context/AuthContext"
import PreguntaExamTestRenderer from "./tareas/renderers/PreguntaExamTestRenderer"
import { CheckCircle2 } from "lucide-react"
import useExamKiosk from "../hooks/useExamKiosk"
import { useMediaPermissions } from "../hooks/useMediaPermissions"
import ConfirmActionModal from "../components/assessment/ConfirmActionModal";
import AssessmentResultCard from "../components/assessment/AssessmentResultCard";

/* ==========================
   Utilidades varias
   ========================== */

// ====== Proctor metrics (cache + por minuto) ======
const buildProctorMetricsKey = ({ presentacionId, horarioId, codigoCurso }) => {
  if (presentacionId != null && Number(presentacionId) > 0) {
    return `exam_proctor_metrics:${presentacionId}`;
  }

  if (horarioId != null && Number(horarioId) > 0) {
    return `exam_proctor_metrics:horario:${horarioId}`;
  }

  return `exam_proctor_metrics:curso:${codigoCurso}`;
}


const fmtScore = (v) => {
  const n = Number(v)
  if (!Number.isFinite(n)) return 0
  return (Math.round(n * 100) / 100).toFixed(2)
}
// ====== Kiosk count + log (separados) ======
const KIOSK_MAX_TOTAL = 6

// ✅ contador: MISMA key que usa useExamKiosk (storageKey)
// ✅ log: key aparte, no interfiere con el hook
const buildKioskLogKey = (storageKey) => `${storageKey}:log`

const safeParse = (s, def = null) => {
  try { return JSON.parse(s) } catch { return def }
}

// lee contador aunque sea número, string o JSON
const loadKioskCount = (key) => {
  try {
    if (typeof window === "undefined") return 0
    const raw = localStorage.getItem(key)
    if (raw == null) return 0
    // si guardaron JSON {count}
    const obj = safeParse(raw, null)
    if (obj && typeof obj === "object") return Math.max(0, Number(obj.count || 0) || 0)
    // si guardaron "5" o 5
    return Math.max(0, Number(raw) || 0)
  } catch {
    return 0
  }
}

const pushKioskLog = (logKey, reason = "violation") => {
  try {
    if (typeof window === "undefined") return
    const cur = safeParse(localStorage.getItem(logKey), null)
    const arr = Array.isArray(cur) ? cur : []
    arr.push({ ts: Date.now(), reason: String(reason || "violation") })
    localStorage.setItem(logKey, JSON.stringify(arr.slice(-80)))
  } catch { }
}




const fmtDate = (d) => {
  try {
    const onlyDate = String(d).slice(0, 10)
    const [y, m, da] = onlyDate.split("-")
    return `${da}/${m}/${y}`
  } catch {
    return String(d)
  }
}
const toBool = (v) => {
  const s = String(v).toLowerCase()
  return v === true || v === 1 || s === "1" || s === "true" || s === "t"
}

const normalizeRequirementLevel = (v) => {
  const s = String(v || "no_requerido").toLowerCase().trim()
  if (["requerido", "opcional", "no_requerido"].includes(s)) return s
  return "no_requerido"
}

const toPositiveInt = (v, def = 0) => {
  const n = Number(v)
  return Number.isFinite(n) && n > 0 ? Math.floor(n) : def
}

const toNullableNumber = (v) => {
  if (v === "" || v == null) return null
  const n = Number(v)
  return Number.isFinite(n) ? n : null
}

const clampNumber = (value, min, max) => {
  const n = Number(value)
  if (!Number.isFinite(n)) return min
  return Math.max(min, Math.min(max, n))
}

const CAMERA_HEALTH_LIMITS = {
  sampleMs: 500,

  // Frame pequeño para analizar rápido sin cargar CPU.
  width: 192,
  height: 144,

  // Luz general.
  darkLumaMax: 38,
  stableLumaMin: 42,
  stableLumaMax: 240,

  // Sobreexposición: mucha luz blanca también es mala.
  overBrightLumaMin: 245,
  overBrightRatioMax: 0.35,

  // Contraste mínimo para evitar imagen lavada, borrosa o tapada.
  contrastMin: 12,
  centerContrastMin: 8,
  centerLumaMin: 38,

  // Movimiento.
  motionDiffMin: 0.9,
  motionRecoverDiffMin: 1.35,

  // Rostro / encuadre.
  faceDetectEveryMs: 800,

  // Balanceado: evita falsos positivos, pero no rechaza rostros reales por estar
  // un poco lejos, cerca o descentrados.
  faceMinAreaPct: 0.02,
  faceMaxAreaPct: 0.75,
  faceCenterTolerance: 0.65,
  faceMinScore: 0.30,

  // Si el navegador no soporta FaceDetector, usamos una validación visual
  // conservadora. No es reconocimiento facial, pero evita bloquear navegadores
  // donde FaceDetector no existe.
  allowQualityFallbackAsFace: true,

  // Validación inicial antes de entrar.
  videoReadyTimeoutMs: 5000,
  verifyDurationMs: 7200,
  verifyMinFrames: 8,
  verifyMinMotionHits: 2,

  // No exijas 3/3, porque FaceDetector puede fallar muestras reales.
  verifyMinFaceHits: 1,
  verifyMinFaceChecks: 2,

  darkGraceMs: 1200,
  noMotionGraceMs: 9000,
  noMotionRecoverMs: 1800,

  // ✅ Durante examen: avisos más razonables, no tan inmediatos.
  examDarkWarningMs: 3000,
  examNoMotionWarningMs: 12000,

  // ✅ Evita spam de avisos y snapshots por el mismo problema.
  examWarningCooldownMs: 12000,
  incidentSnapshotCooldownMs: 30000,

  // ✅ Primera foto obligatoria después de unos segundos, no instantánea.
  firstRequiredSnapshotMinMs: 5000,
  firstRequiredSnapshotMaxMs: 15000,

}

const cleanCameraHealth = () => ({
  ready: false,
  videoReady: false,

  ok: true,
  validated: false,
  validating: false,

  dark: false,
  overBright: false,
  lowContrast: false,
  noMotion: false,

  faceChecked: false,
  faceVisible: null,
  faceScore: null,
  faceCount: 0,
  faceBox: null,
  faceSource: null,
  faceReason: "",

  avgLuma: null,
  contrast: null,
  centerLuma: null,
  centerContrast: null,
  overBrightRatio: null,
  darkRatio: null,

  motionScore: null,
  motionHits: 0,
  frames: 0,

  darkMs: 0,
  noMotionMs: 0,

  reason: "",
  checkedAt: null,
})

const isVideoFrameReady = (videoEl) => {
  try {
    return !!(
      videoEl &&
      videoEl.srcObject &&
      videoEl.readyState >= 2 &&
      Number(videoEl.videoWidth || 0) > 0 &&
      Number(videoEl.videoHeight || 0) > 0
    )
  } catch {
    return false
  }
}

const clamp01 = (v) => {
  const n = Number(v)
  if (!Number.isFinite(n)) return 0
  return Math.max(0, Math.min(1, n))
}

const safeRound = (v, decimals = 0) => {
  const n = Number(v)
  if (!Number.isFinite(n)) return null
  const p = Math.pow(10, decimals)
  return Math.round(n * p) / p
}

const calcStdDev = (sum, sumSq, count) => {
  if (!count || count <= 0) return 0
  const mean = sum / count
  const variance = Math.max(0, (sumSq / count) - (mean * mean))
  return Math.sqrt(variance)
}

const hasNativeFaceDetector = () => {
  try {
    return !!(window.FaceDetector || window.webkitFaceDetector)
  } catch {
    return false
  }
}

const getTrackDeviceId = (track) => {
  try {
    return track?.getSettings?.()?.deviceId || ""
  } catch {
    return ""
  }
}

const stopStreamTracks = (stream) => {
  try {
    stream?.getTracks?.().forEach((track) => {
      try {
        if (track?.readyState !== "ended") track.stop()
      } catch { }
    })
  } catch { }
}

const assertSelectedDeviceWasUsed = ({ track, selectedId, kind = "cámara", streamToStop = null }) => {
  if (!selectedId || !track) return true

  const actualId = getTrackDeviceId(track)

  // Si el navegador expone el deviceId y no coincide, NO aceptes el fallback.
  if (actualId && actualId !== selectedId) {
    stopStreamTracks(streamToStop)

    throw new Error(
      `El navegador abrió otra ${kind} distinta a la seleccionada. Vuelve a elegir el dispositivo o revisa si la ${kind} está ocupada por otra aplicación.`
    )
  }

  return true
}

const buildCameraQualityReason = (flags = {}) => {
  if (flags.dark) {
    return "La cámara se ve muy oscura. Mejora la iluminación frontal para continuar."
  }

  if (flags.overBright) {
    return "La cámara está sobreexpuesta. Reduce la luz directa o evita estar contra una ventana."
  }

  if (flags.lowContrast) {
    return "La imagen no se ve clara. Limpia la cámara, mejora la luz frontal o evita sombras fuertes."
  }

  if (flags.faceChecked && flags.faceVisible === false) {
    return flags.faceReason || "No se aprecia claramente tu rostro. Colócate de frente y centra tu rostro en la cámara."
  }

  if (flags.noMotion) {
    return "No se detecta movimiento en la cámara. Muévete ligeramente para verificar que la cámara está activa."
  }

  return ""
}

const fmtTime = (t) => String(t).slice(0, 5)

const getItemHorario = (it) => {
  const v =
    it?.horario_examen_id ??
    it?.horario_id ??
    it?.exam?.horario_examen_id ??
    it?.examen?.horario_examen_id ??
    it?.horario?.id ??
    null
  return v != null ? Number(v) : null
}

// ===== Helpers de shape/normalización
const ensureJSON = (v, def = {}) => {
  if (v == null) return def
  if (typeof v === "string") {
    try { return JSON.parse(v) } catch { return def }
  }
  return typeof v === "object" ? v : def
}

const sanitizeForSchema = (q, raw) => {
  const t = (q?.tipo || q?.tipo_pregunta || "").toLowerCase()
  if (raw == null) return null
  if (t === "seleccion_multiple" || t === "completar" || t === "ordenamiento" || t === "emparejamiento") {
    const arr = Array.isArray(raw) ? raw : [raw]
    const clean = arr.map((v) => String(v).trim()).filter(Boolean)
    return clean.length ? clean : null
  }
  if (t === "seleccion_simple" || t === "single_choice" || t === "multiple_choice") {
    const v = Array.isArray(raw) ? raw[0] : raw
    const s = String(v ?? "").trim()
    return s || null
  }
  const s = String(raw).trim()
  return s || null
}

// ===== Clave canónica para dedupe
const canonicalKey = (q) => {
  if (q?.fuente && q?.fuente_id != null) return `${q.fuente}:${q.fuente_id}`
  const sid = String(q?.id ?? "")
  const m = sid.match(/^([a-z]+:\d+)(?::\d+)?$/i)
  if (m) return m[1]
  return sid || ""
}
const dedupeByCanonicalKey = (arr) => {
  const seen = new Set()
  const out = []
  for (const q of arr || []) {
    const key = canonicalKey(q)
    if (key && seen.has(key)) continue
    if (key) seen.add(key)
    out.push(q)
  }
  return out
}

// ======= Resultado estilo NivelTest
const normalizeResult = (res = {}) => {
  if (!res) return null
  const s = res.summary || {}

  const porcentaje = Number(
    res.porcentaje_obtenido ?? res.porcentaje ?? s.porcentaje ?? res.scorePct ?? res.score_pct ?? 0
  )
  const puntosGanados = Number(res.puntuacion_obtenida ?? s.puntosGanados ?? res.score ?? 0)
  const puntosTotal = Number(res.puntuacion_maxima ?? s.puntosTotal ?? res.max ?? res.max_score ?? 0)
  const correctas = Number(
    res.respuestas_correctas ?? res.correctas ?? s.correctas
    ?? (Array.isArray(s.detalle) ? s.detalle.filter(d => d.correcto === true).length : 0)
  )
  const incorrectas = Number(
    res.respuestas_incorrectas ?? res.incorrectas ?? s.incorrectas
    ?? (Array.isArray(s.detalle) ? s.detalle.filter(d => d.correcto === false).length : 0)
  )

  return {
    porcentaje: Math.round(porcentaje),
    aprobado: !!(res.aprobado ?? res.passed ?? s.aprobado),
    puntuacion_obtenida: puntosGanados,
    puntuacion_maxima: puntosTotal,
    respuestas_correctas: correctas,
    respuestas_incorrectas: incorrectas,
    tiempo_empleado_segundos: Number(res.tiempo_empleado_segundos ?? res.time_secs ?? 0),
    intento_ordinal: Number(res.intento_ordinal ?? res.attempt ?? 1),
    eligible_coins: !!(res.eligible_coins ?? res.eligibleCoins),
    utmcoins_awarded: Number(res.utmcoins_awarded ?? res.coins ?? 0),
    premio_utm: Number(res.premio_utm ?? res.prize ?? 0),
    detalle: Array.isArray(res.detalle) ? res.detalle
      : Array.isArray(s.detalle) ? s.detalle
        : [],
  }
}

const mergeDetalles = (serverDet = [], localDet = []) => {
  if (!serverDet?.length && localDet?.length) return localDet
  const keyOf = (it) => String(
    it.preguntaId ??
    it.pregunta_id ??
    it.questionId ??
    it.question_id ??
    it.id ??
    it.idx ??
    ""
  )

  const localMap = new Map(localDet.map(d => [keyOf(d), d]))
  return serverDet.map(sd => {
    const k = keyOf(sd)
    const ld = localMap.get(k)
    return {
      ...(ld || {}),
      ...sd,
      respuesta_texto: sd.respuesta_texto ?? ld?.respuesta_texto ?? null,
      esperado_texto: sd.esperado_texto ?? ld?.esperado_texto ?? null,
      enunciado: sd.enunciado ?? ld?.enunciado ?? "",
    }
  })
}

const readOptionsForView = (q) => {
  const ops = q?.opciones
  const toItem = (o, i) => ({ id: String(o?.id ?? i + 1), texto: o?.texto ?? o?.label ?? String(o?.value ?? "") })
  if (!ops) return []
  if (Array.isArray(ops)) return ops.map(toItem)
  if (Array.isArray(ops?.opciones)) return ops.opciones.map(toItem)
  if (Array.isArray(ops?.items)) return ops.items.map(toItem)
  return []
}
const getEnunciado = (q) => q?.enunciado ?? q?.texto ?? q?.pregunta ?? q?.title ?? q?.titulo ?? "Pregunta"

const reasonMap = {
  attention_required: "Debes mantener el examen en foco y en pantalla completa.",
  visibility_hidden: "Cambiaste de pestaña o la página se ocultó.",
  window_blur: "Saliste de la ventana del examen.",
  fullscreen_exit: "Saliste de pantalla completa.",
  page_hide: "La página se ocultó o se navegó fuera del examen.",
  hotkey_navigation: "Se detectó un atajo de teclado no permitido (navegación o copia).",
  request_fullscreen: "Se solicitó volver a pantalla completa.",
  start: "Debes mantener el foco en el examen y estar en pantalla completa.",
  sync: "Vuelve al modo seguro para continuar.",
  focus: "Vuelve al examen para continuar.",
  camera_dark: "La cámara está demasiado oscura. Mejora la iluminación y mantén tu rostro visible.",
  camera_no_motion: "No se detecta movimiento en la cámara. Muévete ligeramente y mantén tu rostro visible.",
  visibility_change: "Regresa al examen para continuar.",
  fullscreen_enter: "Se detectó pantalla completa. Continúa el examen.",
}

const sanitizeMotivo = (m) => {
  const s = String(m || "").toLowerCase().trim()
  // ✅ policy se mantiene como policy
  if (["envio", "timeout", "docente", "abandonado", "policy"].includes(s)) return s
  return "envio"
}

const pickStudentIdentity = (...sources) => {
  const src = Object.assign({}, ...sources.filter(Boolean));

  const nombres = String(
    src.nombres ??
    src.firstName ??
    src.nombre ??
    src.name ??
    ""
  ).trim();

  const apellidos = String(
    src.apellidos ??
    src.lastName ??
    src.apellido ??
    ""
  ).trim();

  const fullName = String(
    src.nombre_completo ??
    src.full_name ??
    src.fullName ??
    `${nombres} ${apellidos}`.trim()
  ).trim();

  const email = String(src.email ?? src.correo ?? "").trim();

  const codigo = String(
    src.codigo_institucional ??
    src.cedula ??
    src.identificacion ??
    src.codigo ??
    src.codigo_matricula ??
    ""
  ).trim();

  return {
    fullName: fullName || "Estudiante",
    email: email || "Sin correo registrado",
    codigo: codigo || "Sin código institucional",
  };
};


/* ========================
   Componente principal
   ======================== */
export default function Examen() {
  const { codigoCurso } = useParams()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const qaMode = searchParams.get("qa") === "1" || searchParams.get("qa") === "true"

  const [deviceModalOpen, setDeviceModalOpen] = useState(false);
  const [deviceDraft, setDeviceDraft] = useState({ camId: "", micId: "" });
  const [deviceSwitching, setDeviceSwitching] = useState({ cam: false, mic: false });

  // idle | preview | validating | valid | invalid
  const [cameraValidationStep, setCameraValidationStep] = useState("idle");

  const validatedProctorDevicesRef = useRef({
    camId: null,
    micId: null,
    at: 0,
  });

  const [proctorSetupReason, setProctorSetupReason] = useState("");
  const [examRuntimeConfig, setExamRuntimeConfig] = useState(null)
  const [examStudentIdentity, setExamStudentIdentity] = useState(null)

  const { setHeaderVisible, setFooterVisible } = useOutletContext() || {}

  const { user } = useAuth()
  const {
    resolveStudentCourseByCode,
    fetchMyExamBookings,
    fetchCourseExams,
    startExam,
    getExamWithQuestions,
    resumeRunningExamAttempt,
    saveExamProgress,
    finishExam,
    uploadProctorSnapshot,
    uploadProctorMetrics,
    fetchExamTerms,
    acceptExamTerms,
    validateExamAccessCode,
  } = useFuncion()

  const servedQuestionIdsRef = useRef([])

  // Refrescar refs de funciones
  const fnsRef = useRef({
    resolveStudentCourseByCode,
    fetchMyExamBookings,
    fetchCourseExams,
    startExam,
    getExamWithQuestions,
    resumeRunningExamAttempt,
    saveExamProgress,
    finishExam,
    uploadProctorSnapshot,
    uploadProctorMetrics,
    validateExamAccessCode,
  })
  useEffect(() => {
    fnsRef.current = {
      resolveStudentCourseByCode,
      fetchMyExamBookings,
      fetchCourseExams,
      startExam,
      getExamWithQuestions,
      resumeRunningExamAttempt,
      saveExamProgress,
      finishExam,
      uploadProctorSnapshot,
      uploadProctorMetrics, // ✅ AÑADE ESTO
      validateExamAccessCode,
    }
  }, [
    resolveStudentCourseByCode,
    fetchMyExamBookings,
    fetchCourseExams,
    startExam,
    getExamWithQuestions,
    resumeRunningExamAttempt,
    saveExamProgress,
    finishExam,
    uploadProctorSnapshot,
    uploadProctorMetrics, // ✅ y en deps
    validateExamAccessCode,
  ])


  // ===== Salir de pantalla completa (cross-browser) =====
  const exitFullscreenSafe = useCallback(async () => {
    try {
      const d = document

      // Estándar
      if (d.fullscreenElement && d.exitFullscreen) {
        await d.exitFullscreen()
        return
      }

      // Safari
      if (d.webkitFullscreenElement && d.webkitExitFullscreen) {
        d.webkitExitFullscreen()
        return
      }

      // Antiguos (por si acaso)
      if (d.mozFullScreenElement && d.mozCancelFullScreen) {
        d.mozCancelFullScreen()
        return
      }
      if (d.msFullscreenElement && d.msExitFullscreen) {
        d.msExitFullscreen()
        return
      }
    } catch { /* noop */ }
  }, [])


  const requestFullscreenSafe = useCallback(async () => {
    try {
      const el = document.documentElement

      if (document.fullscreenElement) return true

      if (el.requestFullscreen) {
        await el.requestFullscreen()
        return true
      }

      if (el.webkitRequestFullscreen) {
        el.webkitRequestFullscreen()
        return true
      }

      if (el.mozRequestFullScreen) {
        el.mozRequestFullScreen()
        return true
      }

      if (el.msRequestFullscreen) {
        el.msRequestFullscreen()
        return true
      }
    } catch {
      return false
    }

    return false
  }, [])

  const hasFullscreenSafe = useCallback(() => {
    try {
      return !!(
        document.fullscreenElement ||
        document.webkitFullscreenElement ||
        document.mozFullScreenElement ||
        document.msFullscreenElement
      )
    } catch {
      return false
    }
  }, [])


  // --------------------- State principal ---------------------
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState("")
  const [course, setCourse] = useState(null)
  const [mySchedule, setMySchedule] = useState(null)
  const [examId, setExamId] = useState(null)
  const [resumeOffer, setResumeOffer] = useState(null)

  const [codigo, setCodigo] = useState("")
  const [accesoConcedido, setAccesoConcedido] = useState(false)
  const [presentacion, setPresentacion] = useState(null)
  const hasServerPresentation = Number(presentacion?.presentacion_id) > 0

  // ✅ Estado principal del examen.
  // Debe estar ANTES de cualquier useMemo que use "examen".
  const [examen, setExamen] = useState(null)
  const [currentIndex, setCurrentIndex] = useState(0)
  const [answers, setAnswers] = useState({})
  const [enviado, setEnviado] = useState(false)
  const [resultado, setResultado] = useState(null)

  const [timeLeft, setTimeLeft] = useState(0)
  const [totalSeconds, setTotalSeconds] = useState(0)
  const [showOnlyWrong, setShowOnlyWrong] = useState(true)

  const timerRef = useRef(null)
  const saveTimer = useRef(null)
  const lastSavedRef = useRef("")
  const [waitingAfterTimeout, setWaitingAfterTimeout] = useState(false)
  const isUnloadingRef = useRef(false)

  const effectiveStudentIdentity = useMemo(() => {
    return pickStudentIdentity(
      user,
      course?.student,
      mySchedule?.student,
      mySchedule?.estudiante,
      mySchedule?.usuario,
      examStudentIdentity
    );
  }, [
    user,
    course?.student,
    mySchedule?.student,
    mySchedule?.estudiante,
    mySchedule?.usuario,
    examStudentIdentity,
  ]);

  // ================= KIOSK =================
  const [kioskWarn, setKioskWarn] = useState(false)
  const [autoClosedByPolicy, setAutoClosedByPolicy] = useState(false)

  const [kioskWarnCountdown, setKioskWarnCountdown] = useState(0)
  const [kioskWarnReason, setKioskWarnReason] = useState("attention_required")

  const kioskWarnDeadlineRef = useRef(0)
  const kioskWarnTimerRef = useRef(null)
  const kioskWarnReasonRef = useRef("attention_required")
  const kioskWarnModeRef = useRef("security") // security | camera
  const kioskWarnReturningRef = useRef(0)

  const KIOSK_WARNING_SECONDS = 8

  const policyLimitTriggeredRef = useRef(false)
  const [policyLimitVisible, setPolicyLimitVisible] = useState(false)
  const [policySubmitting, setPolicySubmitting] = useState(false)

  const [waitingSecurityState, setWaitingSecurityState] = useState("idle")
  // idle | validated | privacy | strike_alert | kicked

  const [waitingWarnings, setWaitingWarnings] = useState(0)
  const [waitingCountdown, setWaitingCountdown] = useState(0)
  const [waitingStrikeReason, setWaitingStrikeReason] = useState("")
  const waitingTimerRef = useRef(null)
  const waitingStrikeLockRef = useRef(0)
  const [accessChecking, setAccessChecking] = useState(false)
  const [accessVerified, setAccessVerified] = useState(false)

  const validateCodeInFlightRef = useRef(false)
  const lastValidatedCodeRef = useRef("")

  const enterExamInFlightRef = useRef(false)

  const validateCodeQuietUntilRef = useRef(0)

  const [codeAttemptsRemaining, setCodeAttemptsRemaining] = useState(3)
  const [codeLockedUntil, setCodeLockedUntil] = useState(null)
  const [codeLockSecondsLeft, setCodeLockSecondsLeft] = useState(0)

  const codeLockedRef = useRef(false)

  const [codeValidationState, setCodeValidationState] = useState("idle")
  // idle | checking | ok | error | locked

  const [codeValidationMessage, setCodeValidationMessage] = useState("")
  const micRuntimeStateRef = useRef({
    loudSince: 0,
    lastWarningAt: 0,
  })

  const [termsModal, setTermsModal] = useState({
    open: false,
    loading: false,
    accepting: false,
    data: null,
    pendingStart: null,
  });

  const kioskStorageKey = useMemo(() => {
    if (hasServerPresentation) {
      return `exam:kiosk:${presentacion.presentacion_id}`;
    }

    if (mySchedule?.horario_id) {
      return `exam:kiosk:horario:${mySchedule.horario_id}`;
    }

    return `exam:kiosk:curso:${codigoCurso}`;
  }, [hasServerPresentation, presentacion?.presentacion_id, mySchedule?.horario_id, codigoCurso]);


  // ✅ MISMA key de contador que usa el hook
  const kioskCountKey = kioskStorageKey
  const kioskLogKey = useMemo(() => buildKioskLogKey(kioskStorageKey), [kioskStorageKey])

  const accessCodeLockKey = useMemo(() => {
    const horarioId = Number(mySchedule?.horario_id || 0) || "sin-horario";
    const userId = Number(user?.id || 0) || "sin-user";
    return `exam:access-code-lock:${horarioId}:${userId}`;
  }, [mySchedule?.horario_id, user?.id]);

  const proctorDeviceValidationKey = useMemo(() => {
    const horarioId = Number(mySchedule?.horario_id || 0) || "sin-horario"
    const userId = Number(user?.id || 0) || "sin-user"
    return `exam:proctor-devices-valid:${horarioId}:${userId}`
  }, [mySchedule?.horario_id, user?.id])

  const readValidatedProctorDevices = useCallback(() => {
    const maxAgeMs = 30 * 60 * 1000

    const current = validatedProctorDevicesRef.current || {}

    if (current?.at && Date.now() - Number(current.at) <= maxAgeMs) {
      return current
    }

    try {
      const cached = safeParse(sessionStorage.getItem(proctorDeviceValidationKey), null)

      if (cached?.at && Date.now() - Number(cached.at) <= maxAgeMs) {
        validatedProctorDevicesRef.current = {
          camId: cached.camId || null,
          micId: cached.micId || null,
          at: Number(cached.at) || Date.now(),
        }

        return validatedProctorDevicesRef.current
      }
    } catch { }

    return current
  }, [proctorDeviceValidationKey])

  const readAccessCodeLock = useCallback(() => {
    try {
      const raw = localStorage.getItem(accessCodeLockKey);
      if (!raw) return null;

      const parsed = JSON.parse(raw);
      if (!parsed?.locked_until) return null;

      const untilMs = new Date(parsed.locked_until).getTime();

      if (!Number.isFinite(untilMs) || untilMs <= Date.now()) {
        localStorage.removeItem(accessCodeLockKey);
        return null;
      }

      return parsed;
    } catch {
      return null;
    }
  }, [accessCodeLockKey]);

  const saveAccessCodeLock = useCallback((lockedUntil) => {
    if (!lockedUntil) return;

    try {
      localStorage.setItem(
        accessCodeLockKey,
        JSON.stringify({
          locked_until: lockedUntil,
          saved_at: new Date().toISOString(),
        })
      );
    } catch { }
  }, [accessCodeLockKey]);

  const clearAccessCodeLock = useCallback(() => {
    try {
      localStorage.removeItem(accessCodeLockKey);
    } catch { }
  }, [accessCodeLockKey]);

  const kioskBaseCount = useMemo(() => loadKioskCount(kioskCountKey), [kioskCountKey])

  /* ---------- Flags calculadas temprano ---------- */
  const isVirtualExam = useMemo(() => toBool(mySchedule?.is_virtual), [mySchedule?.is_virtual])

  useEffect(() => {
    const localLock = readAccessCodeLock();

    if (localLock?.locked_until) {
      setCodeLockedUntil(localLock.locked_until);
    }
  }, [readAccessCodeLock]);

  useEffect(() => {
    if (!codeLockedUntil) {
      setCodeLockSecondsLeft(0);
      return;
    }

    const tick = () => {
      const untilMs = new Date(codeLockedUntil).getTime();
      const left = Math.max(0, Math.ceil((untilMs - Date.now()) / 1000));

      setCodeLockSecondsLeft(left);
      if (left <= 0) {
        setCodeLockedUntil(null);
        codeLockedRef.current = false;
        clearAccessCodeLock();
        setCodeAttemptsRemaining(3);
        setCodeValidationState("idle");
        setCodeValidationMessage("");
      }
    };

    tick();

    const t = setInterval(tick, 1000);

    return () => clearInterval(t);
  }, [codeLockedUntil, clearAccessCodeLock]);

  const needsTeacherEnable = useMemo(() => {
    if (qaMode) return false

    return toBool(
      examRuntimeConfig?.habilitacion_unitaria_estudiantes ??
      examen?.exam_config?.habilitacion_unitaria_estudiantes ??
      examen?.habilitacion_unitaria_estudiantes ??
      mySchedule?.habilitacion_unitaria_estudiantes ??
      false
    )
  }, [
    qaMode,
    examRuntimeConfig?.habilitacion_unitaria_estudiantes,
    examen?.exam_config?.habilitacion_unitaria_estudiantes,
    examen?.habilitacion_unitaria_estudiantes,
    mySchedule?.habilitacion_unitaria_estudiantes,
  ])

  const codeLocked = codeLockSecondsLeft > 0;
  const waitingTeacherEnable = needsTeacherEnable && mySchedule?.habilitado !== true

  useEffect(() => {
    codeLockedRef.current = codeLocked

    if (codeLocked) {
      setCodeValidationState("locked")
    }
  }, [codeLocked])

  useEffect(() => {
    if (!waitingTeacherEnable) return

    setAccessVerified(false)
    lastValidatedCodeRef.current = ""

    if (!codeLockedRef.current && !codeLocked) {
      setCodeValidationState("idle")
      setCodeValidationMessage("")
    }
  }, [waitingTeacherEnable, codeLocked])

  const codeLockLabel = useMemo(() => {
    const m = Math.floor(codeLockSecondsLeft / 60);
    const s = codeLockSecondsLeft % 60;
    return `${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
  }, [codeLockSecondsLeft]);

  // ✅ SOLO PRESENCIAL: bandera para activar incumplimientos (kiosk)
  const presencialKioskEnabled = useMemo(() => {
    const v =
      mySchedule?.kiosk_presencial ??
      mySchedule?.modo_seguro_presencial ??
      mySchedule?.requiere_modo_seguro ?? // fallback si ya existe en tu backend
      false
    return toBool(v)
  }, [
    mySchedule?.kiosk_presencial,
    mySchedule?.modo_seguro_presencial,
    mySchedule?.requiere_modo_seguro,
  ])


  // ===================== KIOSK MODE (HARD / SOFT / OFF) =====================
  // HARD: usa cierre_por_penalizaciones + limite_penalizaciones.
  // SOFT: modo seguro sin cierre automático.
  // OFF : no corre kiosk.
  const cierrePorPenalizaciones = useMemo(() => {
    return toBool(
      examRuntimeConfig?.cierre_por_penalizaciones ??
      examen?.exam_config?.cierre_por_penalizaciones ??
      examen?.cierre_por_penalizaciones ??
      mySchedule?.cierre_por_penalizaciones ??
      false
    )
  }, [
    examRuntimeConfig?.cierre_por_penalizaciones,
    examen?.exam_config?.cierre_por_penalizaciones,
    examen?.cierre_por_penalizaciones,
    mySchedule?.cierre_por_penalizaciones,
  ])

  const limitePenalizaciones = useMemo(() => {
    return toPositiveInt(
      examRuntimeConfig?.limite_penalizaciones ??
      examen?.exam_config?.limite_penalizaciones ??
      examen?.limite_penalizaciones ??
      mySchedule?.limite_penalizaciones ??
      0,
      0
    )
  }, [
    examRuntimeConfig?.limite_penalizaciones,
    examen?.exam_config?.limite_penalizaciones,
    examen?.limite_penalizaciones,
    mySchedule?.limite_penalizaciones,
  ])

  const kioskPolicyEnabled =
    !qaMode &&
    cierrePorPenalizaciones &&
    limitePenalizaciones > 0

  const kioskMode = useMemo(() => {
    if (qaMode) return "off"

    // Solo cierra/envía automáticamente si el horario lo configuró.
    if (kioskPolicyEnabled) return "hard"

    // Si no hay cierre por penalizaciones, puede seguir funcionando como modo seguro suave.
    if (isVirtualExam || presencialKioskEnabled) return "soft"

    return "off"
  }, [qaMode, kioskPolicyEnabled, isVirtualExam, presencialKioskEnabled])

  const enableKiosk = kioskMode !== "off"
  const hardKiosk = kioskMode === "hard"
  const softKiosk = kioskMode === "soft"

  // HARD usa el límite de la tabla horarios_examenes.
  // SOFT usa límite alto para nunca cerrar automáticamente.
  const kioskMaxViolations = hardKiosk ? limitePenalizaciones : 999999

  const kioskLimitLabel = hardKiosk ? String(limitePenalizaciones) : "sin cierre automático"

  const getKioskPolicyFromConfig = useCallback((config = {}) => {
    const cierre = toBool(config?.cierre_por_penalizaciones)
    const limite = toPositiveInt(config?.limite_penalizaciones, 0)

    const hard =
      !qaMode &&
      cierre &&
      limite > 0

    return {
      enabled: hard,
      hard,
      maxViolations: hard ? limite : 999999,
    }
  }, [qaMode])


  const doSubmitRef = useRef(null)
  const closeKioskWarning = useCallback(() => {
    try {
      if (kioskWarnTimerRef.current) {
        clearInterval(kioskWarnTimerRef.current)
      }
    } catch { }

    kioskWarnTimerRef.current = null
    kioskWarnDeadlineRef.current = 0

    kioskWarnReasonRef.current = "attention_required"
    kioskWarnModeRef.current = "security"

    setKioskWarn(false)
    setKioskWarnCountdown(0)
    setKioskWarnReason("attention_required")
  }, [])

  const openKioskWarning = useCallback((seconds = 8, reason = "attention_required", mode = "security") => {
    if (policyLimitTriggeredRef.current) return
    // No abrir avisos si estamos en el proceso de regresar (grace period)
    if (Date.now() < kioskWarnReturningRef.current) return

    const now = Date.now()

    const applyReason = () => {
      kioskWarnReasonRef.current = reason || "attention_required"
      kioskWarnModeRef.current = mode || "security"
      setKioskWarnReason(reason || "attention_required")
    }

    const remaining = Math.max(0, kioskWarnDeadlineRef.current - now)
    const isMaintenance = reason === "heartbeat" || reason === "sync"

    // ✅ Si ya hay una alerta activa y el modal está visible:
    // - Si es un evento de mantenimiento (heartbeat), mantenemos el tiempo actual para que el castigo siga su curso.
    // - Si ya queda poco tiempo, NO lo reiniciamos para evitar que el usuario "alargue" el aviso indefinidamente.
    if (kioskWarn && remaining > 0 && (isMaintenance || remaining > (seconds - 1) * 1000)) {
      // Seguridad normal tiene prioridad sobre cámara.
      if (mode === "security" || kioskWarnModeRef.current !== "security") {
        applyReason()
      }

      setKioskWarn(true)
      return
    }

    applyReason()

    kioskWarnDeadlineRef.current = now + Math.max(1, seconds) * 1000
    setKioskWarnCountdown(seconds)
    setKioskWarn(true)
  }, [kioskWarn])



  const kiosk = useExamKiosk({
    maxViolations: kioskMaxViolations,
    storageKey: kioskStorageKey,

    // ✅ Los strikes repetidos los controla el contador visual.
    autoRepeatStrikes: false,
    repeatStrikeMs: 4500,

    onViolation: (info) => {
      if (!enableKiosk) return
      if (policyLimitTriggeredRef.current) return

      const reason = info?.reason || "violation"
      const mode =
        info?.mode === "camera" || String(reason).startsWith("camera_")
          ? "camera"
          : "security"

      openKioskWarning(KIOSK_WARNING_SECONDS, reason, mode)

      queueMicrotask(() => {
        pushKioskLog(kioskLogKey, reason)
      })
    },

    onTooManyViolations: async () => {
      if (!enableKiosk) return

      if (hardKiosk) {
        if (policyLimitTriggeredRef.current) return

        policyLimitTriggeredRef.current = true

        closeKioskWarning()
        setAutoClosedByPolicy(true)
        setPolicyLimitVisible(true)

        try {
          pushKioskLog(kioskLogKey, "too_many_violations")
        } catch { }

        // ✅ IMPORTANTE:
        // No enviar aquí. Primero se muestra el modal de límite.
        return
      }

      // SOFT: nunca cierra automáticamente.
      openKioskWarning(KIOSK_WARNING_SECONDS, "soft_limit_reached", "security")

      try {
        pushKioskLog(kioskLogKey, "soft_limit_reached")
      } catch { }
    },

    onAttentionRequired: () => {
      if (!enableKiosk) return
      if (policyLimitTriggeredRef.current) return

      openKioskWarning(KIOSK_WARNING_SECONDS, "soft_limit_reached", "security")
    },

    countPageHide: enableKiosk,
    preventDefaultOnHotkeys: enableKiosk,
  })

  const grantExamUiGrace = useCallback((ms = 1200) => {
    try {
      kiosk.grantGrace?.(ms)
    } catch { }

    if (!policyLimitTriggeredRef.current) {
      closeKioskWarning()
    }
  }, [kiosk, closeKioskWarning])


  const confirmPolicyLimitAndSubmit = useCallback(async () => {
    if (policySubmitting) return

    setPolicySubmitting(true)

    try {
      closeKioskWarning()

      try {
        await kiosk.stop?.()
      } catch { }

      try {
        await exitFullscreenSafe()
      } catch { }

      await doSubmitRef.current?.(true, "policy")

      setPolicyLimitVisible(false)
    } catch (err) {
      setError(err?.message || "No se pudo finalizar el examen por política de seguridad.")
    } finally {
      setPolicySubmitting(false)
    }
  }, [
    policySubmitting,
    closeKioskWarning,
    kiosk,
    exitFullscreenSafe,
  ])

  const isExamSecurityStillBlocked = useCallback(() => {
    if (Date.now() < kioskWarnReturningRef.current) return false
    try {
      const hidden = document.hidden === true
      const noFocus = document.hasFocus?.() === false

      const hasFullscreen = !!(
        document.fullscreenElement ||
        document.webkitFullscreenElement ||
        document.mozFullScreenElement ||
        document.msFullscreenElement
      )

      return hidden || noFocus || !hasFullscreen
    } catch {
      return true
    }
  }, [])

  const getExamSecurityReason = useCallback(() => {
    try {
      if (document.hidden === true) return "visibility_hidden"
      if (document.hasFocus?.() === false) return "window_blur"

      const hasFullscreen = !!(
        document.fullscreenElement ||
        document.webkitFullscreenElement ||
        document.mozFullScreenElement ||
        document.msFullscreenElement
      )

      if (!hasFullscreen) return "fullscreen_exit"
    } catch { }

    return "attention_required"
  }, [])

  const isCameraStrikeStillBlocked = useCallback((reason = "") => {
    try {
      const health = cameraHealthRef.current || cleanCameraHealth()

      if (reason === "camera_dark") {
        return (
          health.dark === true &&
          Number(health.darkMs || 0) >= CAMERA_HEALTH_LIMITS.examDarkWarningMs
        )
      }

      if (reason === "camera_no_motion") {
        return (
          health.noMotion === true &&
          Number(health.noMotionMs || 0) >= CAMERA_HEALTH_LIMITS.examNoMotionWarningMs
        )
      }

      return false
    } catch {
      return false
    }
  }, [])

  const isCurrentKioskWarningStillBlocked = useCallback(() => {
    const mode = kioskWarnModeRef.current
    const reason = kioskWarnReasonRef.current

    if (mode === "camera") {
      return isCameraStrikeStillBlocked(reason)
    }

    return isExamSecurityStillBlocked()
  }, [isCameraStrikeStillBlocked, isExamSecurityStillBlocked])

  useEffect(() => {
    try {
      if (kioskWarnTimerRef.current) {
        clearInterval(kioskWarnTimerRef.current)
      }
    } catch { }

    kioskWarnTimerRef.current = null

    if (!kioskWarn || autoClosedByPolicy || policyLimitTriggeredRef.current) {
      setKioskWarnCountdown(0)
      return
    }

    const tick = () => {
      const deadline = Number(kioskWarnDeadlineRef.current || 0)

      if (!deadline) {
        closeKioskWarning()
        return
      }

      const left = Math.max(0, Math.ceil((deadline - Date.now()) / 1000))
      setKioskWarnCountdown(left)

      if (left > 0) return

      const modeAtTimeout = kioskWarnModeRef.current
      const reasonAtTimeout = kioskWarnReasonRef.current || "attention_required"

      // ✅ Ahora soporta:
      // - security: foco / fullscreen / pestaña
      // - camera: cámara oscura / sin movimiento
      const stillBlocked = isCurrentKioskWarningStillBlocked()

      if (!stillBlocked) {
        closeKioskWarning()
        try {
          kiosk.grantGrace?.(900)
        } catch { }
        return
      }

      // Si sigue bloqueado al terminar el contador, se da el strike
      try {
        kiosk.manualStrike?.(reasonAtTimeout, {
          ignoreGrace: true,
          ignoreDebounce: true,
          forceBlocked: false,
          mode: modeAtTimeout,
        })
      } catch { }

      // Y en lugar de cerrar el modal, REINICIAMOS el contador para el próximo strike!
      const nextSeconds = modeAtTimeout === "camera" ? 8 : KIOSK_WARNING_SECONDS
      kioskWarnDeadlineRef.current = Date.now() + Math.max(1, nextSeconds) * 1000
      setKioskWarnCountdown(nextSeconds)
    }

    tick()

    kioskWarnTimerRef.current = setInterval(tick, 250)

    return () => {
      try {
        if (kioskWarnTimerRef.current) {
          clearInterval(kioskWarnTimerRef.current)
        }
      } catch { }

      kioskWarnTimerRef.current = null
    }
  }, [
    kioskWarn,
    autoClosedByPolicy,
    closeKioskWarning,
    kiosk,
    isCurrentKioskWarningStillBlocked,
    getExamSecurityReason,
  ])

  useEffect(() => {
    const inExam =
      accesoConcedido &&
      examen &&
      !waitingAfterTimeout &&
      !enviado &&
      !resultado

    if (!inExam) return

    if (!enableKiosk) {
      try { kiosk.stop?.() } catch { }
      return
    }

    let cancelled = false

    const armKiosk = async () => {
      try {
        await kiosk.start?.()

        if (hardKiosk && !document.fullscreenElement) {
          const ok = await requestFullscreenSafe()

          if (!ok && !cancelled) {
            setKioskWarn(true)
          }
        }
      } catch {
        if (!cancelled) setKioskWarn(true)
      }
    }

    armKiosk()

    return () => {
      cancelled = true
    }
  }, [
    accesoConcedido,
    examen,
    waitingAfterTimeout,
    enviado,
    resultado,
    enableKiosk,
    hardKiosk,
    kiosk,
    requestFullscreenSafe,
  ])

  const manualKioskStrikeRef = useRef(0)

  const refreshDeviceList = useCallback(async () => {
    try {
      const devs = await navigator.mediaDevices.enumerateDevices();
      const cams = devs.filter(d => d.kind === "videoinput");
      const mics = devs.filter(d => d.kind === "audioinput");

      let camId = localStorage.getItem("proctor.camId") || null;
      let micId = localStorage.getItem("proctor.micId") || null;

      const activeCamId = (() => {
        try { return videoTrackRef.current?.getSettings?.()?.deviceId || null; } catch { return null; }
      })();

      const activeMicId = (() => {
        try { return audioTrackRef.current?.getSettings?.()?.deviceId || null; } catch { return null; }
      })();

      if (camId && !cams.some(d => d.deviceId === camId)) camId = activeCamId || null;
      if (micId && !mics.some(d => d.deviceId === micId)) micId = activeMicId || null;

      if (!camId && activeCamId && cams.some(d => d.deviceId === activeCamId)) camId = activeCamId;
      if (!micId && activeMicId && mics.some(d => d.deviceId === activeMicId)) micId = activeMicId;

      setDeviceOpts({ cams, mics, camId, micId });
    } catch { }
  }, []);

  const [deviceOpts, setDeviceOpts] = useState({
    cams: [],
    mics: [],
    camId: localStorage.getItem("proctor.camId") || null,
    micId: localStorage.getItem("proctor.micId") || null,
  })

  const proctorPolicyRef = useRef({
    requireVideo: true,
    requireAudio: true,
    wantVideo: true,
    wantAudio: true,
  })

  const openDeviceModal = useCallback(async () => {
    await refreshDeviceList();

    const activeCamId = (() => {
      try { return videoTrackRef.current?.getSettings?.()?.deviceId || ""; } catch { return ""; }
    })();

    const activeMicId = (() => {
      try { return audioTrackRef.current?.getSettings?.()?.deviceId || ""; } catch { return ""; }
    })();

    setDeviceDraft({
      camId: activeCamId || deviceOpts.camId || "",
      micId: activeMicId || deviceOpts.micId || "",
    });

    setDeviceModalOpen(true);
  }, [refreshDeviceList, deviceOpts.camId, deviceOpts.micId]);


  const stopMicMeter = useCallback(() => {
    try { if (rafIdRef.current) cancelAnimationFrame(rafIdRef.current) } catch { }
    rafIdRef.current = null
    try { if (audioCtxRef.current) audioCtxRef.current.close() } catch { }
    audioCtxRef.current = null; analyserRef.current = null; dataArrayRef.current = null
    setMicLevel(0); micLevelRef.current = 0
  }, [])

  const stopProctoring = useCallback(() => {
    detachDeviceListener()

    try {
      if (cameraAnalyzerRef.current?.timer) {
        clearInterval(cameraAnalyzerRef.current.timer)
      }
    } catch { }

    cameraAnalyzerRef.current = {
      timer: null,
      prevGray: null,
      startedAt: 0,
      lastMotionAt: 0,
      darkSince: 0,
    }

    setCameraHealth(cleanCameraHealth())
    cameraHealthRef.current = cleanCameraHealth()

    // cortar poll
    try { if (proctorPollRef.current) clearInterval(proctorPollRef.current) } catch { }
    proctorPollRef.current = null

    // 1) deshabilitar tracks (por si el navegador tarda en apagar indicador)
    try {
      if (videoTrackRef.current) videoTrackRef.current.enabled = false
    } catch { }
    try {
      if (audioTrackRef.current) audioTrackRef.current.enabled = false
    } catch { }

    // 2) detener SIEMPRE tracks (stream combinado + refs sueltos)
    const stopTrack = (t) => { try { if (t && t.readyState !== "ended") t.stop() } catch { } }

    try {
      const s = streamRef.current
      if (s?.getTracks) s.getTracks().forEach(stopTrack)
    } catch { }

    stopTrack(videoTrackRef.current)
    stopTrack(audioTrackRef.current)

    // limpiar refs
    streamRef.current = null
    videoTrackRef.current = null
    audioTrackRef.current = null

    // 3) apagar medidor mic + audio ctx
    stopMicMeter()

    // 4) soltar preview
    if (proctorVideoRef.current) {
      try { proctorVideoRef.current.pause?.() } catch { }
      try { proctorVideoRef.current.srcObject = null } catch { }
    }

    if (devicePreviewVideoRef.current) {
      try { devicePreviewVideoRef.current.pause?.() } catch { }
      try { devicePreviewVideoRef.current.srcObject = null } catch { }
    }

    // 5) resetear estado UI
    setMicLevel(0)
    micLevelRef.current = 0
    lastOkRef.current = { cam: 0, mic: 0 };
    setProctor({ ready: false, cam: false, mic: false, blocked: false, reason: "" })
  }, [stopMicMeter])



  const clearProctorDeviceState = useCallback(() => {
    lastOkRef.current = { cam: 0, mic: 0 };

    try {
      if (videoTrackRef.current) {
        videoTrackRef.current.onended = null;
        videoTrackRef.current.onmute = null;
        videoTrackRef.current.onunmute = null;
      }
    } catch { }

    try {
      if (audioTrackRef.current) {
        audioTrackRef.current.onended = null;
        audioTrackRef.current.onmute = null;
        audioTrackRef.current.onunmute = null;
      }
    } catch { }

    videoTrackRef.current = null;
    audioTrackRef.current = null;
    streamRef.current = null;

    setMicLevel(0);
    micLevelRef.current = 0;

    setProctor({
      ready: false,
      cam: false,
      mic: false,
      blocked: true,
      reason: "Esperando nuevo dispositivo…",
    });
  }, []);



  const openExactDevices = useCallback(async (camId, micId, options = {}) => {
    const wantVideo = options.wantVideo !== false
    const wantAudio = options.wantAudio !== false

    const requireVideo = options.requireVideo === true
    const requireAudio = options.requireAudio === true

    const stopAll = (stream) => {
      try {
        stream?.getTracks?.().forEach((t) => {
          try { t.stop() } catch { }
        })
      } catch { }
    }

    const tryGet = async (constraintsList = []) => {
      let lastErr = null

      for (const constraints of constraintsList) {
        try {
          return await navigator.mediaDevices.getUserMedia(constraints)
        } catch (err) {
          lastErr = err
        }
      }

      throw lastErr || new Error("No se pudo abrir el dispositivo.")
    }

    let vStream = null
    let aStream = null

    if (wantVideo) {
      const videoTries = camId
        ? [
          // Si el usuario eligió una cámara, SOLO aceptamos esa cámara.
          {
            video: {
              deviceId: { exact: camId },
              width: { ideal: 640 },
              height: { ideal: 480 },
              aspectRatio: { ideal: 4 / 3 },
            },
            audio: false,
          },
          {
            video: {
              deviceId: { exact: camId },
            },
            audio: false,
          },
        ]
        : [
          // Solo cuando NO hay selección manual, permitimos fallback automático.
          {
            video: {
              width: { ideal: 640 },
              height: { ideal: 480 },
              aspectRatio: { ideal: 4 / 3 },
              facingMode: "user",
            },
            audio: false,
          },
          {
            video: { facingMode: "user" },
            audio: false,
          },
          {
            video: true,
            audio: false,
          },
        ]

      try {
        vStream = await tryGet(videoTries)

        const vTrack = vStream?.getVideoTracks?.()[0] || null
        assertSelectedDeviceWasUsed({
          track: vTrack,
          selectedId: camId,
          kind: "cámara",
          streamToStop: vStream,
        })

      } catch (err) {
        if (requireVideo) {
          throw new Error(
            err?.message === "Could not start video source"
              ? "No se pudo iniciar la cámara seleccionada. Puede estar en uso por otra app o quedó bloqueada por el navegador."
              : "No se pudo activar la cámara seleccionada. Revisa permisos o elige otra cámara."
          )
        }
      }
    }

    if (wantAudio) {
      const audioOpts = {
        echoCancellation: true,
        noiseSuppression: true,
        autoGainControl: true,
      }

      const audioTries = micId
        ? [
          {
            video: false,
            audio: {
              deviceId: { exact: micId },
              ...audioOpts,
            },
          },
        ]
        : [
          {
            video: false,
            audio: audioOpts,
          },
          {
            video: false,
            audio: true,
          },
        ]

      try {
        aStream = await tryGet(audioTries)

        const aTrack = aStream?.getAudioTracks?.()[0] || null
        assertSelectedDeviceWasUsed({
          track: aTrack,
          selectedId: micId,
          kind: "micrófono",
          streamToStop: aStream,
        })

      } catch (err) {
        if (requireAudio) {
          stopAll(vStream)

          throw new Error(
            "No se pudo activar el micrófono seleccionado. Revisa permisos o elige otro micrófono."
          )
        }
      }
    }

    return { vStream, aStream }
  }, [])

  const updateProctorFromTracks = useCallback((vTrack, aTrack, why = "") => {
    const now = Date.now()
    const policy = proctorPolicyRef.current || {}

    const requireVideo = policy.requireVideo !== false
    const requireAudio = policy.requireAudio !== false

    const camLive = !!vTrack && vTrack.readyState === "live" && vTrack.enabled !== false
    const micLive = !!aTrack && aTrack.readyState === "live" && aTrack.enabled !== false

    if (camLive) lastOkRef.current.cam = now
    if (micLive) lastOkRef.current.mic = now

    const cameraQuality = cameraHealthRef.current || cleanCameraHealth()

    const cameraQualityBlocked =
      requireVideo &&
      camLive &&
      cameraQuality.ready === true &&
      cameraQuality.ok === false &&
      cameraQuality.validating === true

    const camOk = !requireVideo
      ? true
      : ((camLive || (now - lastOkRef.current.cam < PROCTOR_GRACE_MS)) && !cameraQualityBlocked)

    const micOk = !requireAudio
      ? true
      : (micLive || (now - lastOkRef.current.mic < PROCTOR_GRACE_MS))

    const blocked = !(camOk && micOk)

    const reason = blocked
      ? (
        cameraQualityBlocked
          ? cameraQuality.reason
          : why || (!camOk && !micOk ? "No hay cámara ni micrófono activos"
            : !camOk ? "Cámara no disponible/activa"
              : "Micrófono no disponible/activo")
      )
      : ""

    setProctor(prev => ({
      ...prev,
      ready: true,
      cam: camOk,
      mic: micOk,
      blocked,
      reason,
    }))
  }, [])


  const startMicMeter = useCallback((aTrack) => {
    try { if (rafIdRef.current) cancelAnimationFrame(rafIdRef.current) } catch { }
    rafIdRef.current = null
    try { if (audioCtxRef.current) { audioCtxRef.current.close()?.catch?.(() => { }) } } catch { }
    audioCtxRef.current = null; analyserRef.current = null; dataArrayRef.current = null
    setMicLevel(0); micLevelRef.current = 0
    if (!aTrack) return
    try {
      const Ctx = window.AudioContext || window.webkitAudioContext
      if (!Ctx) return
      const ctx = new Ctx()
      audioCtxRef.current = ctx
      const src = ctx.createMediaStreamSource(new MediaStream([aTrack]))
      const analyser = ctx.createAnalyser()
      analyser.fftSize = 2048
      analyserRef.current = analyser
      src.connect(analyser)
      const buf = new Uint8Array(analyser.fftSize)
      dataArrayRef.current = buf
      const loop = () => {
        if (!analyserRef.current || !dataArrayRef.current) return
        analyserRef.current.getByteTimeDomainData(dataArrayRef.current)
        let sum = 0
        for (let i = 0; i < dataArrayRef.current.length; i++) {
          const v = (dataArrayRef.current[i] - 128) / 128
          sum += v * v
        }
        const rms = Math.sqrt(sum / dataArrayRef.current.length)
        const lvl = Math.min(1, rms * 3)
        const pct = Math.round(lvl * 100)
        if (pct !== micLevelRef.current) { micLevelRef.current = pct; setMicLevel(pct) }
        rafIdRef.current = requestAnimationFrame(loop)
      }
      loop()
    } catch { }
  }, [])

  const getActivePreviewVideo = useCallback(() => {
    const modalVideo = devicePreviewVideoRef.current
    const examVideo = proctorVideoRef.current

    // Primero usa el video que de verdad ya tiene imagen.
    if (isVideoFrameReady(modalVideo)) return modalVideo
    if (isVideoFrameReady(examVideo)) return examVideo

    // Si todavía está cargando, prioriza el modal si está abierto.
    if (modalVideo?.srcObject) return modalVideo
    if (examVideo?.srcObject) return examVideo

    return modalVideo || examVideo || null
  }, [])

  const waitForCameraFrame = useCallback((timeoutMs = CAMERA_HEALTH_LIMITS.videoReadyTimeoutMs) => {
    return new Promise((resolve) => {
      const startedAt = Date.now()
      let rafId = null

      const check = () => {
        const video = getActivePreviewVideo()

        if (isVideoFrameReady(video)) {
          if (rafId) cancelAnimationFrame(rafId)
          resolve(true)
          return
        }

        if (Date.now() - startedAt >= timeoutMs) {
          if (rafId) cancelAnimationFrame(rafId)
          resolve(false)
          return
        }

        rafId = requestAnimationFrame(check)
      }

      check()
    })
  }, [getActivePreviewVideo])

  const attachStreamToVideo = useCallback((videoEl, stream) => {
    if (!videoEl || !stream) return

    try {
      if (videoEl.srcObject !== stream) {
        videoEl.srcObject = stream
      }

      videoEl.muted = true
      videoEl.playsInline = true
      videoEl.autoplay = true

      const play = () => {
        try {
          const p = videoEl.play?.()
          if (p?.catch) p.catch(() => { })
        } catch { }
      }

      videoEl.onloadedmetadata = play
      videoEl.onloadeddata = play
      videoEl.oncanplay = play
      videoEl.onplaying = play

      play()
    } catch { }
  }, [])

  const stopCameraAnalyzer = useCallback(() => {
    try {
      if (cameraAnalyzerRef.current?.timer) {
        clearInterval(cameraAnalyzerRef.current.timer)
      }
    } catch { }

    cameraAnalyzerRef.current = {
      timer: null,
      prevGray: null,
      startedAt: 0,
      lastMotionAt: 0,
      darkSince: 0,
      frames: 0,
      motionHits: 0,
    }

    lastFaceDetectionAtRef.current = 0

    setCameraHealth(cleanCameraHealth())
    cameraHealthRef.current = cleanCameraHealth()
  }, [])


  const analyzeCameraFrameOnce = useCallback(() => {
    try {
      const video = getActivePreviewVideo()
      const track = videoTrackRef.current
      const now = Date.now()

      if (!track || track.readyState !== "live" || track.enabled === false) {
        const next = {
          ...cleanCameraHealth(),
          ok: false,
          reason: "No hay una cámara activa.",
          checkedAt: now,
        }

        cameraHealthRef.current = next
        setCameraHealth(next)
        return next
      }

      if (!isVideoFrameReady(video)) {
        const prev = cameraHealthRef.current || cleanCameraHealth()

        const next = {
          ...prev,
          ready: false,
          videoReady: false,
          ok: true,
          validated: false,
          reason: "Esperando imagen real de la cámara…",
          checkedAt: now,
        }

        cameraHealthRef.current = next
        setCameraHealth(next)
        return next
      }

      const w = CAMERA_HEALTH_LIMITS.width
      const h = CAMERA_HEALTH_LIMITS.height

      const canvas = document.createElement("canvas")
      canvas.width = w
      canvas.height = h

      const ctx = canvas.getContext("2d", { willReadFrequently: true })
      if (!ctx) {
        return cameraHealthRef.current || cleanCameraHealth()
      }

      ctx.drawImage(video, 0, 0, w, h)

      const data = ctx.getImageData(0, 0, w, h).data
      const gray = new Uint8Array(w * h)

      let lumaSum = 0
      let lumaSqSum = 0
      let darkPx = 0
      let brightPx = 0

      let centerSum = 0
      let centerSqSum = 0
      let centerCount = 0

      const cx1 = Math.floor(w * 0.24)
      const cx2 = Math.floor(w * 0.76)
      const cy1 = Math.floor(h * 0.14)
      const cy2 = Math.floor(h * 0.82)

      for (let y = 0, p = 0; y < h; y += 1) {
        for (let x = 0; x < w; x += 1, p += 1) {
          const i = p * 4

          const luma = Math.round(
            data[i] * 0.299 +
            data[i + 1] * 0.587 +
            data[i + 2] * 0.114
          )

          gray[p] = luma
          lumaSum += luma
          lumaSqSum += luma * luma

          if (luma <= CAMERA_HEALTH_LIMITS.darkLumaMax) darkPx += 1
          if (luma >= CAMERA_HEALTH_LIMITS.overBrightLumaMin) brightPx += 1

          if (x >= cx1 && x <= cx2 && y >= cy1 && y <= cy2) {
            centerSum += luma
            centerSqSum += luma * luma
            centerCount += 1
          }
        }
      }

      const totalPx = gray.length
      const avgLuma = lumaSum / totalPx
      const contrast = calcStdDev(lumaSum, lumaSqSum, totalPx)
      const centerLuma = centerCount ? centerSum / centerCount : avgLuma
      const centerContrast = centerCount ? calcStdDev(centerSum, centerSqSum, centerCount) : contrast

      const darkRatio = darkPx / totalPx
      const overBrightRatio = brightPx / totalPx

      const st = cameraAnalyzerRef.current

      if (!st.startedAt) st.startedAt = now
      if (!st.lastMotionAt) st.lastMotionAt = now
      if (!Number.isFinite(st.motionRecoveredAt)) st.motionRecoveredAt = 0
      if (!Number.isFinite(st.frames)) st.frames = 0
      if (!Number.isFinite(st.motionHits)) st.motionHits = 0

      st.frames += 1

      let motionScore = 0

      if (st.prevGray && st.prevGray.length === gray.length) {
        let diff = 0

        for (let i = 0; i < gray.length; i += 1) {
          diff += Math.abs(gray[i] - st.prevGray[i])
        }

        motionScore = diff / gray.length
      }

      st.prevGray = gray

      if (motionScore >= CAMERA_HEALTH_LIMITS.motionDiffMin) {
        st.lastMotionAt = now
        st.motionHits += 1
      }

      if (motionScore >= CAMERA_HEALTH_LIMITS.motionRecoverDiffMin) {
        if (!st.motionRecoveredAt) {
          st.motionRecoveredAt = now
        }
      } else {
        st.motionRecoveredAt = 0
      }

      const isTooDarkNow =
        avgLuma <= CAMERA_HEALTH_LIMITS.darkLumaMax ||
        centerLuma <= CAMERA_HEALTH_LIMITS.centerLumaMin ||
        darkRatio >= 0.42 ||
        (avgLuma <= CAMERA_HEALTH_LIMITS.stableLumaMin && darkRatio >= 0.30)

      if (isTooDarkNow && !st.darkSince) st.darkSince = now
      if (!isTooDarkNow) st.darkSince = 0

      const darkMs = st.darkSince ? now - st.darkSince : 0
      const noMotionMs = now - st.lastMotionAt
      const prev = cameraHealthRef.current || cleanCameraHealth()

      const dark = darkMs >= CAMERA_HEALTH_LIMITS.darkGraceMs

      const overBright =
        avgLuma >= CAMERA_HEALTH_LIMITS.stableLumaMax ||
        overBrightRatio >= CAMERA_HEALTH_LIMITS.overBrightRatioMax

      const lowContrast =
        contrast < CAMERA_HEALTH_LIMITS.contrastMin ||
        centerContrast < CAMERA_HEALTH_LIMITS.centerContrastMin

      const noMotionDetected =
        st.frames >= CAMERA_HEALTH_LIMITS.verifyMinFrames &&
        now - st.startedAt >= CAMERA_HEALTH_LIMITS.noMotionGraceMs &&
        noMotionMs >= CAMERA_HEALTH_LIMITS.noMotionGraceMs

      const sustainedRecovery =
        Number(st.motionRecoveredAt || 0) > 0 &&
        now - Number(st.motionRecoveredAt || 0) >= CAMERA_HEALTH_LIMITS.noMotionRecoverMs

      const noMotion =
        prev.noMotion === true
          ? !sustainedRecovery
          : noMotionDetected

      if (dark && !prev.dark) {
        cameraIssueStatsRef.current.dark_events += 1
        cameraIssueStatsRef.current.last_issue = "dark"
      }

      if (overBright && !prev.overBright) {
        cameraIssueStatsRef.current.overbright_events += 1
        cameraIssueStatsRef.current.last_issue = "overbright"
      }

      if (lowContrast && !prev.lowContrast) {
        cameraIssueStatsRef.current.low_contrast_events += 1
        cameraIssueStatsRef.current.last_issue = "low_contrast"
      }

      if (noMotion && !prev.noMotion) {
        cameraIssueStatsRef.current.no_motion_events += 1
        cameraIssueStatsRef.current.last_issue = "no_motion"
      }

      cameraIssueStatsRef.current.max_dark_ms = Math.max(
        cameraIssueStatsRef.current.max_dark_ms,
        darkMs
      )

      cameraIssueStatsRef.current.max_no_motion_ms = Math.max(
        cameraIssueStatsRef.current.max_no_motion_ms,
        noMotionMs
      )

      const faceBlocked =
        prev.faceChecked === true &&
        prev.faceVisible === false

      const reason = buildCameraQualityReason({
        dark,
        overBright,
        lowContrast,
        noMotion,
        faceChecked: prev.faceChecked,
        faceVisible: prev.faceVisible,
        faceReason: prev.faceReason,
      })

      const inRunningExam = inExamRef.current === true

      // ✅ Antes de entrar al examen, la calidad sí bloquea.
      // ✅ Durante el examen, oscuridad/no movimiento generan aviso + evidencia,
      // pero no congelan el examen como si se hubiera desconectado la cámara.
      const nextOk = inRunningExam
        ? true
        : !(dark || overBright || lowContrast || noMotion || faceBlocked)

      const next = {
        ...prev,

        ready: true,
        videoReady: true,

        ok: nextOk,
        // La validación solo debe perderse si cambia el dispositivo o si una validación manual falla.
        // El análisis pasivo de frames NO debe invalidarla.
        validated: prev.validated === true,
        validating: prev.validating === true,

        dark,
        overBright,
        lowContrast,
        noMotion,

        avgLuma: safeRound(avgLuma),
        contrast: safeRound(contrast),
        centerLuma: safeRound(centerLuma),
        centerContrast: safeRound(centerContrast),
        overBrightRatio: safeRound(overBrightRatio, 3),
        darkRatio: safeRound(darkRatio, 3),

        motionScore: safeRound(motionScore, 2),
        motionHits: st.motionHits,
        frames: st.frames,

        darkMs,
        noMotionMs,

        reason: inRunningExam ? "" : reason,
        checkedAt: now,
      }

      cameraHealthRef.current = next
      setCameraHealth(next)

      return next
    } catch {
      return cameraHealthRef.current || cleanCameraHealth()
    }
  }, [getActivePreviewVideo])

  const analyzeFaceQualityOnce = useCallback(async () => {
    const now = Date.now()

    try {
      const video = getActivePreviewVideo()

      if (!isVideoFrameReady(video)) {
        return cameraHealthRef.current || cleanCameraHealth()
      }

      const videoW = Number(video.videoWidth || 0)
      const videoH = Number(video.videoHeight || 0)

      if (!videoW || !videoH) {
        return cameraHealthRef.current || cleanCameraHealth()
      }

      const prev = cameraHealthRef.current || cleanCameraHealth()

      let faceResult = {
        faceChecked: true,
        faceVisible: false,
        faceScore: 0,
        faceCount: 0,
        faceBox: null,
        faceSource: "none",
        faceReason: "No se detecta un rostro visible. Mira al frente, centra tu rostro y asegúrate de que tu cara esté dentro de la cámara.",
      }

      const Detector = window.FaceDetector || window.webkitFaceDetector || null

      if (Detector) {
        try {
          if (!faceDetectorRef.current) {
            faceDetectorRef.current = new Detector({
              fastMode: true,
              maxDetectedFaces: 3,
            })
          }

          const faces = await faceDetectorRef.current.detect(video)
          const faceCount = Array.isArray(faces) ? faces.length : 0

          if (faceCount === 0) {
            faceResult = {
              ...faceResult,
              faceSource: "FaceDetector",
              faceCount: 0,
              faceVisible: false,
              faceScore: 0,
              faceReason: "No se detecta tu rostro. Mira al frente, mejora la luz frontal y coloca tu cara dentro del recuadro.",
            }
          } else if (faceCount > 1) {
            faceResult = {
              ...faceResult,
              faceSource: "FaceDetector",
              faceCount,
              faceVisible: false,
              faceScore: 0,
              faceReason: "Se detectó más de un rostro. Solo debe aparecer el estudiante frente a la cámara.",
            }
          } else {
            const box = faces[0]?.boundingBox || {}

            const x = Number(box.x || 0)
            const y = Number(box.y || 0)
            const width = Number(box.width || 0)
            const height = Number(box.height || 0)

            const areaPct = (width * height) / (videoW * videoH)

            const faceCx = x + width / 2
            const faceCy = y + height / 2

            const dx = Math.abs(faceCx - videoW / 2) / (videoW / 2)
            const dy = Math.abs(faceCy - videoH / 2) / (videoH / 2)

            const centerOffset = Math.max(dx, dy)

            const widthRatio = width / videoW
            const heightRatio = height / videoH
            const boxAspect = height > 0 ? width / height : 0

            const tooSmall = areaPct < CAMERA_HEALTH_LIMITS.faceMinAreaPct
            const tooLarge = areaPct > CAMERA_HEALTH_LIMITS.faceMaxAreaPct
            const offCenter = centerOffset > CAMERA_HEALTH_LIMITS.faceCenterTolerance

            // Más tolerante: FaceDetector ya encontró un rostro, no hace falta
            // castigar demasiado el aspect ratio.
            const weirdShape = boxAspect < 0.25 || boxAspect > 2.00

            // Evita solo casos extremos, no encuadres normales.
            const faceTooLow = faceCy > videoH * 0.90
            const faceTooHigh = faceCy < videoH * 0.05

            let faceReason = ""

            if (tooSmall) {
              faceReason = "Tu rostro se ve muy lejos. Acércate un poco a la cámara."
            } else if (tooLarge) {
              faceReason = "Tu rostro está demasiado cerca. Aléjate un poco para que se vea completo."
            } else if (offCenter) {
              faceReason = "Centra un poco más tu rostro en la cámara."
            } else if (weirdShape) {
              faceReason = "No se aprecia una forma de rostro estable. Mira de frente a la cámara."
            } else if (faceTooLow || faceTooHigh) {
              faceReason = "Ajusta la cámara para que tu rostro quede visible."
            }

            const areaScore = tooSmall || tooLarge ? 0.25 : 1
            const centerScore = clamp01(1 - centerOffset)
            const shapeScore = weirdShape ? 0.35 : 1
            const positionScore = faceTooLow || faceTooHigh ? 0.45 : 1

            const faceScore = clamp01(
              areaScore * 0.34 +
              centerScore * 0.30 +
              shapeScore * 0.20 +
              positionScore * 0.16
            )

            const faceVisible =
              !tooSmall &&
              !tooLarge &&
              !weirdShape &&
              !faceTooLow &&
              !faceTooHigh &&
              widthRatio >= 0.10 &&
              heightRatio >= 0.12 &&
              faceScore >= CAMERA_HEALTH_LIMITS.faceMinScore

            faceResult = {
              faceChecked: true,
              faceVisible,
              faceScore: safeRound(faceScore, 2),
              faceCount,
              faceBox: {
                x: safeRound(x),
                y: safeRound(y),
                width: safeRound(width),
                height: safeRound(height),
                areaPct: safeRound(areaPct, 3),
                centerOffset: safeRound(centerOffset, 3),
                widthRatio: safeRound(widthRatio, 3),
                heightRatio: safeRound(heightRatio, 3),
                aspect: safeRound(boxAspect, 3),
              },
              faceSource: "FaceDetector",
              faceReason: faceVisible
                ? ""
                : faceReason || "Tu rostro fue detectado, pero el encuadre no es suficiente. Mira de frente y centra tu rostro.",
            }
          }
        } catch {
          faceResult = {
            ...faceResult,
            faceSource: "FaceDetector_error",
            faceVisible: false,
            faceReason: "No se pudo confirmar tu rostro con el detector del navegador. Se usará validación visual de respaldo.",
          }
        }
      }

      // Fallback visual:
      // Antes estaba prácticamente bloqueando siempre si no había FaceDetector.
      // Ahora permite aprobar cuando la imagen es clara, centrada, con luz suficiente
      // y movimiento. Esto evita bloquear Chrome/Edge/Firefox cuando FaceDetector no existe.
      if (!Detector || faceResult.faceSource === "FaceDetector_error") {
        const centerLuma = Number(prev.centerLuma || 0)
        const centerContrast = Number(prev.centerContrast || 0)
        const avgLuma = Number(prev.avgLuma || 0)
        const contrast = Number(prev.contrast || 0)
        const overBrightRatio = Number(prev.overBrightRatio || 0)
        const darkRatio = Number(prev.darkRatio || 0)
        const motionScore = Number(prev.motionScore || 0)

        const lightScore = clamp01((centerLuma - 35) / 85)
        const contrastScore = clamp01(centerContrast / 24)
        const globalScore = clamp01(contrast / 30)
        const motionScoreNorm = clamp01(motionScore / Math.max(1, CAMERA_HEALTH_LIMITS.motionDiffMin * 2))

        const penalty =
          (darkRatio > 0.50 ? 0.30 : 0) +
          (overBrightRatio > CAMERA_HEALTH_LIMITS.overBrightRatioMax ? 0.30 : 0) +
          (avgLuma < CAMERA_HEALTH_LIMITS.stableLumaMin ? 0.18 : 0)

        const qualityScore = clamp01(
          lightScore * 0.38 +
          contrastScore * 0.30 +
          globalScore * 0.20 +
          motionScoreNorm * 0.12 -
          penalty
        )

        const qualityOk =
          CAMERA_HEALTH_LIMITS.allowQualityFallbackAsFace &&
          qualityScore >= 0.45 &&
          centerLuma >= CAMERA_HEALTH_LIMITS.centerLumaMin &&
          centerContrast >= CAMERA_HEALTH_LIMITS.centerContrastMin &&
          darkRatio < 0.60 &&
          overBrightRatio < CAMERA_HEALTH_LIMITS.overBrightRatioMax

        faceResult = {
          faceChecked: true,
          faceVisible: qualityOk,
          faceScore: safeRound(qualityScore, 2),
          faceCount: qualityOk ? 1 : 0,
          faceBox: null,
          faceSource: Detector ? "quality_fallback_after_detector_error" : "quality_fallback",
          faceReason: qualityOk
            ? ""
            : "No se pudo confirmar visualmente tu rostro. Mejora la luz frontal, mira a la cámara y mantén tu rostro centrado unos segundos.",
        }
      }

      const faceBlocked = faceResult.faceVisible !== true

      if (faceBlocked && prev.faceVisible !== false) {
        cameraIssueStatsRef.current.face_missing_events += 1
        cameraIssueStatsRef.current.last_issue = "face_missing"
      }

      const baseBlocked =
        prev.dark ||
        prev.overBright ||
        prev.lowContrast ||
        prev.noMotion

      const reason = buildCameraQualityReason({
        dark: prev.dark,
        overBright: prev.overBright,
        lowContrast: prev.lowContrast,
        noMotion: prev.noMotion,
        faceChecked: faceResult.faceChecked,
        faceVisible: faceResult.faceVisible,
        faceReason: faceResult.faceReason,
      })

      const nextOk = !(baseBlocked || faceBlocked)

      const next = {
        ...prev,
        ...faceResult,
        ok: nextOk,
        // No invalidar una cámara ya validada por un frame malo o una lectura facial pasiva.
        validated: prev.validated === true,
        reason,
        checkedAt: now,
      }

      cameraHealthRef.current = next
      setCameraHealth(next)

      return next
    } catch {
      return cameraHealthRef.current || cleanCameraHealth()
    }
  }, [getActivePreviewVideo])

  const startCameraAnalyzer = useCallback(() => {
    stopCameraAnalyzer()

    cameraAnalyzerRef.current = {
      timer: null,
      prevGray: null,
      startedAt: Date.now(),
      lastMotionAt: Date.now(),
      motionRecoveredAt: 0,
      darkSince: 0,
      frames: 0,
      motionHits: 0,
    }

    let faceBusy = false

    const tick = () => {
      const health = analyzeCameraFrameOnce()

      updateProctorFromTracks(
        videoTrackRef.current,
        audioTrackRef.current,
        health?.reason || ""
      )

      const now = Date.now()
      const shouldCheckFace =
        now - Number(lastFaceDetectionAtRef.current || 0) >= CAMERA_HEALTH_LIMITS.faceDetectEveryMs

      if (shouldCheckFace && !faceBusy) {
        faceBusy = true
        lastFaceDetectionAtRef.current = now

        analyzeFaceQualityOnce()
          .then((faceHealth) => {
            updateProctorFromTracks(
              videoTrackRef.current,
              audioTrackRef.current,
              faceHealth?.reason || ""
            )
          })
          .catch(() => { })
          .finally(() => {
            faceBusy = false
          })
      }
    }

    setTimeout(tick, 250)

    cameraAnalyzerRef.current.timer = setInterval(
      tick,
      CAMERA_HEALTH_LIMITS.sampleMs
    )
  }, [
    analyzeCameraFrameOnce,
    analyzeFaceQualityOnce,
    stopCameraAnalyzer,
    updateProctorFromTracks,
  ])

  const mergeStreamsToPreview = useCallback((vs, as) => {
    const tracks = []

    if (vs) tracks.push(...vs.getVideoTracks())
    if (as) tracks.push(...as.getAudioTracks())

    const combined = new MediaStream(tracks)
    streamRef.current = combined

    const vTrack = combined.getVideoTracks()[0] || null
    const aTrack = combined.getAudioTracks()[0] || null

    videoTrackRef.current = vTrack
    audioTrackRef.current = aTrack

    // ✅ Conecta el mismo stream al video del examen y al preview del modal
    attachStreamToVideo(proctorVideoRef.current, combined)
    attachStreamToVideo(devicePreviewVideoRef.current, combined)

    updateProctorFromTracks(vTrack, aTrack)
    startMicMeter(aTrack)

    if (vTrack) {
      setTimeout(() => {
        startCameraAnalyzer()
      }, 350)
    }
  }, [
    attachStreamToVideo,
    updateProctorFromTracks,
    startMicMeter,
    startCameraAnalyzer,
  ])


  const startProctoring = useCallback(async () => {
    if (!navigator?.mediaDevices?.getUserMedia) {
      setProctor({ ready: false, cam: false, mic: false, blocked: true, reason: "Tu navegador no soporta cámara/mic." })
      return false
    }
    if (!isSecureOk()) {
      setProctor({ ready: false, cam: false, mic: false, blocked: true, reason: "Debes usar HTTPS o localhost para habilitar cámara y micrófono." })
      return false
    }

    const policy = proctorPolicyRef.current || {}
    const requireVideo = policy.requireVideo !== false
    const requireAudio = policy.requireAudio !== false
    const wantVideo = policy.wantVideo !== false
    const wantAudio = policy.wantAudio !== false


    let vStream = null
    let aStream = null

    try {
      const exact = await openExactDevices(deviceOpts.camId || null, deviceOpts.micId || null, {
        wantVideo,
        wantAudio,
        requireVideo,
        requireAudio,
      })

      vStream = exact.vStream
      aStream = exact.aStream
    } catch (err) {
      setProctor({
        ready: true,
        cam: false,
        mic: false,
        blocked: true,
        reason: err?.message || "No se pudieron activar los dispositivos seleccionados.",
      })

      return false
    }

    const missingRequiredVideo = requireVideo && !vStream
    const missingRequiredAudio = requireAudio && !aStream

    if (missingRequiredVideo || missingRequiredAudio) {
      try { vStream?.getTracks?.().forEach((t) => t.stop()) } catch { }
      try { aStream?.getTracks?.().forEach((t) => t.stop()) } catch { }

      setProctor({
        ready: true,
        cam: !!vStream,
        mic: !!aStream,
        blocked: true,
        reason:
          missingRequiredVideo && missingRequiredAudio
            ? "No se pudo iniciar cámara ni micrófono. Verifica permisos y dispositivos."
            : missingRequiredVideo
              ? "No se pudo iniciar la cámara. Verifica permisos y el dispositivo seleccionado."
              : "No se pudo iniciar el micrófono. Verifica permisos y el dispositivo seleccionado.",
      })

      return false
    }
    await refreshDeviceList()
    mergeStreamsToPreview(vStream, aStream)
    const vTrack = (vStream ? vStream.getVideoTracks()[0] : null) || (streamRef.current?.getVideoTracks?.()[0] ?? null)
    const aTrack =
      (aStream ? aStream.getAudioTracks()[0] : null) ||
      (streamRef.current?.getAudioTracks?.() ? streamRef.current.getAudioTracks()[0] : null)

    const recheck = () => updateProctorFromTracks(videoTrackRef.current, audioTrackRef.current)
    if (vTrack) { vTrack.onended = () => updateProctorFromTracks(null, audioTrackRef.current, "Cámara finalizó"); vTrack.onmute = recheck; vTrack.onunmute = recheck }
    if (aTrack) { aTrack.onended = () => updateProctorFromTracks(videoTrackRef.current, null, "Micrófono finalizó"); aTrack.onmute = recheck; aTrack.onunmute = recheck }

    const onDeviceChange = async () => {
      try {
        await refreshDeviceList();

        const devs = await navigator.mediaDevices.enumerateDevices();
        const hasCam = devs.some((d) => d.kind === "videoinput");
        const hasMic = devs.some((d) => d.kind === "audioinput");

        if (!hasCam || !hasMic) {
          clearProctorDeviceState();
          updateProctorFromTracks(
            hasCam ? videoTrackRef.current : null,
            hasMic ? audioTrackRef.current : null,
            "Dispositivo desconectado"
          );
          return;
        }

        const vTrack = videoTrackRef.current;
        const aTrack = audioTrackRef.current;

        const camLive = !!vTrack && vTrack.readyState === "live";
        const micLive = !!aTrack && aTrack.readyState === "live";

        if (camLive && micLive) {
          updateProctorFromTracks(vTrack, aTrack);
          return;
        }

        clearProctorDeviceState();

        const nextCamId = localStorage.getItem("proctor.camId") || null;
        const nextMicId = localStorage.getItem("proctor.micId") || null;

        try {
          const exact = await openExactDevices(nextCamId, nextMicId);
          mergeStreamsToPreview(exact.vStream, exact.aStream);
          updateProctorFromTracks(
            exact.vStream.getVideoTracks?.()[0] || null,
            exact.aStream.getAudioTracks?.()[0] || null
          );
        } catch {
          updateProctorFromTracks(null, null, "No se pudo reconectar el dispositivo seleccionado");
        }
      } catch {
        updateProctorFromTracks(null, null, "Cambio de dispositivo");
      }
    };

    deviceChangeHandlerRef.current = onDeviceChange
    if (navigator.mediaDevices.addEventListener) {
      navigator.mediaDevices.addEventListener("devicechange", onDeviceChange)
      deviceListenerKindRef.current = "addEventListener"
    } else {
      navigator.mediaDevices.ondevicechange = onDeviceChange
      deviceListenerKindRef.current = "ondevicechange"
    }

    if (proctorPollRef.current) clearInterval(proctorPollRef.current)
    proctorPollRef.current = setInterval(() => {
      updateProctorFromTracks(videoTrackRef.current, audioTrackRef.current)
    }, 1500)

    return true
  }, [deviceOpts.camId, deviceOpts.micId, mergeStreamsToPreview, refreshDeviceList, updateProctorFromTracks])

  const applySelectedDevices = useCallback(async (nextCamId, nextMicId) => {
    const prevCamId = deviceOpts.camId || null
    const prevMicId = deviceOpts.micId || null

    const currentCamId = (() => {
      try { return videoTrackRef.current?.getSettings?.()?.deviceId || null } catch { return null }
    })()

    const currentMicId = (() => {
      try { return audioTrackRef.current?.getSettings?.()?.deviceId || null } catch { return null }
    })()

    const currentVideoLive =
      !!videoTrackRef.current &&
      videoTrackRef.current.readyState === "live" &&
      videoTrackRef.current.enabled !== false

    const currentAudioLive =
      !!audioTrackRef.current &&
      audioTrackRef.current.readyState === "live" &&
      audioTrackRef.current.enabled !== false

    const selectedCamId = nextCamId || null
    const selectedMicId = nextMicId || null

    const sameCam = selectedCamId
      ? selectedCamId === currentCamId
      : (!deviceOpts.camId && currentVideoLive)

    const sameMic = selectedMicId
      ? selectedMicId === currentMicId
      : (!deviceOpts.micId && currentAudioLive)

    try {
      setError("")
      setProctorSetupReason("Aplicando dispositivo seleccionado…")

      setDeviceSwitching({
        cam: selectedCamId !== prevCamId,
        mic: selectedMicId !== prevMicId,
      })

      const policy = proctorPolicyRef.current || {}
      const requireVideo = policy.requireVideo !== false
      const requireAudio = policy.requireAudio !== false
      const wantVideo = policy.wantVideo !== false
      const wantAudio = policy.wantAudio !== false

      // ✅ Si ya está usando ese mismo dispositivo y está vivo, NO lo cierres ni lo abras de nuevo.
      // Esto evita el error "Could not start video source".
      if (
        sameCam &&
        sameMic &&
        (!wantVideo || currentVideoLive) &&
        (!wantAudio || currentAudioLive) &&
        streamRef.current
      ) {
        attachStreamToVideo(devicePreviewVideoRef.current, streamRef.current)
        attachStreamToVideo(proctorVideoRef.current, streamRef.current)

        setDeviceOpts(prev => ({
          ...prev,
          camId: selectedCamId,
          micId: selectedMicId,
        }))

        localStorage.setItem("proctor.camId", selectedCamId || "")
        localStorage.setItem("proctor.micId", selectedMicId || "")

        updateProctorFromTracks(videoTrackRef.current, audioTrackRef.current)
        startMicMeter(audioTrackRef.current)

        setProctor({
          ready: true,
          cam: requireVideo ? currentVideoLive : true,
          mic: requireAudio ? currentAudioLive : true,
          blocked: false,
          reason: "",
        })

        return true
      }

      // ✅ Solo aquí cortamos si realmente cambió de dispositivo.
      stopProctoring()
      clearProctorDeviceState()

      setProctor({
        ready: false,
        cam: false,
        mic: false,
        blocked: true,
        reason: "Aplicando dispositivo seleccionado…",
      })

      const exact = await openExactDevices(selectedCamId, selectedMicId, {
        wantVideo,
        wantAudio,
        requireVideo,
        requireAudio,
      })

      const vStream = exact.vStream
      const aStream = exact.aStream

      const vTrack = vStream?.getVideoTracks?.()[0] || null
      const aTrack = aStream?.getAudioTracks?.()[0] || null

      assertSelectedDeviceWasUsed({
        track: vTrack,
        selectedId: selectedCamId,
        kind: "cámara",
        streamToStop: vStream,
      })

      assertSelectedDeviceWasUsed({
        track: aTrack,
        selectedId: selectedMicId,
        kind: "micrófono",
        streamToStop: aStream,
      })

      const actualCamId = getTrackDeviceId(vTrack) || selectedCamId || null
      const actualMicId = getTrackDeviceId(aTrack) || selectedMicId || null

      if (requireVideo && !vTrack) {
        throw new Error("No se pudo obtener una cámara válida del dispositivo seleccionado.")
      }

      if (requireAudio && !aTrack) {
        throw new Error("No se pudo obtener un micrófono válido del dispositivo seleccionado.")
      }

      mergeStreamsToPreview(vStream, aStream)

      videoTrackRef.current = vTrack
      audioTrackRef.current = aTrack

      setDeviceOpts(prev => ({
        ...prev,
        camId: actualCamId,
        micId: actualMicId,
      }))

      localStorage.setItem("proctor.camId", actualCamId || "")
      localStorage.setItem("proctor.micId", actualMicId || "")

      await refreshDeviceList()

      updateProctorFromTracks(vTrack, aTrack)
      startMicMeter(aTrack)

      setProctor({
        ready: true,
        cam: requireVideo ? !!vTrack : true,
        mic: requireAudio ? !!aTrack : true,
        blocked: false,
        reason: "",
      })

      return true
    } catch (err) {
      setProctorSetupReason(err?.message || "No se pudo aplicar el cambio de dispositivo.")
      setError(err?.message || "No se pudo aplicar el cambio de dispositivo.")
      return false
    } finally {
      setDeviceSwitching({ cam: false, mic: false })
    }
  }, [
    deviceOpts.camId,
    deviceOpts.micId,
    stopProctoring,
    clearProctorDeviceState,
    openExactDevices,
    mergeStreamsToPreview,
    refreshDeviceList,
    updateProctorFromTracks,
    startMicMeter,
    attachStreamToVideo,
  ])

  const previewSelectedDevices = useCallback(async (nextCamId, nextMicId) => {
    const requestId = ++previewSwitchSeqRef.current

    const selectedCamId = nextCamId || null
    const selectedMicId = nextMicId || null

    const currentCamId = (() => {
      try { return videoTrackRef.current?.getSettings?.()?.deviceId || null } catch { return null }
    })()

    const currentMicId = (() => {
      try { return audioTrackRef.current?.getSettings?.()?.deviceId || null } catch { return null }
    })()

    const currentVideoLive =
      !!videoTrackRef.current &&
      videoTrackRef.current.readyState === "live" &&
      videoTrackRef.current.enabled !== false

    const currentAudioLive =
      !!audioTrackRef.current &&
      audioTrackRef.current.readyState === "live" &&
      audioTrackRef.current.enabled !== false

    const sameCam = selectedCamId
      ? selectedCamId === currentCamId
      : (!deviceOpts.camId && currentVideoLive)

    const sameMic = selectedMicId
      ? selectedMicId === currentMicId
      : (!deviceOpts.micId && currentAudioLive)

    try {
      setError("")
      setProctorSetupReason("Cambiando vista previa de dispositivos…")

      setDeviceSwitching({
        cam: !sameCam,
        mic: !sameMic,
      })

      const policy = proctorPolicyRef.current || {}

      const requireVideo = policy.requireVideo !== false
      const requireAudio = policy.requireAudio !== false
      const wantVideo = policy.wantVideo !== false
      const wantAudio = policy.wantAudio !== false

      if (
        sameCam &&
        sameMic &&
        streamRef.current &&
        (!wantVideo || currentVideoLive) &&
        (!wantAudio || currentAudioLive)
      ) {
        attachStreamToVideo(devicePreviewVideoRef.current, streamRef.current)
        attachStreamToVideo(proctorVideoRef.current, streamRef.current)

        updateProctorFromTracks(videoTrackRef.current, audioTrackRef.current)
        startMicMeter(audioTrackRef.current)

        if (videoTrackRef.current?.readyState === "live") {
          startCameraAnalyzer()
        }

        setProctorSetupReason("Vista previa activa. Muévete ligeramente frente a la cámara.")
        return true
      }

      stopProctoring()
      clearProctorDeviceState()

      setCameraHealth(cleanCameraHealth())
      cameraHealthRef.current = cleanCameraHealth()

      // Pequeña pausa para que el navegador libere la cámara anterior.
      await new Promise((resolve) => setTimeout(resolve, 250))

      if (requestId !== previewSwitchSeqRef.current) {
        return false
      }

      const exact = await openExactDevices(selectedCamId, selectedMicId, {
        wantVideo,
        wantAudio,
        requireVideo,
        requireAudio,
      })

      if (requestId !== previewSwitchSeqRef.current) {
        try {
          exact?.vStream?.getTracks?.().forEach((t) => t.stop())
          exact?.aStream?.getTracks?.().forEach((t) => t.stop())
        } catch { }

        return false
      }

      const vTrack = exact.vStream?.getVideoTracks?.()[0] || null
      const aTrack = exact.aStream?.getAudioTracks?.()[0] || null

      assertSelectedDeviceWasUsed({
        track: vTrack,
        selectedId: selectedCamId,
        kind: "cámara",
        streamToStop: exact.vStream,
      })

      assertSelectedDeviceWasUsed({
        track: aTrack,
        selectedId: selectedMicId,
        kind: "micrófono",
        streamToStop: exact.aStream,
      })

      const actualCamId = getTrackDeviceId(vTrack) || selectedCamId || null
      const actualMicId = getTrackDeviceId(aTrack) || selectedMicId || null

      if (requireVideo && !vTrack) {
        throw new Error("No se pudo obtener una cámara válida del dispositivo seleccionado.")
      }

      if (requireAudio && !aTrack) {
        throw new Error("No se pudo obtener un micrófono válido del dispositivo seleccionado.")
      }

      mergeStreamsToPreview(exact.vStream, exact.aStream)

      videoTrackRef.current = vTrack
      audioTrackRef.current = aTrack

      setDeviceOpts((prev) => ({
        ...prev,
        camId: actualCamId,
        micId: actualMicId,
      }))

      updateProctorFromTracks(vTrack, aTrack)
      startMicMeter(aTrack)

      setProctor({
        ready: true,
        cam: requireVideo ? !!vTrack : true,
        mic: requireAudio ? !!aTrack : true,
        blocked: false,
        reason: "",
      })

      await refreshDeviceList()

      setProctorSetupReason("Vista previa actualizada. Verificando luz, rostro y movimiento…")

      if (vTrack) {
        setTimeout(() => {
          startCameraAnalyzer()
        }, 300)
      }

      return true
    } catch (err) {
      const msg =
        err?.message ||
        "No se pudo cambiar la vista previa. Revisa permisos o selecciona otro dispositivo."

      setProctorSetupReason(msg)
      setError(msg)

      setProctor((prev) => ({
        ...prev,
        ready: true,
        cam: false,
        blocked: true,
        reason: msg,
      }))

      return false
    } finally {
      setDeviceSwitching({ cam: false, mic: false })
    }
  }, [
    deviceOpts.camId,
    deviceOpts.micId,
    stopProctoring,
    clearProctorDeviceState,
    openExactDevices,
    mergeStreamsToPreview,
    refreshDeviceList,
    updateProctorFromTracks,
    startMicMeter,
    attachStreamToVideo,
    startCameraAnalyzer,
  ])

  const scheduleDevicePreviewChange = useCallback((nextDraft) => {
    try {
      if (previewSwitchTimerRef.current) {
        clearTimeout(previewSwitchTimerRef.current)
      }
    } catch { }

    previewSwitchTimerRef.current = setTimeout(() => {
      previewSelectedDevices(
        nextDraft.camId || null,
        nextDraft.micId || null
      )
    }, 250)
  }, [previewSelectedDevices])

  useEffect(() => {
    return () => {
      try {
        if (previewSwitchTimerRef.current) {
          clearTimeout(previewSwitchTimerRef.current)
        }
      } catch { }
    }
  }, [])


  // ✅ variable del horario (solo interesa para presencial)
  // en virtual lo dejamos true por defecto (como hoy)
  // ✅ Presencial: proctoring SOLO si el horario lo exige
  const presencialProctorRequired = useMemo(() => {
    const v =
      mySchedule?.requiere_camara_microfono ??
      mySchedule?.proctor_presencial ??
      mySchedule?.presencial_proctoring ??
      false
    return toBool(v)
  }, [
    mySchedule?.requiere_camara_microfono,
    mySchedule?.proctor_presencial,
    mySchedule?.presencial_proctoring,
  ])

  // ✅ Virtual: SIEMPRE hay proctoring (independiente del flag)
  const cameraLevel = useMemo(() => {
    return normalizeRequirementLevel(
      examRuntimeConfig?.camara_nivel ??
      examen?.exam_config?.camara_nivel ??
      examen?.camara_nivel ??
      mySchedule?.camara_nivel ??
      (isVirtualExam ? "requerido" : "no_requerido")
    );
  }, [
    examRuntimeConfig?.camara_nivel,
    examen?.exam_config?.camara_nivel,
    examen?.camara_nivel,
    mySchedule?.camara_nivel,
    isVirtualExam,
  ]);

  const microphoneLevel = useMemo(() => {
    return normalizeRequirementLevel(
      examRuntimeConfig?.microfono_nivel ??
      examen?.exam_config?.microfono_nivel ??
      examen?.microfono_nivel ??
      mySchedule?.microfono_nivel ??
      (isVirtualExam ? "requerido" : "no_requerido")
    );
  }, [
    examRuntimeConfig?.microfono_nivel,
    examen?.exam_config?.microfono_nivel,
    examen?.microfono_nivel,
    mySchedule?.microfono_nivel,
    isVirtualExam,
  ]);

  const requireCamera = useMemo(() => {
    if (qaMode) return false;
    return cameraLevel === "requerido" || isVirtualExam;
  }, [qaMode, cameraLevel, isVirtualExam]);

  const requireMicrophone = useMemo(() => {
    if (qaMode) return false;
    return microphoneLevel === "requerido" || isVirtualExam;
  }, [qaMode, microphoneLevel, isVirtualExam]);

  const optionalCamera = useMemo(() => {
    if (qaMode) return false;
    return cameraLevel === "opcional";
  }, [qaMode, cameraLevel]);

  const optionalMicrophone = useMemo(() => {
    if (qaMode) return false;
    return microphoneLevel === "opcional";
  }, [qaMode, microphoneLevel]);

  const requireProctor = useMemo(() => {
    if (qaMode) return false;

    return (
      requireCamera ||
      requireMicrophone ||
      optionalCamera ||
      optionalMicrophone ||
      presencialProctorRequired
    );
  }, [
    qaMode,
    requireCamera,
    requireMicrophone,
    optionalCamera,
    optionalMicrophone,
    presencialProctorRequired,
  ]);


  const normalizeExamRuntimeConfig = (...sources) => {
    const src = Object.assign({}, ...sources.filter(Boolean))

    return {
      horario_examen_id: Number(src.horario_examen_id ?? src.horario_id ?? src.id ?? 0) || null,
      curso_id: Number(src.curso_id ?? src.course_id ?? 0) || null,

      is_virtual: toBool(src.is_virtual),

      permitir_navegacion: toBool(src.permitir_navegacion),
      mostrar_resumen_final: toBool(src.mostrar_resumen_final),

      numero_preguntas: toPositiveInt(src.numero_preguntas, 0),
      tiempo_limite_minutos: toPositiveInt(src.tiempo_limite_minutos, 10),
      nota_maxima: toNullableNumber(src.nota_maxima),

      requiere_codigo: toBool(src.requiere_codigo),
      habilitacion_unitaria_estudiantes: toBool(src.habilitacion_unitaria_estudiantes),

      camara_nivel: normalizeRequirementLevel(
        src.camara_nivel ??
        src.camera_level ??
        src.camara ??
        (toBool(src.requireCamera ?? src.requiere_camara) ? "requerido" : "no_requerido")
      ),

      microfono_nivel: normalizeRequirementLevel(
        src.microfono_nivel ??
        src.microphone_level ??
        src.microfono ??
        (toBool(src.requireMic ?? src.requiere_microfono) ? "requerido" : "no_requerido")
      ),
      numero_fotos_requeridas: toPositiveInt(src.numero_fotos_requeridas, 0),
      umbral_decibeles_microfono: toNullableNumber(src.umbral_decibeles_microfono),

      cierre_por_penalizaciones: toBool(
        src.cierre_por_penalizaciones ??
        src.close_on_penalties ??
        src.requireSecurityClose ??
        false
      ),

      limite_penalizaciones: toPositiveInt(
        src.limite_penalizaciones ??
        src.maxWarningsExam ??
        src.max_violations ??
        0,
        0
      ),
    }
  }

  const verifyCameraBeforeExam = useCallback(async ({ durationMs = CAMERA_HEALTH_LIMITS.verifyDurationMs } = {}) => {
    if (!requireCamera) return true

    setError("")
    setProctorSetupReason("Verificando cámara: mira al frente, centra tu rostro y muévete ligeramente…")

    setCameraHealth((prev) => {
      const next = {
        ...(prev || cleanCameraHealth()),
        validating: true,
        validated: false,
        reason: "Esperando imagen real de la cámara…",
      }

      cameraHealthRef.current = next
      return next
    })

    const hasVideoFrame = await waitForCameraFrame()

    if (!hasVideoFrame) {
      const reason = "No se pudo leer la imagen de la cámara. Revisa permisos, selecciona otra cámara o cierra otra app que la esté usando."

      const next = {
        ...cleanCameraHealth(),
        ready: false,
        videoReady: false,
        ok: false,
        validated: false,
        validating: false,
        reason,
        checkedAt: Date.now(),
      }

      cameraHealthRef.current = next
      setCameraHealth(next)

      setProctor((prev) => ({
        ...prev,
        cam: false,
        blocked: true,
        reason,
      }))

      setProctorSetupReason(reason)
      return false
    }

      cameraAnalyzerRef.current = {
      timer: cameraAnalyzerRef.current?.timer || null,
      prevGray: null,
      startedAt: Date.now(),
      lastMotionAt: Date.now(),
      motionRecoveredAt: 0,
      darkSince: 0,
      frames: 0,
      motionHits: 0,
    }

    const started = Date.now()

    let last = cameraHealthRef.current || cleanCameraHealth()
    let lastFace = cameraHealthRef.current || cleanCameraHealth()

    let frames = 0
    let motionHits = 0
    let faceHits = 0
    let faceChecks = 0

    let bestMotion = 0

    let lumaSum = 0
    let lumaCount = 0

    let contrastSum = 0
    let contrastCount = 0

    let centerLumaSum = 0
    let centerLumaCount = 0

    let centerContrastSum = 0
    let centerContrastCount = 0

    let maxOverBrightRatio = 0
    let maxDarkRatio = 0

    while (Date.now() - started < durationMs) {
      last = analyzeCameraFrameOnce()

      if (last?.ready && last?.videoReady) {
        frames += 1

        const luma = Number(last.avgLuma)
        const contrast = Number(last.contrast)
        const centerLuma = Number(last.centerLuma)
        const centerContrast = Number(last.centerContrast)
        const overBrightRatio = Number(last.overBrightRatio || 0)
        const darkRatio = Number(last.darkRatio || 0)
        const motion = Number(last.motionScore || 0)

        if (Number.isFinite(luma)) {
          lumaSum += luma
          lumaCount += 1
        }

        if (Number.isFinite(contrast)) {
          contrastSum += contrast
          contrastCount += 1
        }

        if (Number.isFinite(centerLuma)) {
          centerLumaSum += centerLuma
          centerLumaCount += 1
        }

        if (Number.isFinite(centerContrast)) {
          centerContrastSum += centerContrast
          centerContrastCount += 1
        }

        maxOverBrightRatio = Math.max(maxOverBrightRatio, overBrightRatio)
        maxDarkRatio = Math.max(maxDarkRatio, darkRatio)

        if (motion >= CAMERA_HEALTH_LIMITS.motionDiffMin) {
          motionHits += 1
        }

        bestMotion = Math.max(bestMotion, motion)
      }

      // Rostro: no hace falta analizar cada frame, pero sí varias veces.
      if (
        Date.now() - Number(lastFaceDetectionAtRef.current || 0) >=
        CAMERA_HEALTH_LIMITS.faceDetectEveryMs
      ) {
        lastFaceDetectionAtRef.current = Date.now()
        lastFace = await analyzeFaceQualityOnce()

        if (lastFace?.faceChecked) {
          faceChecks += 1
          if (lastFace.faceVisible === true) {
            faceHits += 1
          }
        }
      }

      await new Promise((resolve) => setTimeout(resolve, 420))
    }

    const avgStableLuma = lumaCount > 0
      ? Math.round(lumaSum / lumaCount)
      : Number(last?.avgLuma || 0)

    const avgContrast = contrastCount > 0
      ? Math.round(contrastSum / contrastCount)
      : Number(last?.contrast || 0)

    const avgCenterLuma = centerLumaCount > 0
      ? Math.round(centerLumaSum / centerLumaCount)
      : Number(last?.centerLuma || 0)

    const avgCenterContrast = centerContrastCount > 0
      ? Math.round(centerContrastSum / centerContrastCount)
      : Number(last?.centerContrast || 0)

    const enoughFrames = frames >= CAMERA_HEALTH_LIMITS.verifyMinFrames

    const enoughLight =
      avgStableLuma >= CAMERA_HEALTH_LIMITS.stableLumaMin &&
      avgStableLuma <= CAMERA_HEALTH_LIMITS.stableLumaMax &&
      avgCenterLuma >= CAMERA_HEALTH_LIMITS.centerLumaMin &&
      maxDarkRatio < 0.52

    const notOverBright =
      maxOverBrightRatio < CAMERA_HEALTH_LIMITS.overBrightRatioMax

    const enoughContrast =
      avgContrast >= CAMERA_HEALTH_LIMITS.contrastMin &&
      avgCenterContrast >= CAMERA_HEALTH_LIMITS.centerContrastMin

    const usingNativeFaceDetector =
      lastFace?.faceSource === "FaceDetector"

    const usingFallbackFace =
      String(lastFace?.faceSource || "").includes("quality_fallback")

    const enoughFaceChecks =
      faceChecks >= CAMERA_HEALTH_LIMITS.verifyMinFaceChecks

    const enoughFace =
      enoughFaceChecks &&
      (
        usingNativeFaceDetector
          ? faceHits >= CAMERA_HEALTH_LIMITS.verifyMinFaceHits
          : usingFallbackFace
            ? lastFace?.faceVisible === true
            : faceHits >= CAMERA_HEALTH_LIMITS.verifyMinFaceHits
      )

    const enoughMotion =
      motionHits >= CAMERA_HEALTH_LIMITS.verifyMinMotionHits ||
      bestMotion >= CAMERA_HEALTH_LIMITS.motionDiffMin * 1.35 ||
      enoughFace === true

    let reason = ""

    if (!enoughFrames) {
      reason = "No se recibieron suficientes frames de la cámara. Espera un momento o selecciona otra cámara."
    } else if (!enoughLight) {
      reason = "La cámara no tiene luz suficiente. Usa luz frontal y evita estar en una habitación oscura."
    } else if (!notOverBright) {
      reason = "La cámara está sobreexpuesta. Evita luz directa o una ventana detrás/frente a la cámara."
    } else if (!enoughContrast) {
      reason = "La imagen no se ve clara. Limpia la cámara, evita sombras fuertes y mejora la iluminación."
    } else if (!enoughFaceChecks) {
      reason = "No se pudo comprobar el rostro suficientes veces. Mantente frente a la cámara unos segundos."
    } else if (!enoughFace) {
      reason =
        lastFace?.faceReason ||
        "No se aprecia claramente tu rostro. Mira al frente, centra tu rostro y asegúrate de que tu cara esté visible."
    } else if (!enoughMotion) {
      reason = "No se detectó movimiento suficiente. Mueve ligeramente la cabeza o parpadea frente a la cámara."
    }

    if (reason) {
      const next = {
        ...(last || cleanCameraHealth()),
        ...(lastFace || {}),

        ready: true,
        videoReady: true,
        ok: false,
        validated: false,
        validating: false,

        reason,

        avgLuma: avgStableLuma,
        contrast: avgContrast,
        centerLuma: avgCenterLuma,
        centerContrast: avgCenterContrast,
        overBrightRatio: safeRound(maxOverBrightRatio, 3),
        darkRatio: safeRound(maxDarkRatio, 3),

        motionScore: safeRound(bestMotion, 2),
        motionHits,
        frames,

        checkedAt: Date.now(),
      }

      cameraHealthRef.current = next
      setCameraHealth(next)

      setProctor((prev) => ({
        ...prev,
        cam: false,
        blocked: true,
        reason,
      }))

      setProctorSetupReason(reason)
      return false
    }

    const next = {
      ...(last || cleanCameraHealth()),
      ...(lastFace || {}),

      ready: true,
      videoReady: true,
      ok: true,
      validated: true,
      validating: false,

      dark: false,
      overBright: false,
      lowContrast: false,
      noMotion: false,

      faceChecked: true,
      faceVisible: true,
      faceReason: "",

      reason: "",

      avgLuma: avgStableLuma,
      contrast: avgContrast,
      centerLuma: avgCenterLuma,
      centerContrast: avgCenterContrast,
      overBrightRatio: safeRound(maxOverBrightRatio, 3),
      darkRatio: safeRound(maxDarkRatio, 3),

      motionScore: safeRound(bestMotion, 2),
      motionHits,
      frames,

      checkedAt: Date.now(),
    }

    cameraHealthRef.current = next
    setCameraHealth(next)

    updateProctorFromTracks(videoTrackRef.current, audioTrackRef.current)

    setProctorSetupReason("Cámara validada correctamente. Rostro visible, luz adecuada y movimiento detectado.")
    return true
  }, [
    requireCamera,
    waitForCameraFrame,
    analyzeCameraFrameOnce,
    analyzeFaceQualityOnce,
    updateProctorFromTracks,
  ])

  const getCurrentLiveDeviceIds = useCallback(() => {
    const liveCamId = (() => {
      try {
        return videoTrackRef.current?.getSettings?.()?.deviceId || null;
      } catch {
        return null;
      }
    })();

    const liveMicId = (() => {
      try {
        return audioTrackRef.current?.getSettings?.()?.deviceId || null;
      } catch {
        return null;
      }
    })();

    return {
      camId: liveCamId || deviceOpts.camId || localStorage.getItem("proctor.camId") || null,
      micId: liveMicId || deviceOpts.micId || localStorage.getItem("proctor.micId") || null,
    };
  }, [deviceOpts.camId, deviceOpts.micId]);

  const markCurrentDevicesAsValidated = useCallback(() => {
    const ids = getCurrentLiveDeviceIds()

    const payload = {
      camId: ids.camId || null,
      micId: ids.micId || null,
      at: Date.now(),
    }

    validatedProctorDevicesRef.current = payload

    try {
      sessionStorage.setItem(proctorDeviceValidationKey, JSON.stringify(payload))
    } catch { }

    setCameraHealth((prev) => {
      const next = {
        ...(prev || cleanCameraHealth()),
        ready: true,
        videoReady: true,
        ok: true,
        validated: true,
        validating: false,
        reason: "",
      }

      cameraHealthRef.current = next
      return next
    })
  }, [getCurrentLiveDeviceIds, proctorDeviceValidationKey])

  const confirmDeviceModal = useCallback(async () => {
    if (deviceSwitching.cam || deviceSwitching.mic) return

    setError("")

    const currentHealth = cameraHealthRef.current || cleanCameraHealth()

    if (requireCamera && currentHealth.validated !== true) {
      setCameraValidationStep("invalid")
      setProctorSetupReason("Primero debes validar la cámara antes de usar estos dispositivos.")
      return
    }

    const ok = await applySelectedDevices(
      deviceDraft.camId || null,
      deviceDraft.micId || null
    )

    if (!ok) return

    markCurrentDevicesAsValidated();

    setProctorSetupReason("")
    setDeviceModalOpen(false)
  }, [
    applySelectedDevices,
    deviceDraft.camId,
    deviceDraft.micId,
    deviceSwitching.cam,
    deviceSwitching.mic,
    requireCamera,
    markCurrentDevicesAsValidated,
  ])

  const validateCameraFromModal = useCallback(async () => {
    if (deviceSwitching.cam || deviceSwitching.mic) return

    setError("")
    setCameraValidationStep("validating")
    setProctorSetupReason("Preparando cámara seleccionada…")

    const ok = await applySelectedDevices(
      deviceDraft.camId || null,
      deviceDraft.micId || null
    )

    if (!ok) {
      setCameraValidationStep("invalid")
      return
    }

    setProctorSetupReason("Validando luz, encuadre, rostro y movimiento…")

    const cameraOk = await verifyCameraBeforeExam({
      durationMs: CAMERA_HEALTH_LIMITS.verifyDurationMs,
    })

    if (!cameraOk) {
      setCameraValidationStep("invalid")
      return
    }

    markCurrentDevicesAsValidated();

    setCameraValidationStep("valid")
    setProctorSetupReason("Cámara validada correctamente. Ya puedes usar estos dispositivos.")
  }, [
    applySelectedDevices,
    deviceDraft.camId,
    deviceDraft.micId,
    verifyCameraBeforeExam,
    deviceSwitching.cam,
    deviceSwitching.mic,
    markCurrentDevicesAsValidated,
  ])

  useEffect(() => {
    if (!deviceModalOpen) return

    let cancelled = false

    const bootPreview = async () => {
      try {
        setError("")

        await refreshDeviceList()

        if (cancelled) return

        // Si ya hay stream activo, solo lo conecta al video del modal.
        if (streamRef.current) {
          attachStreamToVideo(devicePreviewVideoRef.current, streamRef.current)

          if (videoTrackRef.current?.readyState === "live") {
            startCameraAnalyzer()
          }

          return
        }

        // Si aún no hay stream, intenta activarlo para que el estudiante pueda verse.
        await startProctoring()

        if (cancelled) return

        if (streamRef.current) {
          attachStreamToVideo(devicePreviewVideoRef.current, streamRef.current)
        }
      } catch (err) {
        if (!cancelled) {
          setProctorSetupReason(
            err?.message ||
            "No se pudo mostrar la cámara. Revisa permisos o selecciona otro dispositivo."
          )
        }
      }
    }

    setTimeout(bootPreview, 120)

    return () => {
      cancelled = true
    }
  }, [
    deviceModalOpen,
    refreshDeviceList,
    attachStreamToVideo,
    startCameraAnalyzer,
    startProctoring,
  ])

  const requireCamMic = requireProctor;

  const requiredSnapshotCount = useMemo(() => {
    return toPositiveInt(
      examRuntimeConfig?.numero_fotos_requeridas ??
      examen?.exam_config?.numero_fotos_requeridas ??
      examen?.numero_fotos_requeridas ??
      mySchedule?.numero_fotos_requeridas ??
      0,
      0
    )
  }, [
    examRuntimeConfig?.numero_fotos_requeridas,
    examen?.exam_config?.numero_fotos_requeridas,
    examen?.numero_fotos_requeridas,
    mySchedule?.numero_fotos_requeridas,
  ])

  const snapshotStateKey = useMemo(() => {
    if (presentacion?.presentacion_id) return `exam_proctor_snapshots:${presentacion.presentacion_id}`
    if (mySchedule?.horario_id) return `exam_proctor_snapshots:horario:${mySchedule.horario_id}`
    return `exam_proctor_snapshots:curso:${codigoCurso}`
  }, [presentacion?.presentacion_id, mySchedule?.horario_id, codigoCurso])

  const readSnapshotState = useCallback(() => {
    const raw = safeParse(localStorage.getItem(snapshotStateKey), null)

    if (raw && typeof raw === "object") {
      snapshotStateRef.current = {
        count: toPositiveInt(raw.count, 0),
        failed: toPositiveInt(raw.failed, 0),
        tags: Array.isArray(raw.tags) ? raw.tags.slice(-80) : [],
      }
    } else {
      snapshotStateRef.current = {
        count: 0,
        failed: 0,
        tags: [],
      }
    }

    return snapshotStateRef.current
  }, [snapshotStateKey])

  const writeSnapshotState = useCallback(() => {
    try {
      localStorage.setItem(snapshotStateKey, JSON.stringify(snapshotStateRef.current))
    } catch { }
  }, [snapshotStateKey])

  useEffect(() => {
    readSnapshotState()
  }, [snapshotStateKey, readSnapshotState])

  const mediaPerms = useMediaPermissions({
    requireAudio: requireMicrophone,
    requireVideo: requireCamera,
    persistKey: "proctor",
  })

  useEffect(() => {
    proctorPolicyRef.current = {
      requireVideo: !!requireCamera,
      requireAudio: !!requireMicrophone,
      wantVideo: !!(requireCamera || optionalCamera),
      wantAudio: !!(requireMicrophone || optionalMicrophone),
    }
  }, [
    requireCamera,
    requireMicrophone,
    optionalCamera,
    optionalMicrophone,
  ])


  const getLiveProctorStatus = useCallback(() => {
    const vTrack = videoTrackRef.current;
    const aTrack = audioTrackRef.current;

    const camLive =
      !!vTrack &&
      vTrack.readyState === "live" &&
      vTrack.enabled !== false;

    const micLive =
      !!aTrack &&
      aTrack.readyState === "live" &&
      aTrack.enabled !== false;

    const camOk = requireCamera ? camLive : true;
    const micOk = requireMicrophone ? micLive : true;

    const blocked = !camOk || !micOk;

    return {
      camOk,
      micOk,
      camLive,
      micLive,
      blocked,
      vTrack,
      aTrack,
    };
  }, [requireCamera, requireMicrophone]);


  const invalidateCurrentDeviceValidation = useCallback(() => {
    validatedProctorDevicesRef.current = {
      camId: null,
      micId: null,
      at: 0,
    }

    try {
      sessionStorage.removeItem(proctorDeviceValidationKey)
    } catch { }

    setCameraHealth((prev) => {
      const next = {
        ...(prev || cleanCameraHealth()),
        validated: false,
      }

      cameraHealthRef.current = next
      return next
    })
  }, [proctorDeviceValidationKey])

  const currentDevicesAlreadyValidated = useCallback(() => {
    const live = getLiveProctorStatus()

    if (live.blocked) return false

    const health = cameraHealthRef.current || cleanCameraHealth()
    const validated = readValidatedProctorDevices() || {}

    const hasFreshValidation =
      !!validated?.at &&
      Date.now() - Number(validated.at) <= 30 * 60 * 1000

    if (health.validated !== true && !hasFreshValidation) {
      return false
    }

    const ids = getCurrentLiveDeviceIds()

    const sameDeviceOrUnknown = (required, validatedId, currentId) => {
      if (!required) return true

      // Algunos navegadores no exponen deviceId de forma estable.
      // Si el stream está vivo y la cámara ya fue validada, no fuerces a abrir el modal otra vez.
      if (!validatedId || !currentId) return true

      return String(validatedId) === String(currentId)
    }

    return (
      sameDeviceOrUnknown(requireCamera, validated.camId, ids.camId) &&
      sameDeviceOrUnknown(requireMicrophone, validated.micId, ids.micId)
    )
  }, [
    getLiveProctorStatus,
    getCurrentLiveDeviceIds,
    readValidatedProctorDevices,
    requireCamera,
    requireMicrophone,
  ])

  const openPermissionAndDeviceSetup = useCallback(async ({ force = false } = {}) => {
    setError("")

    if (!requireProctor) return true

    const live = getLiveProctorStatus()

    // ✅ Si ya está todo activo y no estamos forzando configuración,
    // no abras el modal otra vez.
    if (!force && !live.blocked) {
      return true
    }

    const needsBrowserPermission =
      (requireCamera && mediaPerms.camState !== "granted") ||
      (requireMicrophone && mediaPerms.micState !== "granted")

    if (needsBrowserPermission) {
      const ok = await mediaPerms.request()

      if (!ok) {
        setProctorSetupReason(
          mediaPerms.error ||
          "No se pudieron habilitar los permisos del navegador. Revisa los permisos del sitio y vuelve a intentar."
        )

        try {
          await refreshDeviceList()
        } catch { }

        setDeviceModalOpen(true)
        return false
      }
    }

    try {
      await refreshDeviceList()
    } catch { }

    // ✅ Si no se pidió abrir selector manualmente, intenta primero iniciar
    // con los dispositivos guardados.
    if (!force) {
      const started = await startProctoring()

      if (started) {
        await new Promise((resolve) => setTimeout(resolve, 250))

        const afterStart = getLiveProctorStatus()

        if (!afterStart.blocked) {
          return true
        }
      }
    }

    // ✅ Solo abre modal si se pidió explícitamente o si no logró iniciar.
    try {
      await openDeviceModal()
    } catch {
      setDeviceModalOpen(true)
    }

    return false
  }, [
    requireProctor,
    requireCamera,
    requireMicrophone,
    mediaPerms,
    refreshDeviceList,
    openDeviceModal,
    getLiveProctorStatus,
    startProctoring,
  ])

  const startRequiredProctoringOrOpenSetup = useCallback(async () => {
    if (!requireProctor) return true

    setError("")
    setProctorSetupReason("")

    // ✅ Primero revisa si ya está activo desde sala de espera.
    const currentLive = getLiveProctorStatus()

    if (!currentLive.blocked) {
      updateProctorFromTracks(currentLive.vTrack, currentLive.aTrack)

      // ✅ Ya validó esta misma cámara/micrófono en el modal.
      // No abrir el selector otra vez.
      if (currentDevicesAlreadyValidated()) {
        setProctorSetupReason("")
        return true
      }

      const cameraOk = await verifyCameraBeforeExam({
        durationMs: CAMERA_HEALTH_LIMITS.verifyDurationMs,
      })

      if (!cameraOk) {
        setLoading(false)
        await openPermissionAndDeviceSetup({ force: true })
        return false
      }

      markCurrentDevicesAsValidated()
      return true
    }

    try {
      await refreshDeviceList()
    } catch { }

    // ✅ No hagas stopProctoring aquí. Primero intenta iniciar/reusar.
    const ok = await startProctoring()

    const requiredText = [
      requireCamera ? "cámara" : null,
      requireMicrophone ? "micrófono" : null,
    ].filter(Boolean).join(" y ")

    const optionalText = [
      optionalCamera ? "cámara opcional" : null,
      optionalMicrophone ? "micrófono opcional" : null,
    ].filter(Boolean).join(" y ")

    const proctorMsg = requiredText
      ? `Este examen requiere ${requiredText} activos. Habilita los permisos para continuar.`
      : optionalText
        ? `Este examen permite ${optionalText}. Puedes habilitarlos para registrar evidencia.`
        : "Este examen requiere validación de dispositivos."

    if (!ok) {
      setLoading(false)

      const reason =
        proctorRef.current?.reason ||
        proctorMsg

      setProctorSetupReason(reason)
      setError("")

      await openPermissionAndDeviceSetup({ force: true })

      return false
    }

    await new Promise((resolve) => setTimeout(resolve, 250))

    const live = getLiveProctorStatus()
    updateProctorFromTracks(live.vTrack, live.aTrack)

    if (live.blocked) {
      setLoading(false)

      const reason =
        requireCamera && requireMicrophone && !live.camLive && !live.micLive
          ? "No se detectó cámara ni micrófono activos. Habilita ambos dispositivos para continuar."
          : requireCamera && !live.camLive
            ? "No se detectó una cámara activa. Habilita la cámara para continuar."
            : requireMicrophone && !live.micLive
              ? "No se detectó un micrófono activo. Habilita el micrófono para continuar."
              : proctorMsg

      setProctorSetupReason(reason)
      setError("")

      await openPermissionAndDeviceSetup({ force: true })

      return false
    }

    if (currentDevicesAlreadyValidated()) {
      setProctorSetupReason("")
      return true
    }

    const cameraOk = await verifyCameraBeforeExam({
      durationMs: CAMERA_HEALTH_LIMITS.verifyDurationMs,
    })

    if (!cameraOk) {
      setLoading(false)
      await openPermissionAndDeviceSetup({ force: true })
      return false
    }

    markCurrentDevicesAsValidated()

    setProctorSetupReason("")
    return true
  }, [
    requireProctor,
    requireCamera,
    requireMicrophone,
    optionalCamera,
    optionalMicrophone,
    refreshDeviceList,
    startProctoring,
    getLiveProctorStatus,
    updateProctorFromTracks,
    openPermissionAndDeviceSetup,
    verifyCameraBeforeExam,
    currentDevicesAlreadyValidated,
    markCurrentDevicesAsValidated,
  ])

  const isWithinSchedule = useMemo(() => {
    const D = mySchedule?.fecha_examen
    const T1 = mySchedule?.hora_inicio
    const T2 = mySchedule?.hora_fin
    if (!D || !T1 || !T2) return false
    const parseYmd = (s) => {
      const iso = String(s).match(/(\d{4})-(\d{2})-(\d{2})/); if (iso) return { y: +iso[1], m: +iso[2], d: +iso[3] }
      const dmy = String(s).match(/(\d{2})[/-](\d{2})[/-](\d{4})/); if (dmy) return { y: +dmy[3], m: +dmy[2], d: +dmy[1] }; return null
    }
    const parseHms = (s) => { const m = String(s).match(/(\d{1,2}):(\d{2})(?::(\d{2}))?/); if (!m) return null; return { h: +m[1], mi: +m[2], s: +(m[3] ?? 0) } }
    const makeLocalDate = (dateStr, timeStr) => { const d = parseYmd(dateStr); const t = parseHms(timeStr); if (!d || !t) return null; return new Date(d.y, d.m - 1, d.d, t.h, t.mi, t.s) }
    const start = makeLocalDate(D, T1)
    const end0 = makeLocalDate(D, T2)
    if (!start || !end0 || isNaN(start) || isNaN(end0)) return false
    const end = end0 <= start ? new Date(end0.getTime() + 24 * 60 * 60 * 1000) : end0
    const now = new Date()
    return now >= start && now <= end
  }, [mySchedule?.fecha_examen, mySchedule?.hora_inicio, mySchedule?.hora_fin])

  const validateBeforeStartExam = useCallback(async ({ scheduleForStart, codeToSend }) => {
    if (!scheduleForStart?.horario_id) {
      return {
        ok: false,
        message: "No se pudo resolver el horario del examen.",
      };
    }

    if (mySchedule?.presentacion?.completada === true || scheduleForStart?.presentacion?.completada === true) {
      return {
        ok: false,
        message: "Este examen ya fue completado.",
      };
    }

    if (!qaMode && !isWithinSchedule) {
      return {
        ok: false,
        message: "El examen no está disponible en este horario.",
      };
    }

    if (!qaMode && scheduleForStart?.habilitado !== true) {
      return {
        ok: false,
        message: "El docente debe habilitarte para rendir este examen.",
      };
    }

    if (toBool(scheduleForStart?.requiere_codigo) && !String(codeToSend || "").trim()) {
      return {
        ok: false,
        message: "Debes ingresar el código de acceso del examen.",
      };
    }

    return { ok: true };
  }, [
    mySchedule?.presentacion?.completada,
    qaMode,
    isWithinSchedule,
  ]);

  const runStartExamFlow = useCallback(async ({ codeToSend, scheduleOverride = null }) => {
    const scheduleForStart = scheduleOverride || mySchedule;
    const scheduleId = Number(scheduleForStart?.horario_id);

    setLoading(true);
    setError("");

    const precheck = await validateBeforeStartExam({
      scheduleForStart,
      codeToSend,
    });

    if (!precheck.ok) {
      setLoading(false);
      setError(precheck.message);
      return;
    }

    if (!Number.isFinite(scheduleId) || scheduleId <= 0) {
      setError("Horario inválido (no se pudo resolver horario_examen_id).");
      setLoading(false);
      return;
    }

    if (requireProctor) {
      const live = getLiveProctorStatus();

      if (currentDevicesAlreadyValidated()) {
        updateProctorFromTracks(live.vTrack, live.aTrack);
        setProctorSetupReason("");
      } else if (live.blocked) {
        const proctorOk = await startRequiredProctoringOrOpenSetup();

        if (!proctorOk) {
          setLoading(false);
          return;
        }
      } else {
        // Si hay cámara/micrófono vivos pero todavía no estaban marcados como validados,
        // validamos una vez y dejamos guardada esa validación.
        const cameraOk = await verifyCameraBeforeExam({
          durationMs: CAMERA_HEALTH_LIMITS.verifyDurationMs,
        });

        if (!cameraOk) {
          setLoading(false);
          await openPermissionAndDeviceSetup({ force: true });
          return;
        }

        markCurrentDevicesAsValidated();
      }
    }
    try {
      localStorage.removeItem(`exam:kiosk:curso:${codigoCurso}`);
      localStorage.removeItem(`exam:kiosk:curso:${codigoCurso}:log`);
      localStorage.removeItem(`exam_proctor_metrics:curso:${codigoCurso}`);
    } catch { }

    const started = await startExam(Number(examId), {
      horario_examen_id: scheduleId,
      horario_id: scheduleId,
      codigo: codeToSend,
      strictTime: !qaMode,
      qa: qaMode,
    });

    setExamStudentIdentity(
      pickStudentIdentity(
        user,
        scheduleForStart?.student,
        scheduleForStart?.estudiante,
        scheduleForStart?.usuario,
        started?.student,
        started?.estudiante,
        started?.usuario
      )
    );

    setExamRuntimeConfig(
      normalizeExamRuntimeConfig(
        scheduleForStart,
        started?.exam_config,
        started?.schedule_config
      )
    )

    const pres = started?.presentacion_id
      ? {
        presentacion_id: started.presentacion_id,
        estado: started.estado || "en_progreso",
        fecha_inicio: started.fecha_inicio || new Date().toISOString(),
      }
      : started;

    setPresentacion(pres);

    const limitN = Number(scheduleForStart?.numero_preguntas || 0) || undefined;

    const data = await getExamWithQuestions(Number(examId), {
      shuffle: true,
      limit: limitN,
      strictTime: !qaMode,
      qa: qaMode,
      presentacionId: Number(pres?.presentacion_id || started?.presentacion_id),
    });

    if (!data?.exam || !data?.questions) {
      throw new Error("No se pudieron cargar las preguntas del examen");
    }

    const finalRuntimeConfig = normalizeExamRuntimeConfig(
      mySchedule,
      started?.exam_config,
      started?.schedule_config,
      data?.exam,
      data?.exam?.exam_config,
      data?.meta?.exam_config
    )

    setExamRuntimeConfig(finalRuntimeConfig)

    const rawQs = data.questions || [];
    const uniqQs = dedupeByCanonicalKey(rawQs);
    servedQuestionIdsRef.current = (uniqQs || [])
      .map(q => String(q.canonical || q.id))
      .filter(Boolean);

    setExamen({
      ...(data.exam || {}),
      preguntas: uniqQs,
      exam_config: finalRuntimeConfig,
      student_identity: pickStudentIdentity(
        user,
        scheduleForStart?.student,
        scheduleForStart?.estudiante,
        scheduleForStart?.usuario,
        started?.student,
        started?.estudiante,
        started?.usuario,
        data?.student,
        data?.estudiante,
        data?.usuario
      ),
    });

    const secs = Number(data.exam?.tiempo_limite_minutos || scheduleForStart?.tiempo_limite_minutos || 10) * 60;
    setTimeLeft(secs);
    setTotalSeconds(secs);

    const localKioskPolicy = getKioskPolicyFromConfig(finalRuntimeConfig)

    if (localKioskPolicy.enabled) {
      try { await requestFullscreenSafe(); } catch { }
      try { await kiosk.requestFullscreen(); } catch { }
      try { await kiosk.start(); } catch { }

      setKioskWarn(false)
    } else {
      try { await kiosk.stop(); } catch { }
    }

    setAccesoConcedido(true);
    setCurrentIndex(0);
    setAnswers({});
    setEnviado(false);
    setResultado(null);
    lastSavedRef.current = "";
    setLoading(false);
  }, [
    mySchedule,
    requireProctor,
    getLiveProctorStatus,
    startRequiredProctoringOrOpenSetup,
    codigoCurso,
    startExam,
    examId,
    qaMode,
    getExamWithQuestions,
    kiosk,
    requestFullscreenSafe,
    getKioskPolicyFromConfig,
    validateBeforeStartExam,
    user,
    currentDevicesAlreadyValidated,
    updateProctorFromTracks,
    verifyCameraBeforeExam,
    openPermissionAndDeviceSetup,
    markCurrentDevicesAsValidated,
  ]);


  const ensureExamTermsAccepted = useCallback(async ({ horarioId, continueStart }) => {
    if (!Number.isFinite(Number(horarioId)) || Number(horarioId) <= 0) {
      return false;
    }

    // Si no requiere proctoring, no pedimos términos.
    if (!requireProctor) {
      return true;
    }

    const data = await fetchExamTerms(Number(horarioId));

    if (!data?.requires_terms || data?.accepted) {
      return true;
    }

    setTermsModal({
      open: true,
      loading: false,
      accepting: false,
      data,
      pendingStart: continueStart,
    });

    return false;
  }, [fetchExamTerms, requireProctor]);


  /* ============ PROCTORING (Cámara + Mic) ============ */
  const PROCTOR_GRACE_MS = 900
  const [proctor, setProctor] = useState({ ready: false, cam: false, mic: false, blocked: false, reason: "" })
  const proctorRef = useRef(proctor)
  useEffect(() => { proctorRef.current = proctor }, [proctor])

  const [cameraHealth, setCameraHealth] = useState(cleanCameraHealth)
  const cameraHealthRef = useRef(cleanCameraHealth())

  const [cameraRuntimeAlert, setCameraRuntimeAlert] = useState(null)
  const cameraRuntimeAlertRef = useRef(null)

  useEffect(() => {
    cameraRuntimeAlertRef.current = cameraRuntimeAlert
  }, [cameraRuntimeAlert])

  const lastCameraWarningAtRef = useRef({
    dark: 0,
    no_motion: 0,
  })

  const lastIncidentSnapshotAtRef = useRef({
    dark: 0,
    no_motion: 0,
  })

  const cameraRuntimeRecoverTimerRef = useRef(null)

  const cameraRuntimeDismissedUntilRef = useRef({
    dark: 0,
    no_motion: 0,
  })


  const getCameraValidationLabel = (health = {}) => {
    if (health.validating) return "Validando cámara…"
    if (health.validated) return "Cámara validada correctamente"
    if (health.reason) return health.reason
    if (health.videoReady) return "Vista previa activa. Centra tu rostro dentro del círculo."
    return "Esperando imagen real de la cámara…"
  }

  const getCameraValidationTone = (health = {}) => {
    if (health.validating) return "checking"
    if (health.validated) return "ok"
    if (health.ok === false || health.faceVisible === false) return "bad"
    if (health.videoReady) return "info"
    return "idle"
  }

  const cameraCheckOk = (value) => value === true
  const cameraCheckBad = (value) => value === false

  useEffect(() => {
    cameraHealthRef.current = cameraHealth
  }, [cameraHealth])

  const cameraAnalyzerRef = useRef({
    timer: null,
    prevGray: null,
    startedAt: 0,
    lastMotionAt: 0,
    darkSince: 0,
  })

  const faceDetectorRef = useRef(null)
  const lastFaceDetectionAtRef = useRef(0)

  const cameraIssueStatsRef = useRef({
    dark_events: 0,
    overbright_events: 0,
    low_contrast_events: 0,
    no_motion_events: 0,
    face_missing_events: 0,

    // Se usa para avisos durante el examen.
    warning_events: 0,
    incident_snapshots: 0,

    // Si más adelante quieres cerrar por cámara, aquí queda el contador.
    blocked_events: 0,

    max_dark_ms: 0,
    max_no_motion_ms: 0,

    last_issue: null,
    last_warning: null,
  })

  const snapshotStateRef = useRef({
    count: 0,
    failed: 0,
    tags: [],
  })

  // ✅ Evita carreras entre:
  // - timer aleatorio de fotos
  // - foto final al enviar
  // - foto por incidente de cámara
  const snapshotSendPromiseRef = useRef(null)

  const timeLeftRef = useRef(0)
  const totalSecondsRef = useRef(0)

  useEffect(() => {
    timeLeftRef.current = timeLeft
  }, [timeLeft])

  useEffect(() => {
    totalSecondsRef.current = totalSeconds
  }, [totalSeconds])

  const streamRef = useRef(null)
  const videoTrackRef = useRef(null)
  const audioTrackRef = useRef(null)
  const proctorVideoRef = useRef(null)

  // ✅ Preview de cámara dentro del modal "Elegir cámara y micrófono"
  const devicePreviewVideoRef = useRef(null)
  const previewSwitchSeqRef = useRef(0)
  const previewSwitchTimerRef = useRef(null)

  const [micLevel, setMicLevel] = useState(0)
  const micLevelRef = useRef(0)
  const audioCtxRef = useRef(null)
  const analyserRef = useRef(null)
  const dataArrayRef = useRef(null)
  const rafIdRef = useRef(null)





  const deviceListenerKindRef = useRef(null)
  const deviceChangeHandlerRef = useRef(null)
  const proctorPollRef = useRef(null)
  const lastOkRef = useRef({ cam: 0, mic: 0 })



  const waitingMaxWarnings = useMemo(() => {
    return hardKiosk
      ? Math.max(1, Number(kioskMaxViolations || 1))
      : Math.max(1, Number(limitePenalizaciones || 3) || 3)
  }, [hardKiosk, kioskMaxViolations, limitePenalizaciones])

  const waitingCamOk = useMemo(() => {
    if (!requireCamera) return true
    return !!proctor?.cam
  }, [requireCamera, proctor?.cam])

  const waitingMicOk = useMemo(() => {
    if (!requireMicrophone) return true
    return !!proctor?.mic
  }, [requireMicrophone, proctor?.mic])

  const waitingHardwareOk = waitingCamOk && waitingMicOk

  const waitingAccessOk = useMemo(() => {
    if (waitingTeacherEnable) return false
    if (codeLocked) return false
    if (toBool(mySchedule?.requiere_codigo) && !accessVerified) return false
    return true
  }, [
    waitingTeacherEnable,
    mySchedule?.requiere_codigo,
    accessVerified,
    codeLocked,
  ])

  const canEnterSecureExam = waitingHardwareOk && waitingAccessOk

  const refreshCurrentScheduleStatus = useCallback(async () => {
    if (!course?.id || !mySchedule?.horario_id) {
      return mySchedule;
    }

    const horarioId = Number(mySchedule.horario_id);

    const normalizeList = (res) => {
      if (Array.isArray(res?.bookings)) return res.bookings;
      if (Array.isArray(res?.items)) return res.items;
      if (Array.isArray(res)) return res;
      return [];
    };

    let booking = null;

    try {
      const upcomingRes = await fnsRef.current.fetchMyExamBookings?.({
        curso_id: Number(course.id),
        upcoming: true,
        user_id: user?.id,
        limit: 5,
      });

      const upcoming = normalizeList(upcomingRes);

      booking =
        upcoming.find((b) => Number(b?.horario_examen_id ?? b?.horario_id) === horarioId) ||
        null;

      if (!booking) {
        const allRes = await fnsRef.current.fetchMyExamBookings?.({
          curso_id: Number(course.id),
          upcoming: false,
          user_id: user?.id,
          limit: 5,
        });

        const all = normalizeList(allRes);

        booking =
          all.find((b) => Number(b?.horario_examen_id ?? b?.horario_id) === horarioId) ||
          null;
      }

      if (!booking) {
        return mySchedule;
      }

      const nextSchedule = bookingToMySchedule(booking);

      setMySchedule((prev) => {
        if (!prev) return nextSchedule;

        return {
          ...prev,
          ...nextSchedule,
          presentacion: {
            ...(prev.presentacion || {}),
            ...(nextSchedule.presentacion || {}),
          },
        };
      });

      return {
        ...(mySchedule || {}),
        ...nextSchedule,
        presentacion: {
          ...(mySchedule?.presentacion || {}),
          ...(nextSchedule.presentacion || {}),
        },
      };
    } catch {
      return mySchedule;
    }
  }, [
    course?.id,
    mySchedule,
    user?.id,
  ]);

  const verifySecureAccess = useCallback(async ({ force = false } = {}) => {
    if (validateCodeInFlightRef.current && !force) {
      return false
    }

    if (accessChecking) {
      return false
    }
    if (validateCodeInFlightRef.current || accessChecking) {
      return false
    }

    const refreshedSchedule = await refreshCurrentScheduleStatus()
    const scheduleForValidate = refreshedSchedule || mySchedule
    const horarioId = Number(scheduleForValidate?.horario_id || 0)

    const codeToSend = String(codigo || "")
      .trim()
      .toUpperCase()
      .replace(/[^A-Z0-9]/g, "")
      .slice(0, 10)

    const requiresCode = toBool(scheduleForValidate?.requiere_codigo)

    if (needsTeacherEnable && scheduleForValidate?.habilitado !== true) {
      setAccessVerified(false)
      lastValidatedCodeRef.current = ""

      if (!codeLockedRef.current && !codeLocked) {
        setCodeValidationState("idle")
        setCodeValidationMessage("")
      }

      return false
    }

    if (!force && accessVerified && (!requiresCode || lastValidatedCodeRef.current === codeToSend)) {
      return true
    }

    if (codeLockedRef.current || codeLocked) {
      setAccessVerified(false)
      setCodeValidationState("locked")
      setCodeValidationMessage(`Código bloqueado temporalmente. Intenta nuevamente en ${codeLockLabel}.`)
      return false
    }

    if (requiresCode && !codeToSend) {
      setAccessVerified(false)
      setCodeValidationState("idle")
      setCodeValidationMessage("")
      return false
    }

    if (!examId || !horarioId) {
      setAccessVerified(false)
      setCodeValidationState("error")
      setCodeValidationMessage("No se pudo resolver el examen u horario.")
      return false
    }

    validateCodeInFlightRef.current = true
    validateCodeQuietUntilRef.current = Date.now() + 6000

    setAccessChecking(true)
    setCodeValidationState("checking")
    setCodeValidationMessage("")
    setError("")

    try {
      const result = await fnsRef.current.validateExamAccessCode?.(Number(examId), {
        horario_examen_id: horarioId,
        horario_id: horarioId,
        codigo: codeToSend,
        qa: qaMode,
      })

      if (!result?.ok) {
        const err = new Error(result?.error || "Código inválido.")
        err.code = result?.code
        err.response = result
        throw err
      }

      setAccessVerified(true)
      lastValidatedCodeRef.current = codeToSend

      setCodeAttemptsRemaining(3)
      setCodeLockedUntil(null)
      codeLockedRef.current = false
      clearAccessCodeLock()

      setCodeValidationState("ok")
      setCodeValidationMessage("Código verificado correctamente.")

      setWaitingSecurityState("validated")
      return true
    } catch (err) {
      const data = err?.response || err?.data || err?.payload || {}
      const code = err?.code || data?.code

      const lockedUntil =
        err?.locked_until ||
        data?.locked_until ||
        null

      const remainingRaw =
        err?.attempts_remaining ??
        data?.attempts_remaining

      if (Number.isFinite(Number(remainingRaw))) {
        setCodeAttemptsRemaining(Math.max(0, Number(remainingRaw)))
      }

      setAccessVerified(false)
      lastValidatedCodeRef.current = ""

      if (code === "CODE_LOCKED" || err?.status === 429) {
        const fallbackLockedUntil =
          lockedUntil ||
          new Date(Date.now() + 10 * 60 * 1000).toISOString()

        setCodeAttemptsRemaining(0)
        setCodeLockedUntil(fallbackLockedUntil)
        codeLockedRef.current = true
        saveAccessCodeLock(fallbackLockedUntil)

        setCodeValidationState("locked")
        setCodeValidationMessage("Has superado los intentos permitidos. La validación quedó bloqueada temporalmente.")
        return false
      }

      const remaining =
        Number.isFinite(Number(remainingRaw))
          ? Math.max(0, Number(remainingRaw))
          : codeAttemptsRemaining

      setCodeValidationState("error")
      setCodeValidationMessage(
        data?.error ||
        data?.message ||
        err?.message ||
        `Código incorrecto. Te quedan ${remaining} intento${remaining === 1 ? "" : "s"}.`
      )

      return false
    } finally {
      setAccessChecking(false)
      validateCodeInFlightRef.current = false
      validateCodeQuietUntilRef.current = Date.now() + 6000
    }
  }, [
    codigo,
    mySchedule,
    refreshCurrentScheduleStatus,
    accessVerified,
    accessChecking,
    needsTeacherEnable,
    codeLocked,
    codeLockLabel,
    examId,
    qaMode,
    clearAccessCodeLock,
    saveAccessCodeLock,
    codeAttemptsRemaining,
  ])


  const prepareSecureHardware = useCallback(async () => {
    setError("")

    if (!requireProctor) {
      return true
    }

    // ✅ Si ya está activo desde sala de espera, no abras modal.
    const liveNow = getLiveProctorStatus()

    if (!liveNow.blocked) {
      updateProctorFromTracks(liveNow.vTrack, liveNow.aTrack)

      // ✅ Si el estudiante ya validó estos mismos dispositivos en el modal,
      // no vuelvas a pedir cámara/micrófono antes de entrar al examen.
      if (currentDevicesAlreadyValidated()) {
        setProctorSetupReason("")
        return true
      }

      const cameraOk = await verifyCameraBeforeExam({
        durationMs: CAMERA_HEALTH_LIMITS.verifyDurationMs,
      })

      if (!cameraOk) {
        await openPermissionAndDeviceSetup({ force: true })
        return false
      }

      markCurrentDevicesAsValidated()
      return true
    }

    // ✅ Intenta levantar con dispositivos guardados.
    const ok = await startRequiredProctoringOrOpenSetup()

    if (!ok) {
      return false
    }

    if (currentDevicesAlreadyValidated()) {
      setProctorSetupReason("")
      return true
    }

    await new Promise((resolve) => setTimeout(resolve, 250))

    const live = getLiveProctorStatus()

    if (live.blocked) {
      setProctorSetupReason(
        !live.camOk && !live.micOk
          ? "Debes habilitar cámara y micrófono para continuar."
          : !live.camOk
            ? "Debes habilitar la cámara para continuar."
            : "Debes habilitar el micrófono para continuar."
      )

      await openPermissionAndDeviceSetup({ force: true })

      return false
    }

    markCurrentDevicesAsValidated()

    return true
  }, [
    requireProctor,
    startRequiredProctoringOrOpenSetup,
    getLiveProctorStatus,
    updateProctorFromTracks,
    openPermissionAndDeviceSetup,
    verifyCameraBeforeExam,
    currentDevicesAlreadyValidated,
    markCurrentDevicesAsValidated,
  ])

  const registerWaitingRoomStrike = useCallback((reason = "focus_lost") => {
    const now = Date.now()

    if (now - waitingStrikeLockRef.current < 1200) return
    waitingStrikeLockRef.current = now

    setWaitingStrikeReason(reason)

    setWaitingWarnings((prev) => {
      const next = prev + 1

      if (next >= waitingMaxWarnings) {
        setWaitingSecurityState("kicked")
        return next
      }

      setWaitingCountdown(3)
      setWaitingSecurityState("strike_alert")
      return next
    })
  }, [waitingMaxWarnings])

  const enterSecureExam = useCallback(async () => {
    if (enterExamInFlightRef.current) return

    enterExamInFlightRef.current = true
    setError("")

    let fullscreenPrepared = true

    try {
      // ✅ Importante:
      // La pantalla completa debe pedirse al inicio del click.
      // Si se pide después de varios await, el navegador puede bloquearla.
      if (enableKiosk) {
        fullscreenPrepared = await requestFullscreenSafe()

        try {
          kiosk.grantGrace?.(2500)
        } catch { }
      }

      if (accessChecking || validateCodeInFlightRef.current) {
        return
      }

      if (codeLockedRef.current || codeLocked) {
        setCodeValidationState("locked")
        setCodeValidationMessage(`Código bloqueado temporalmente. Intenta nuevamente en ${codeLockLabel}.`)
        return
      }

      const codeToSend = String(codigo || "")
        .trim()
        .toUpperCase()
        .replace(/[^A-Z0-9]/g, "")
        .slice(0, 10)

      const requiresCode = toBool(mySchedule?.requiere_codigo)

      const accessOk =
        accessVerified && (!requiresCode || lastValidatedCodeRef.current === codeToSend)
          ? true
          : await verifySecureAccess()

      if (!accessOk) {
        return
      }

      const hardwareOk = await prepareSecureHardware()

      if (!hardwareOk) {
        return
      }

      const freshSchedule = await refreshCurrentScheduleStatus()

      if (enableKiosk) {
        const fsOk =
          fullscreenPrepared ||
          hasFullscreenSafe() ||
          await requestFullscreenSafe()

        if (!fsOk && !hasFullscreenSafe()) {
          setWaitingSecurityState("privacy")
          setError("El navegador bloqueó la pantalla completa. Haz clic en la pantalla de privacidad y vuelve a ingresar.")
          return
        }

        try {
          await kiosk.requestFullscreen?.()
        } catch { }

        try {
          await kiosk.start?.()
        } catch { }
      }

      setWaitingSecurityState("idle")

      const scheduleForStart = freshSchedule || mySchedule

      const pendingStart = async () => {
        await runStartExamFlow({
          codeToSend,
          scheduleOverride: scheduleForStart,
        })
      }

      const termsOk = await ensureExamTermsAccepted({
        horarioId: Number(scheduleForStart?.horario_id),
        continueStart: pendingStart,
      })

      if (!termsOk) {
        return
      }

      await pendingStart()
    } catch (err) {
      setError(
        err?.message ||
        "No se pudo ingresar al examen. Revisa la conexión, permisos o vuelve a intentarlo."
      )
    } finally {
      enterExamInFlightRef.current = false
    }
  }, [
    accessChecking,
    codeLocked,
    codeLockLabel,
    codigo,
    mySchedule,
    accessVerified,
    verifySecureAccess,
    prepareSecureHardware,
    refreshCurrentScheduleStatus,
    enableKiosk,
    requestFullscreenSafe,
    hasFullscreenSafe,
    kiosk,
    runStartExamFlow,
    ensureExamTermsAccepted,
  ])

  useEffect(() => {
    const shouldProtectWaitingRoom =
      !accesoConcedido &&
      waitingSecurityState === "privacy" &&
      (enableKiosk || requireProctor)

    if (!shouldProtectWaitingRoom) return

    const onVisibilityChange = () => {
      if (document.visibilityState === "hidden") {
        registerWaitingRoomStrike("visibility_hidden")
      }
    }

    const onBlur = () => {
      registerWaitingRoomStrike("window_blur")
    }

    const onFullscreenChange = () => {
      if (enableKiosk && !document.fullscreenElement) {
        registerWaitingRoomStrike("fullscreen_exit")
      }
    }

    document.addEventListener("visibilitychange", onVisibilityChange, true)
    window.addEventListener("blur", onBlur, true)
    document.addEventListener("fullscreenchange", onFullscreenChange, true)
    document.addEventListener("webkitfullscreenchange", onFullscreenChange, true)

    return () => {
      document.removeEventListener("visibilitychange", onVisibilityChange, true)
      window.removeEventListener("blur", onBlur, true)
      document.removeEventListener("fullscreenchange", onFullscreenChange, true)
      document.removeEventListener("webkitfullscreenchange", onFullscreenChange, true)
    }
  }, [
    accesoConcedido,
    waitingSecurityState,
    enableKiosk,
    requireProctor,
    registerWaitingRoomStrike,
  ])

  useEffect(() => {
    if (waitingSecurityState !== "strike_alert") return

    if (waitingCountdown <= 0) {
      setWaitingSecurityState("privacy")
      return
    }

    if (waitingTimerRef.current) {
      clearTimeout(waitingTimerRef.current)
    }

    waitingTimerRef.current = setTimeout(() => {
      setWaitingCountdown((v) => Math.max(0, v - 1))
    }, 1000)

    return () => {
      if (waitingTimerRef.current) {
        clearTimeout(waitingTimerRef.current)
      }
    }
  }, [waitingSecurityState, waitingCountdown])


  // ✅ Políticas del horario_examenes
  const permitirNavegacion = useMemo(() => {
    return toBool(
      examRuntimeConfig?.permitir_navegacion ??
      examen?.exam_config?.permitir_navegacion ??
      examen?.permitir_navegacion ??
      mySchedule?.permitir_navegacion ??
      false
    )
  }, [
    examRuntimeConfig?.permitir_navegacion,
    examen?.exam_config?.permitir_navegacion,
    examen?.permitir_navegacion,
    mySchedule?.permitir_navegacion,
  ])

  const mostrarResumenFinal = useMemo(() => {
    return toBool(
      examRuntimeConfig?.mostrar_resumen_final ??
      examen?.exam_config?.mostrar_resumen_final ??
      examen?.mostrar_resumen_final ??
      mySchedule?.mostrar_resumen_final ??
      false
    )
  }, [
    examRuntimeConfig?.mostrar_resumen_final,
    examen?.exam_config?.mostrar_resumen_final,
    examen?.mostrar_resumen_final,
    mySchedule?.mostrar_resumen_final,
  ])

  // ====== métricas proctor por minuto ======
  const PROCTOR_TICK_MS = 1000
  const proctorMetricsKey = useMemo(() => (
    buildProctorMetricsKey({
      presentacionId: presentacion?.presentacion_id ?? null,
      horarioId: mySchedule?.horario_id ?? null,
      codigoCurso,
    })
  ), [presentacion?.presentacion_id, mySchedule?.horario_id, codigoCurso]);

  const proctorMetricsRef = useRef({
    version: 1,
    started_at: null,
    ended_at: null,
    presentacion_id: null,
    exam_id: null,
    course_id: null,
    minutes: [],     // lista de buckets por minuto
    peaks: [],       // picos (por minuto)
    last_flush_ts: 0,
  })

  const minuteAccRef = useRef({
    minuteIndex: 0,
    t0: 0,
    samples: 0,
    micSum: 0,
    micMax: 0,
    micMin: 100,
    camOk: 0,
    micOk: 0,
    proctorBlocked: 0,
    kioskBlocked: 0,
  })

  const proctorTickRef = useRef(null)

  const loadProctorMetricsFromCache = useCallback(() => {
    const cached = safeParse(localStorage.getItem(proctorMetricsKey), null)
    if (cached && typeof cached === "object") {
      proctorMetricsRef.current = {
        ...proctorMetricsRef.current,
        ...cached,
        version: 1,
      }
    }
  }, [proctorMetricsKey])

  const flushProctorMetricsToCache = useCallback((markPending = false) => {
    try {
      const payload = {
        ...proctorMetricsRef.current,
        last_flush_ts: Date.now(),
        pending_send: !!markPending,
      }
      localStorage.setItem(proctorMetricsKey, JSON.stringify(payload))
    } catch { }
  }, [proctorMetricsKey])

  const resetMinuteAccumulator = useCallback((minuteIndex, nowTs) => {
    minuteAccRef.current = {
      minuteIndex,
      t0: nowTs,
      samples: 0,
      micSum: 0,
      micMax: 0,
      micMin: 100,
      camOk: 0,
      micOk: 0,
      proctorBlocked: 0,
      kioskBlocked: 0,
    }
  }, [])

  const commitMinuteBucket = useCallback(() => {
    const acc = minuteAccRef.current
    if (!acc || acc.samples <= 0) return

    const micAvg = Math.round(acc.micSum / acc.samples)
    const micMax = Math.round(acc.micMax)
    const micMin = Math.round(acc.micMin === 100 ? 0 : acc.micMin)

    const pct = (x) => Math.round((x / acc.samples) * 100)

    const bucket = {
      minute_index: acc.minuteIndex,
      ts_start: acc.t0,
      samples: acc.samples,
      mic_avg: micAvg,
      mic_max: micMax,
      mic_min: micMin,
      cam_ok_pct: pct(acc.camOk),
      mic_ok_pct: pct(acc.micOk),
      proctor_blocked_pct: pct(acc.proctorBlocked),
      kiosk_blocked_pct: pct(acc.kioskBlocked),
    }

    proctorMetricsRef.current.minutes = Array.isArray(proctorMetricsRef.current.minutes)
      ? [...proctorMetricsRef.current.minutes, bucket]
      : [bucket]

    // “pico” por minuto (lo que tú llamas picos de porcentaje)
    proctorMetricsRef.current.peaks = Array.isArray(proctorMetricsRef.current.peaks)
      ? [...proctorMetricsRef.current.peaks, { ts: Date.now(), minute_index: acc.minuteIndex, mic_peak: micMax }]
      : [{ ts: Date.now(), minute_index: acc.minuteIndex, mic_peak: micMax }]
  }, [])

  const finalizeMinuteIfPartial = useCallback(() => {
    commitMinuteBucket();
    flushProctorMetricsToCache(true);

    // ✅ evita que el snapshot final vuelva a agregar el minuto parcial
    try { minuteAccRef.current.samples = 0; } catch { }
  }, [commitMinuteBucket, flushProctorMetricsToCache]);


  useEffect(() => {
    const inExam =
      accesoConcedido &&
      examen &&
      !waitingAfterTimeout &&
      !enviado &&
      !resultado

    if (!inExam) {
      try { if (proctorTickRef.current) clearInterval(proctorTickRef.current) } catch { }
      proctorTickRef.current = null
      return
    }

    // init desde cache si existe
    loadProctorMetricsFromCache()

    // set meta
    const now = Date.now()
    const pm = proctorMetricsRef.current
    if (!pm.started_at) pm.started_at = now
    pm.presentacion_id = presentacion?.presentacion_id ?? pm.presentacion_id ?? null
    pm.exam_id = examId ?? pm.exam_id ?? null
    pm.course_id = course?.id ?? pm.course_id ?? null

    // si ya había minutos guardados, continuar índice
    const nextMinuteIndex = Array.isArray(pm.minutes) ? pm.minutes.length : 0
    resetMinuteAccumulator(nextMinuteIndex, now)

    // tick cada segundo: acumula y cada 60 samples guarda bucket
    proctorTickRef.current = setInterval(() => {
      const acc = minuteAccRef.current
      if (!acc) return

      // si está bloqueado por proctor o kiosk, igual contamos (eso sirve para % bloqueado)
      const mic = Number(micLevelRef.current || 0)
      const camOk = !!proctorRef.current?.cam
      const micOk = !!proctorRef.current?.mic
      const pBlocked = !!proctorRef.current?.blocked
      const kBlocked = enableKiosk && !!kiosk.blocked

      acc.samples += 1
      acc.micSum += mic
      acc.micMax = Math.max(acc.micMax, mic)
      acc.micMin = Math.min(acc.micMin, mic)

      if (camOk) acc.camOk += 1
      if (micOk) acc.micOk += 1
      if (pBlocked) acc.proctorBlocked += 1
      if (kBlocked) acc.kioskBlocked += 1

      // cada 60 segundos (aprox) -> commit minuto
      if (acc.samples >= 60) {
        commitMinuteBucket()
        flushProctorMetricsToCache(true)
        resetMinuteAccumulator(acc.minuteIndex + 1, Date.now())
      }
    }, PROCTOR_TICK_MS)

    return () => {
      try { if (proctorTickRef.current) clearInterval(proctorTickRef.current) } catch { }
      proctorTickRef.current = null
      // guardado best-effort al salir del examen
      flushProctorMetricsToCache(true)
    }
  }, [
    accesoConcedido,
    examen,
    waitingAfterTimeout,
    enviado,
    resultado,
    presentacion?.presentacion_id,
    examId,
    course?.id,
    kiosk.blocked,
    loadProctorMetricsFromCache,
    resetMinuteAccumulator,
    commitMinuteBucket,
    flushProctorMetricsToCache,
  ])

  const requiereCodigo = toBool(mySchedule?.requiere_codigo)


  useEffect(() => {
    if (!requiereCodigo) setCodigo("")
  }, [requiereCodigo])

  const isSecureOk = () => {
    try {
      const host = typeof window !== "undefined" ? window.location.hostname : ""
      const isLocalhost = /(^|\.)localhost$|^127\.0\.0\.1$|^::1$/.test(host)
      const https = typeof window !== "undefined" && (window.isSecureContext || window.location.protocol === "https:")
      return https || isLocalhost
    } catch { return false }
  }

  const detachDeviceListener = () => {
    try {
      if (!navigator.mediaDevices) return
      if (deviceListenerKindRef.current === "addEventListener") {
        navigator.mediaDevices.removeEventListener?.("devicechange", deviceChangeHandlerRef.current)
      } else if (deviceListenerKindRef.current === "ondevicechange") {
        if (navigator.mediaDevices.ondevicechange === deviceChangeHandlerRef.current) {
          navigator.mediaDevices.ondevicechange = null
        }
      }
    } catch { }
    deviceListenerKindRef.current = null
    deviceChangeHandlerRef.current = null
  }




  async function ensureVideoPref(prefId) {
    if (!prefId || !navigator?.mediaDevices?.enumerateDevices) return null
    try {
      const devs = await navigator.mediaDevices.enumerateDevices()
      const list = devs.filter(d => d.kind === "videoinput")
      return list.find(d => d.deviceId === prefId) || null
    } catch { return null }
  }
  async function findVideoByGroupId(groupId) {
    if (!groupId || !navigator?.mediaDevices?.enumerateDevices) return null
    try {
      const devs = await navigator.mediaDevices.enumerateDevices()
      const list = devs.filter(d => d.kind === "videoinput" && d.groupId === groupId)
      return list[0] || null
    } catch { return null }
  }
  const openVideo = async () => {
    const pref = deviceOpts.camId || localStorage.getItem("proctor.camId") || null
    let prefDev = await ensureVideoPref(pref)
    if (prefDev) {
      try {
        return await navigator.mediaDevices.getUserMedia({
          video: {
            deviceId: { exact: prefDev.deviceId },
            width: { ideal: 640 },
            height: { ideal: 480 },
            aspectRatio: { ideal: 4 / 3 },
            facingMode: "user"
          }
        })
      } catch {
        try {
          return await navigator.mediaDevices.getUserMedia({
            video: {
              deviceId: prefDev.deviceId,
              width: { ideal: 640 },
              height: { ideal: 480 },
              aspectRatio: { ideal: 4 / 3 },
              facingMode: "user"
            }
          })
        } catch {
          try {
            const byGroup = await findVideoByGroupId(prefDev.groupId)
            if (byGroup) {
              return await navigator.mediaDevices.getUserMedia({
                video: {
                  deviceId: { exact: byGroup.deviceId },
                  width: { ideal: 640 },
                  height: { ideal: 480 },
                  aspectRatio: { ideal: 4 / 3 },
                  facingMode: "user"
                }
              })
            }
          } catch { }
        }
      }
    }
    const tries = [
      { video: { facingMode: "user", width: { ideal: 640 }, height: { ideal: 480 }, aspectRatio: { ideal: 4 / 3 } } },
      { video: { facingMode: "user" } },
      { video: true },
    ]
    for (const c of tries) { try { return await navigator.mediaDevices.getUserMedia(c) } catch { } }
    return null
  }

  async function ensureAudioPref(prefId) {
    if (!prefId || !navigator?.mediaDevices?.enumerateDevices) return null
    try {
      const devs = await navigator.mediaDevices.enumerateDevices()
      const list = devs.filter(d => d.kind === "audioinput")
      return list.find(d => d.deviceId === prefId) || null
    } catch { return null }
  }
  const openAudio = async () => {
    const pref = deviceOpts.micId || localStorage.getItem("proctor.micId") || null
    const opts = { echoCancellation: true, noiseSuppression: true, autoGainControl: true }
    const prefDev = await ensureAudioPref(pref)
    if (prefDev) {
      try {
        return await navigator.mediaDevices.getUserMedia({ audio: { deviceId: { exact: prefDev.deviceId }, ...opts } })
      } catch {
        try { return await navigator.mediaDevices.getUserMedia({ audio: { deviceId: prefDev.deviceId, ...opts } }) } catch { }
      }
    }
    const tries = [{ audio: opts }, { audio: { echoCancellation: true } }, { audio: true }]
    for (const c of tries) { try { return await navigator.mediaDevices.getUserMedia(c) } catch { } }
    return null
  }



  const retryProctoring = useCallback(async () => {
    stopProctoring()
    setProctor({ ready: false, cam: false, mic: false, blocked: true, reason: "Reintentando…" })
    const ok = await startProctoring()
    if (!ok) setProctor(prev => ({ ...prev, blocked: true }))
  }, [startProctoring, stopProctoring])

  const activeKioskReason =
    kioskWarnReason ||
    kiosk.lastViolationReason ||
    kiosk.reason ||
    "attention_required"

  const kioskFriendly =
    reasonMap[activeKioskReason] ||
    "Se detectó una salida del modo seguro. Vuelve a pantalla completa y mantén el foco en el examen para continuar."

  const prevBlockedRef = useRef(kiosk.blocked)
  useEffect(() => {
    if (
      prevBlockedRef.current === true &&
      kiosk.blocked === false &&
      kioskWarnModeRef.current !== "camera"
    ) {
      setKioskWarn(false)
    }

    prevBlockedRef.current = kiosk.blocked
  }, [kiosk.blocked])

  // Confirm modal
  const [confirm, setConfirm] = useState({ open: false, title: "", message: "", onConfirm: null })
  // Aviso navegación (cuando no se permite volver)
  const [navGuard, setNavGuard] = useState({ open: false, message: "" })
  const closeNavGuard = useCallback(() => setNavGuard({ open: false, message: "" }), [])

  const askConfirm = useCallback((title, message, onConfirm) => { setConfirm({ open: true, title, message, onConfirm }) }, [])
  const closeConfirm = useCallback(() => { setConfirm((c) => ({ ...c, open: false, onConfirm: null })) }, [])

  const isPresentationCompleted = (b) => {
    const presObj =
      b?.presentacion ||
      b?.presentation ||
      (Array.isArray(b?.presentaciones) && b.presentaciones[0]) ||
      (Array.isArray(b?.attempts) && b.attempts[0]) ||
      null
    const estadoRaw = b?.presentacion_estado ?? b?.estado_presentacion ?? presObj?.estado ?? presObj?.status ?? ""
    const estado = String(estadoRaw || "").toLowerCase()
    const fechaFin = b?.presentacion_fecha_finalizacion ?? b?.fecha_finalizacion ?? presObj?.fecha_finalizacion ?? null
    return (
      estado === "completado" ||
      estado === "completada" ||
      estado === "finalizado" ||
      estado === "finalizada" ||
      (fechaFin != null && String(fechaFin).length > 0)
    )
  }

  const bookingToMySchedule = (b) => {
    const candidates = [b.is_virtual, b.virtual, b.horario_is_virtual, b.he_is_virtual, b.isVirtual, b.horario_virtual, b?.horario?.is_virtual]
    const firstDefined = candidates.find((v) => v !== undefined && v !== null)
    return {
      horario_id: b.horario_examen_id ?? b.horario_id,
      fecha_examen: b.fecha_examen,
      hora_inicio: b.hora_inicio,
      hora_fin: b.hora_fin,
      is_virtual: toBool(firstDefined),
      habilitado: [b.habilitado, b.enabled, b.is_enabled].some(toBool),
      // ✅ NUEVO (importantísimo)
      numero_preguntas: Number(
        b?.numero_preguntas ??
        b?.horario_numero_preguntas ??
        b?.he_numero_preguntas ??
        b?.horario?.numero_preguntas ??
        0
      ) || null,

      requiere_codigo: toBool(
        b?.requiere_codigo ??
        b?.horario_requiere_codigo ??
        b?.he_requiere_codigo ??
        b?.horario?.requiere_codigo ??
        false
      ),
      ubicacion_info: (
        b?.ubicacion_info ??
        b?.horario_ubicacion_info ??
        b?.he_ubicacion_info ??
        b?.horario?.ubicacion_info ??
        null
      ),

      // opcional (si quieres)
      tiempo_limite_minutos: Number(
        b?.tiempo_limite_minutos ??
        b?.horario_tiempo_limite_minutos ??
        b?.he_tiempo_limite_minutos ??
        b?.horario?.tiempo_limite_minutos ??
        0
      ) || null,
      habilitacion_unitaria_estudiantes: toBool(
        b?.habilitacion_unitaria_estudiantes ??
        b?.horario_habilitacion_unitaria_estudiantes ??
        b?.he_habilitacion_unitaria_estudiantes ??
        b?.horario?.habilitacion_unitaria_estudiantes ??
        false
      ),
      presentacion: {
        id: b.presentacion_id ?? null,
        estado: b.presentacion_estado ?? (isPresentationCompleted(b) ? "completado" : null),
        examen_id: b.presentacion_examen_id ?? null,
        porcentaje: b.porcentaje_obtenido ?? null,
        aprobado: b.aprobado ?? null,
        fecha_finalizacion: b.presentacion_fecha_finalizacion ?? null,
        remaining_seconds: b.presentacion_remaining_seconds ?? null,
        completada: !!b.presentacion_completada || isPresentationCompleted(b),
      },
      permitir_navegacion: toBool(
        b?.permitir_navegacion ??
        b?.horario_permitir_navegacion ??
        b?.he_permitir_navegacion ??
        b?.horario?.permitir_navegacion ??
        false
      ),
      mostrar_resumen_final: toBool(
        b?.mostrar_resumen_final ??
        b?.horario_mostrar_resumen_final ??
        b?.he_mostrar_resumen_final ??
        b?.horario?.mostrar_resumen_final ??
        false
      ),
      camara_nivel: normalizeRequirementLevel(
        b?.camara_nivel ??
        b?.horario_camara_nivel ??
        b?.he_camara_nivel ??
        b?.horario?.camara_nivel ??
        "no_requerido"
      ),

      microfono_nivel: normalizeRequirementLevel(
        b?.microfono_nivel ??
        b?.horario_microfono_nivel ??
        b?.he_microfono_nivel ??
        b?.horario?.microfono_nivel ??
        "no_requerido"
      ),

      numero_fotos_requeridas: toPositiveInt(
        b?.numero_fotos_requeridas ??
        b?.horario_numero_fotos_requeridas ??
        b?.he_numero_fotos_requeridas ??
        b?.horario?.numero_fotos_requeridas ??
        0,
        0
      ),

      umbral_decibeles_microfono: toNullableNumber(
        b?.umbral_decibeles_microfono ??
        b?.horario_umbral_decibeles_microfono ??
        b?.he_umbral_decibeles_microfono ??
        b?.horario?.umbral_decibeles_microfono ??
        null
      ),

      cierre_por_penalizaciones: toBool(
        b?.cierre_por_penalizaciones ??
        b?.horario_cierre_por_penalizaciones ??
        b?.he_cierre_por_penalizaciones ??
        b?.horario?.cierre_por_penalizaciones ??
        false
      ),

      limite_penalizaciones: toPositiveInt(
        b?.limite_penalizaciones ??
        b?.horario_limite_penalizaciones ??
        b?.he_limite_penalizaciones ??
        b?.horario?.limite_penalizaciones ??
        0,
        0
      ),

      habilitacion_unitaria_estudiantes: toBool(
        b?.habilitacion_unitaria_estudiantes ??
        b?.horario_habilitacion_unitaria_estudiantes ??
        b?.he_habilitacion_unitaria_estudiantes ??
        b?.horario?.habilitacion_unitaria_estudiantes ??
        false
      ),

      requiere_camara_microfono: toBool(
        b?.requiere_camara_microfono ??
        b?.horario_requiere_camara_microfono ??
        b?.he_requiere_camara_microfono ??
        b?.horario?.requiere_camara_microfono ??
        false
      ),

      kiosk_presencial: toBool(
        b?.kiosk_presencial ??
        b?.modo_seguro_presencial ??
        b?.requiere_modo_seguro ??
        b?.horario?.kiosk_presencial ??
        false
      ),
    }
  }

  const buildZeroLocal = (examen, totalSeconds, timeLeft) => {
    const preguntas = Array.isArray(examen?.preguntas) ? examen.preguntas : []
    let puntosTotal = 0
    let autoGradables = 0

    const detalle = preguntas.map((p, idx) => {
      const puntos = Number(p?.puntos ?? 1) || 1
      const keyRaw = ensureJSON(p?.respuesta_correcta, p?.respuesta_correcta ?? null)
      const hasKey = keyRaw !== undefined && keyRaw !== null

      if (hasKey) { puntosTotal += puntos; autoGradables++ }

      return {
        idx,
        preguntaId: p.id,
        enunciado: getEnunciado(p),
        tipo: String(p.tipo_pregunta || p.tipo || "").toLowerCase(),
        puntos: hasKey ? puntos : 0,
        respuesta: null,
        respuesta_texto: "",
        esperado: null,
        esperado_texto: "",
        correcto: hasKey ? false : null,
      }
    })

    return {
      puntosGanados: 0,
      puntosTotal,
      porcentaje: puntosTotal > 0 ? 0 : 0,
      aprobado: false,
      autoGradables,
      correctas: 0,
      incorrectas: autoGradables,
      tiempo_empleado_segundos: (totalSeconds || 0) - (timeLeft || 0),
      detalle,
    }
  }

  // ====== Calificar local + DETALLE ======
  const calificar = useCallback(() => {
    if (!examen) return null
    let puntosTotal = 0, puntosGanados = 0, autoGradables = 0, correctas = 0
    const detalle = []

      ; (examen.preguntas || []).forEach((p, idx) => {
        const tipo = String(p.tipo_pregunta || p.tipo || "").toLowerCase()
        const puntos = Number(p.puntos ?? 1) || 1
        const ansRaw = answers[p.id]
        const keyRaw = ensureJSON(p.respuesta_correcta, p.respuesta_correcta ?? null)
        const hasKey = typeof keyRaw !== "undefined" && keyRaw !== null
        const enunciado = getEnunciado(p)

        if (hasKey) { puntosTotal += puntos; autoGradables++ }

        const ops = readOptionsForView(p)
        const opsMap = new Map(ops.map(o => [String(o.id), o.texto]))

        const formatSeleccion = (ids) => (ids || []).map(id => opsMap.get(String(id)) || String(id)).join(", ")
        const formatOrden = (ids) => (ids || []).map((id, i) => `${i + 1}. ${opsMap.get(String(id)) || String(id)}`).join("  ·  ")
        const colA = (p?.opciones?.columna_a || []).map((o, i) => ({ id: String(o?.id ?? `A${i + 1}`), texto: o?.texto ?? String(o?.value ?? "") }))
        const colB = (p?.opciones?.columna_b || []).map((o, i) => ({ id: String(o?.id ?? `B${i + 1}`), texto: o?.texto ?? String(o?.value ?? "") }))
        const mapA = new Map(colA.map(o => [o.id, o.texto]))
        const mapB = new Map(colB.map(o => [o.id, o.texto]))
        const formatPairs = (pairs) => (pairs || []).map(str => {
          const [a, b] = String(str).split("|")
          return `${mapA.get(a) || a} → ${mapB.get(b) || b}`
        }).join("  ·  ")

        let correcto = null
        let expectedNorm = null
        let expectedText = ""
        let givenText = ""

        if (tipo === "seleccion_multiple") {
          const selected = Array.isArray(ansRaw) ? ansRaw.map(String) : (typeof ansRaw === "string" && ansRaw ? [String(ansRaw)] : [])
          const correctIds = Array.isArray(keyRaw?.correctas)
            ? keyRaw.correctas.map(String)
            : Array.isArray(keyRaw) ? keyRaw.map(String) : keyRaw?.id ? [String(keyRaw.id)] : []
          if (hasKey) correcto = JSON.stringify([...selected].sort()) === JSON.stringify([...correctIds].sort())
          expectedNorm = correctIds
          expectedText = formatSeleccion(correctIds)
          givenText = formatSeleccion(selected)
        } else if (tipo === "verdadero_falso") {
          const expected = String(keyRaw?.respuesta ?? keyRaw)
          const given = String(ansRaw ?? "")
          if (hasKey) correcto = expected && given ? expected.trim().toLowerCase() === given.trim().toLowerCase() : false
          expectedNorm = expected
          expectedText = expected || ""
          givenText = given || ""
        } else if (tipo === "respuesta_corta") {
          const expected = String(keyRaw?.respuesta ?? keyRaw)
          const given = String(ansRaw ?? "")
          if (hasKey) correcto = given.trim().toLowerCase() === expected.trim().toLowerCase()
          expectedNorm = expected
          expectedText = expected || ""
          givenText = given || ""
        } else if (tipo === "completar") {
          const expected = Array.isArray(keyRaw?.respuestas) ? keyRaw.respuestas : Array.isArray(keyRaw) ? keyRaw : []
          const given = Array.isArray(ansRaw) ? ansRaw : []
          if (hasKey) correcto = expected.length === given.length && expected.every((x, i) => x.trim().toLowerCase() === (given[i] || "").trim().toLowerCase())
          expectedNorm = expected
          expectedText = expected.join(" / ")
          givenText = given.join(" / ")
        } else if (tipo === "ordenamiento") {
          const expected = Array.isArray(keyRaw?.orden_correcto)
            ? keyRaw.orden_correcto.map(String)
            : Array.isArray(keyRaw) ? keyRaw.map(String) : []
          const given = Array.isArray(ansRaw) ? ansRaw.map(String) : []
          if (hasKey) correcto = expected.length > 0 && JSON.stringify(expected) === JSON.stringify(given)
          expectedNorm = expected
          expectedText = formatOrden(expected)
          givenText = formatOrden(given)
        } else if (tipo === "emparejamiento") {
          const expectedPairs = Array.isArray(keyRaw?.pares_correctos) ? keyRaw.pares_correctos : []
          const expectedSet = new Set(expectedPairs.map((pp) => `${pp.a}|${pp.b}`))
          const given = Array.isArray(ansRaw) ? ansRaw : []
          const givenSet = new Set(given.map(String))
          if (hasKey) correcto = expectedSet.size > 0 && expectedSet.size === givenSet.size && [...expectedSet].every((x) => givenSet.has(x))
          expectedNorm = [...expectedSet]
          expectedText = formatPairs([...expectedSet])
          givenText = formatPairs(given)
        } else {
          correcto = null
        }

        if (correcto === true) { puntosGanados += puntos; correctas += 1 }
        detalle.push({
          idx,
          preguntaId: p.id,
          enunciado,
          tipo,
          puntos: hasKey ? puntos : 0,
          respuesta: ansRaw ?? null,
          respuesta_texto: givenText,
          esperado: expectedNorm,
          esperado_texto: expectedText,
          correcto
        })
      })

    const porcentaje = puntosTotal > 0 ? Math.round((puntosGanados / puntosTotal) * 100) : null
    const aprobado = porcentaje !== null ? porcentaje >= (examen.porcentaje_aprobacion || 70) : null
    const incorrectas = autoGradables > 0 ? Math.max(0, autoGradables - correctas) : 0

    return {
      puntosGanados,
      puntosTotal,
      porcentaje,
      aprobado,
      autoGradables,
      correctas,
      incorrectas,
      tiempo_empleado_segundos: (totalSeconds || 0) - (timeLeft || 0),
      detalle
    }
  }, [examen, answers, totalSeconds, timeLeft])

  // --------------------- Autosave ---------------------
  const buildDraftAndPayload = useCallback(() => {
    if (!examen?.preguntas?.length) return { draft: {}, index: currentIndex }
    const draft = (examen.preguntas || []).reduce((acc, q) => {
      const v = sanitizeForSchema(q, answers[q.id])
      acc[String(q.id)] = v === null ? null : v
      return acc
    }, {})
    return { draft, index: currentIndex }
  }, [examen?.preguntas, answers, currentIndex])

  const blockedByProctor = requireProctor && proctor.blocked
  const blockedByKiosk = enableKiosk && kiosk.blocked

  // Forzar que el modal de advertencia se muestre siempre que el kiosk esté bloqueado,
  // para evitar que el usuario se quede con la pantalla gris sin poder regresar.
  useEffect(() => {
    // Solo forzar si estamos DENTRO del examen activo (preguntas en pantalla)
    const isExamActive = accesoConcedido && examen && !enviado && !resultado && !waitingAfterTimeout;
    const isReturning = Date.now() < kioskWarnReturningRef.current;

    if (isExamActive && !isReturning && blockedByKiosk && !kioskWarn && !policyLimitTriggeredRef.current) {
      openKioskWarning(KIOSK_WARNING_SECONDS, kiosk.reason || "attention_required", "security")
    }
  }, [blockedByKiosk, kioskWarn, kiosk.reason, openKioskWarning, accesoConcedido, examen, enviado, resultado, waitingAfterTimeout])

  const interactionBlocked = (
    (requireProctor && proctor.blocked) ||
    blockedByKiosk
  ) && accesoConcedido && examen && !waitingAfterTimeout && !enviado && !resultado


  // --------------------- Finalizar ---------------------
  const finalizeAttempt = useCallback(
    async (respaldoLocal, motivo = "envio", override = null, proctorSummary = null) => {
      try {
        if (!hasServerPresentation) return normalizeResult(respaldoLocal)

        const motivoSafe = sanitizeMotivo(motivo) // ✅

        const { draft, index } = override?.draft && override?.index != null
          ? { draft: override.draft, index: override.index }
          : buildDraftAndPayload()
        let serverResp = null
        try {
          serverResp = await fnsRef.current.finishExam(presentacion.presentacion_id, {
            answers: draft,
            currentIndex: index,
            motivo: motivoSafe,
            question_ids: servedQuestionIdsRef.current,
            proctor_summary: proctorSummary || undefined,
          })
        } catch (e) {
          const code = e?.code || e?.response?.data?.code
          const status = e?.status || e?.response?.status
          if (!(status === 409 && code === "NOT_IN_PROGRESS")) throw e
        }

        const payload = serverResp?.data || serverResp || null
        if (payload) return normalizeResult(payload)
        return normalizeResult(respaldoLocal)
      } catch {
        return normalizeResult(respaldoLocal)
      }
    },
    [hasServerPresentation, presentacion?.presentacion_id, buildDraftAndPayload],
  )




  const flushSave = useCallback(
    async (force = false) => {
      try {
        if (!hasServerPresentation) return

        const { draft, index } = buildDraftAndPayload()
        const fingerprint = JSON.stringify({ draft, index })
        if (!force && fingerprint === lastSavedRef.current) return

        await fnsRef.current.saveExamProgress?.(presentacion.presentacion_id, draft, index)
        lastSavedRef.current = fingerprint
        localStorage.setItem(
          `exam_prog:${presentacion.presentacion_id}`,
          JSON.stringify({ draft, index, ts: Date.now() })
        )
      } catch (e) {
        const code = e?.code || e?.response?.data?.error || e?.response?.data?.code
        const status = e?.status || e?.response?.status
        if (status === 403 && code === "OUT_OF_TIME") {
          setWaitingAfterTimeout(true)
          if (hasServerPresentation) setError("No se pudo finalizar en el servidor. Intenta nuevamente.")
          return
        }
        try {
          const { draft, index } = buildDraftAndPayload()
          localStorage.setItem(
            `exam_prog:${presentacion.presentacion_id}`,
            JSON.stringify({ draft, index, ts: Date.now(), pending: true })
          )
        } catch { }
      }
    },
    [hasServerPresentation, presentacion?.presentacion_id, buildDraftAndPayload],
  )


  /* ====== PROCTORING SNAPSHOTS ====== */
  const snapshotTimerRef = useRef(null)

  const snapshotPlanRef = useRef({
    key: "",
    totalSec: 0,
    required: 0,
    timesMs: [],
  })

  const captureSnapshotBlob = useCallback(async () => {
    try {
      const v = proctorVideoRef.current
      const vt = videoTrackRef.current
      if (!v || !vt || vt.readyState !== "live") return null
      const w = v.videoWidth || 640
      const h = v.videoHeight || 480
      const canvas = document.createElement("canvas")
      canvas.width = w; canvas.height = h
      const ctx = canvas.getContext("2d")
      ctx.drawImage(v, 0, 0, w, h)
      const blob = await new Promise((res) => canvas.toBlob(res, "image/jpeg", 0.7))
      return blob || null
    } catch { return null }
  }, [])

  const buildMicSnapshotMetrics = async () => {
    const now = Date.now()

    const aTrack = audioTrackRef.current
    const enabled = !!aTrack && aTrack.readyState === "live" && aTrack.enabled !== false

    // deviceId real del track (si el browser lo expone)
    let trackDeviceId = null
    try { trackDeviceId = aTrack?.getSettings?.()?.deviceId || null } catch { }

    // el seleccionado por UI (si existe)
    const chosenId = deviceOpts?.micId || localStorage.getItem("proctor.micId") || null
    const micId = chosenId || trackDeviceId || null

    // label: del enumerateDevices (si hay permisos, ya viene)
    let micLabel = null
    try {
      const list = deviceOpts?.mics || []
      micLabel = list.find(d => d.deviceId === micId)?.label || null
    } catch { }

    const permState = await safePermState()

    // ====== Agregados desde proctorMetricsRef + minuto parcial ======
    const pm = proctorMetricsRef.current || {}
    const minutes = Array.isArray(pm.minutes) ? pm.minutes : []

    // incluir minuto parcial (sin mutar pm) usando lo que hay en minuteAccRef
    const acc = minuteAccRef.current
    let partial = null
    if (acc?.samples > 0) {
      const pct = (x) => Math.round((x / acc.samples) * 100)
      partial = {
        minute_index: acc.minuteIndex,
        ts_start: acc.t0,
        samples: acc.samples,
        mic_avg: Math.round(acc.micSum / acc.samples),
        mic_max: Math.round(acc.micMax),
        mic_min: Math.round(acc.micMin === 100 ? 0 : acc.micMin),
        cam_ok_pct: pct(acc.camOk),
        mic_ok_pct: pct(acc.micOk),
        proctor_blocked_pct: pct(acc.proctorBlocked),
        kiosk_blocked_pct: pct(acc.kioskBlocked),
      }
    }

    const allBuckets = partial ? [...minutes, partial] : minutes

    const agg = (() => {
      if (!allBuckets.length) {
        const cur = Number(micLevelRef.current || 0)
        return { avg: cur, max: cur, min: cur }
      }
      let sum = 0, cnt = 0, mx = 0, mn = 100
      for (const b of allBuckets) {
        const s = Number(b?.samples || 0)
        const a = Number(b?.mic_avg || 0)
        const x = Number(b?.mic_max || 0)
        const n = Number(b?.mic_min || 0)
        if (s > 0) { sum += a * s; cnt += s }
        mx = Math.max(mx, x)
        mn = Math.min(mn, n)
      }
      const avg = cnt > 0 ? Math.round(sum / cnt) : Number(micLevelRef.current || 0)
      return { avg, max: mx, min: (mn === 100 ? 0 : mn) }
    })()

    // dropouts: segundos donde micOk fue false (aprox)
    const dropouts = (() => {
      let d = 0
      for (const b of allBuckets) {
        const s = Number(b?.samples || 0)
        const okPct = Number(b?.mic_ok_pct || 0)
        const ok = Math.round((okPct / 100) * s)
        d += Math.max(0, s - ok)
      }
      // + minuto parcial ya incluido arriba si existía
      return d
    })()

    // duración y sampleRate (si existe AudioContext)
    const startedAt = Number(pm.started_at || 0)
    const durationMs = startedAt ? Math.max(0, now - startedAt) : null
    const sampleRateHz = Number(audioCtxRef.current?.sampleRate || 0) || null

    // NOTA: tu micLevel es 0..100 (no dB real). Lo guardamos “como db” por compat,
    // y además guardamos todo el JSON crudo en mic_metrics.
    return {
      // campos “rápidos” (a columnas)
      mic_enabled: enabled,
      mic_permission_state: permState,
      mic_device_id: micId,
      mic_device_label: micLabel,

      mic_duration_ms: durationMs,
      mic_sample_rate_hz: sampleRateHz,
      mic_samples_count: null,

      mic_avg_db: agg.avg,
      mic_max_db: agg.max,
      mic_min_db: agg.min,

      mic_silence_pct: null,
      mic_clip_count: null,
      mic_dropouts_count: dropouts,

      // JSON completo
      mic_metrics: {
        ts: now,
        current_level_pct: Number(micLevelRef.current || 0),
        aggregated_level_pct: agg,
        dropouts_seconds_approx: dropouts,
        buckets: allBuckets,
        peaks: Array.isArray(pm.peaks) ? pm.peaks : [],
        device: { micId, micLabel, trackDeviceId, chosenId },
        permission_state: permState,
        started_at: pm.started_at || null,
        presentacion_id: pm.presentacion_id || null,
        exam_id: pm.exam_id || null,
        course_id: pm.course_id || null,
        ua: typeof navigator !== "undefined" ? navigator.userAgent : null,
        note: "levels are 0..100 (rms-derived), not real dB",
      },
    }
  }

  const sendSnapshot = useCallback(async ({
    final = false,
    tag = null,
    force = false,

    // true  = foto normal planificada
    // false = foto por incidente, PERO igual consume cupo del total configurado
    countsRequired = true,

    incidentType = null,
    incidentReason = null,
  } = {}) => {
    let runPromise = null

    try {
      if (!requireProctor) return false

      // ✅ Serializa capturas: si ya hay una foto subiendo, espera a que termine.
      // Esto evita que el timer y el submit final suban dos fotos al mismo tiempo.
      while (snapshotSendPromiseRef.current) {
        await snapshotSendPromiseRef.current.catch(() => false)
      }

      runPromise = (async () => {
        readSnapshotState()

        const currentCount = Number(snapshotStateRef.current.count || 0)
        const maxCount = Number(requiredSnapshotCount || 0)

        // ✅ Regla dura:
        // Si el horario dice 0 fotos, NO se sube ninguna.
        // Si dice 5, jamás se sube la 6, aunque sea final o incidente.
        if (maxCount <= 0) return false
        if (currentCount >= maxCount) return false

        const nextNo = currentCount + 1

        // ✅ Las fotos normales respetan bloqueo del proctor.
        // Las forzadas se permiten solo si aún hay cupo.
        if (!final && !force && proctorRef.current.blocked) return false

        const blob = await captureSnapshotBlob()

        if (!blob) {
          snapshotStateRef.current.failed += 1
          writeSnapshotState()
          return false
        }

        const micPack = final ? await buildMicSnapshotMetrics() : null
        const health = cameraHealthRef.current || cleanCameraHealth()

        const normalizedTag =
          tag ||
          (
            final
              ? `final_${nextNo}`
              : countsRequired
                ? `required_${nextNo}`
                : `incident_${incidentType || "camera"}_${nextNo}`
          )

        const response = await fnsRef.current.uploadProctorSnapshot?.({
          blob,
          presentacionId: presentacion?.presentacion_id || null,
          examId,
          courseId: course?.id || null,
          horarioExamenId: mySchedule?.horario_id || null,
          mic: micPack,
          final: final ? 1 : 0,
          tag: normalizedTag,
          codigoCurso,
          meta: {
            required_snapshot_count: maxCount,

            // ✅ Toda foto consume un número dentro del máximo configurado.
            snapshot_number: nextNo,
            snapshot_kind: countsRequired ? "required" : "incident",

            incident_type: incidentType,
            incident_reason: incidentReason,

            camera_health: health,
            camera_issue_stats: cameraIssueStatsRef.current,
          },
        })

        // ✅ El backend puede rechazar suavemente si ya se llegó al límite.
        // No lo contamos como fallo.
        if (response?.skipped) {
          const serverTotal = toPositiveInt(response?.total_snapshots, currentCount)

          snapshotStateRef.current.count = clampNumber(serverTotal, 0, maxCount)
          writeSnapshotState()

          return false
        }

        if (response?.ok !== false) {
          // ✅ Incrementa SIEMPRE, sea required o incident.
          // Así una foto por incidente ocupa 1 de las 5 configuradas, no crea una 6ta.
          snapshotStateRef.current.count = Math.min(maxCount, currentCount + 1)

          snapshotStateRef.current.tags = [
            ...(Array.isArray(snapshotStateRef.current.tags) ? snapshotStateRef.current.tags : []),
            {
              tag: normalizedTag,
              ts: Date.now(),
              snapshot_id: response?.snapshot_id || null,
              kind: countsRequired ? "required" : "incident",
              incident_type: incidentType || null,
              number: nextNo,
            },
          ].slice(-80)

          if (!countsRequired) {
            cameraIssueStatsRef.current.incident_snapshots += 1
          }

          writeSnapshotState()
          return true
        }

        snapshotStateRef.current.failed += 1
        writeSnapshotState()
        return false
      })()

      snapshotSendPromiseRef.current = runPromise
      return await runPromise
    } catch {
      snapshotStateRef.current.failed += 1
      writeSnapshotState()
      return false
    } finally {
      if (snapshotSendPromiseRef.current === runPromise) {
        snapshotSendPromiseRef.current = null
      }
    }
  }, [
    captureSnapshotBlob,
    requireProctor,
    requiredSnapshotCount,
    presentacion?.presentacion_id,
    examId,
    course?.id,
    mySchedule?.horario_id,
    codigoCurso,
    readSnapshotState,
    writeSnapshotState,
    buildMicSnapshotMetrics,
  ])

  useEffect(() => {
    const inExam =
      accesoConcedido &&
      examen &&
      requireProctor &&
      !waitingAfterTimeout &&
      !enviado &&
      !resultado

    if (!inExam) {
      setCameraRuntimeAlert(null)
      cameraRuntimeAlertRef.current = null

      if (kioskWarnModeRef.current === "camera") {
        closeKioskWarning()
      }

      return
    }

    const health = cameraHealthRef.current || cameraHealth || cleanCameraHealth()
    const now = Date.now()

    let type = null
    let reason = null
    let message = ""

    if (
      health.dark &&
      Number(health.darkMs || 0) >= CAMERA_HEALTH_LIMITS.examDarkWarningMs
    ) {
      type = "dark"
      reason = "camera_dark"
      message = "La cámara está demasiado oscura. Mejora la iluminación y mantén tu rostro visible."
    } else if (
      health.noMotion &&
      Number(health.noMotionMs || 0) >= CAMERA_HEALTH_LIMITS.examNoMotionWarningMs
    ) {
      type = "no_motion"
      reason = "camera_no_motion"
      message = "No se detecta movimiento en la cámara. Muévete ligeramente y mantén tu rostro visible."
    }

    // ✅ Si se arregló la cámara, se cierra el aviso de strike de cámara.
    if (!type || !reason) {
      setCameraRuntimeAlert(null)
      cameraRuntimeAlertRef.current = null

      if (kioskWarnModeRef.current === "camera") {
        closeKioskWarning()
      }

      return
    }

    cameraIssueStatsRef.current.warning_events += 1
    cameraIssueStatsRef.current.last_warning = type
    cameraIssueStatsRef.current.last_issue = type

    // ✅ Ya no usamos el modal descuadrado.
    setCameraRuntimeAlert(null)
    cameraRuntimeAlertRef.current = null

    // ✅ Mostrar el MISMO aviso de strike con contador.
    if (!kioskWarn || kioskWarnModeRef.current !== "camera") {
      openKioskWarning(KIOSK_WARNING_SECONDS, reason, "camera")
    }

    // ✅ Evidencia por incidente, pero consume 1 cupo del total configurado.
    // Si el horario tiene 5 fotos, entre fotos normales + incidentes nunca pasará de 5.
    const lastShotAt = Number(lastIncidentSnapshotAtRef.current[type] || 0)
    const canTakeIncidentSnapshot =
      now - lastShotAt >= CAMERA_HEALTH_LIMITS.incidentSnapshotCooldownMs

    if (canTakeIncidentSnapshot) {
      lastIncidentSnapshotAtRef.current[type] = now

      sendSnapshot({
        tag: `incident_${type}_${now}`,
        force: true,
        countsRequired: false,
        incidentType: type,
        incidentReason: message,
      }).catch(() => { })
    }
  }, [
    cameraHealth,
    accesoConcedido,
    examen,
    requireProctor,
    waitingAfterTimeout,
    enviado,
    resultado,
    kioskWarn,
    openKioskWarning,
    closeKioskWarning,
    sendSnapshot,
  ])

  useEffect(() => {
    const inExam =
      accesoConcedido &&
      examen &&
      requireProctor &&
      requireMicrophone &&
      !waitingAfterTimeout &&
      !enviado &&
      !resultado

    if (!inExam) {
      micRuntimeStateRef.current = {
        loudSince: 0,
        lastWarningAt: 0,
      }
      return
    }

    const micThreshold = Number(
      examRuntimeConfig?.umbral_decibeles_microfono ??
      examen?.exam_config?.umbral_decibeles_microfono ??
      examen?.umbral_decibeles_microfono ??
      mySchedule?.umbral_decibeles_microfono ??
      0
    )

    if (!Number.isFinite(micThreshold) || micThreshold <= 0) return

    const level = Number(micLevelRef.current || micLevel || 0)
    const now = Date.now()
    const state = micRuntimeStateRef.current

    if (level >= micThreshold) {
      if (!state.loudSince) state.loudSince = now

      const sustainedMs = now - state.loudSince
      const cooldownMs = 12000

      if (
        sustainedMs >= 2200 &&
        now - Number(state.lastWarningAt || 0) >= cooldownMs
      ) {
        state.lastWarningAt = now

        openKioskWarning(KIOSK_WARNING_SECONDS, "microphone_noise", "security")

        sendSnapshot({
          tag: `incident_microphone_noise_${now}`,
          force: true,
          countsRequired: false,
          incidentType: "microphone_noise",
          incidentReason: "Se detectó ruido elevado o constante en el micrófono durante el examen.",
        }).catch(() => { })
      }

      return
    }

    state.loudSince = 0
  }, [
    accesoConcedido,
    examen,
    requireProctor,
    requireMicrophone,
    waitingAfterTimeout,
    enviado,
    resultado,
    examRuntimeConfig?.umbral_decibeles_microfono,
    examen?.exam_config?.umbral_decibeles_microfono,
    examen?.umbral_decibeles_microfono,
    mySchedule?.umbral_decibeles_microfono,
    micLevel,
    openKioskWarning,
    sendSnapshot,
  ])

  // ==== Mic metrics helper (para snapshot) ====
  const safePermState = async () => {
    try {
      if (!navigator?.permissions?.query) return null
      // En algunos browsers "microphone" puede fallar
      const st = await navigator.permissions.query({ name: "microphone" })
      return st?.state || null
    } catch {
      return null
    }
  }


  const buildProctorFinishSummary = useCallback(() => {
    readSnapshotState()

    const minutes = Array.isArray(proctorMetricsRef.current?.minutes)
      ? proctorMetricsRef.current.minutes
      : []

    const maxOf = (field) => {
      return minutes.reduce((max, item) => {
        const n = Number(item?.[field] || 0)
        return Number.isFinite(n) ? Math.max(max, n) : max
      }, 0)
    }

    return {
      required_snapshot_count: requiredSnapshotCount,
      snapshot_count: Number(snapshotStateRef.current.count || 0),
      snapshot_failed_count: Number(snapshotStateRef.current.failed || 0),
      snapshot_tags: Array.isArray(snapshotStateRef.current.tags) ? snapshotStateRef.current.tags : [],
      camera: {
        current_health: cameraHealthRef.current || cleanCameraHealth(),
        ...cameraIssueStatsRef.current,
      },
      metrics: {
        minutes_count: minutes.length,
        proctor_blocked_pct_max: maxOf("proctor_blocked_pct"),
        kiosk_blocked_pct_max: maxOf("kiosk_blocked_pct"),
        cam_ok_pct_min: minutes.length
          ? minutes.reduce((min, b) => Math.min(min, Number(b?.cam_ok_pct ?? 100)), 100)
          : 100,
        mic_ok_pct_min: minutes.length
          ? minutes.reduce((min, b) => Math.min(min, Number(b?.mic_ok_pct ?? 100)), 100)
          : 100,
      },
    }
  }, [requiredSnapshotCount, readSnapshotState])


  // --------------------- Submit (manual/auto) ---------------------
  const doSubmit = useCallback(
    async (auto = false, autoMotivo = "timeout", opts = {}) => {
      const forceZero = opts?.forceZero === true
      if (enviado) return

      // ✅ Si forceZero => NO guardes progreso, NO uses respuestas actuales
      const rLocal = forceZero
        ? buildZeroLocal(examen, totalSeconds, timeLeft)
        : (calificar() || {})

      if (!auto && !forceZero) await flushSave(true)

      try {
        finalizeMinuteIfPartial()
      } catch { }

      if (requireProctor && requiredSnapshotCount > 0) {
        readSnapshotState()

        let guard = 0

        while (
          Number(snapshotStateRef.current.count || 0) < requiredSnapshotCount &&
          guard < requiredSnapshotCount
        ) {
          guard += 1

          const nextNo = Number(snapshotStateRef.current.count || 0) + 1

          const ok = await sendSnapshot({
            final: nextNo >= requiredSnapshotCount,
            tag: `required_${nextNo}`,
            force: true,
          })

          if (!ok) break
        }
      }

      const proctorFinishSummary = requireProctor ? buildProctorFinishSummary() : null

      // ✅ Si forceZero: manda answers vacías al server para que califique 0
      const serverSummary = await finalizeAttempt(
        rLocal,
        auto ? autoMotivo : "envio",
        forceZero ? { draft: {}, index: 0 } : null,
        proctorFinishSummary
      )

      // ✅ cerrar minuto parcial + cache
      try { finalizeMinuteIfPartial() } catch { }

      // ✅ enviar métricas al server (best-effort)
      let metricsSent = false
      try {
        const metricsPayload = {
          ...proctorMetricsRef.current,
          ended_at: Date.now(),
          finish_summary: proctorFinishSummary,
        }

        const r = await fnsRef.current.uploadProctorMetrics?.({
          presentacionId: presentacion?.presentacion_id ?? null,
          examId,
          courseId: course?.id ?? null,
          horarioExamenId: mySchedule?.horario_id ?? null,
          kind: "proctor_summary",
          metrics: metricsPayload,
        })

        metricsSent = !!r

        localStorage.setItem(
          proctorMetricsKey,
          JSON.stringify({
            ...metricsPayload,
            pending_send: false,
            last_flush_ts: Date.now(),
          })
        )
      } catch {
        // queda en cache como pending_send=true
      }


      try { if (saveTimer.current) clearTimeout(saveTimer.current) } catch { }
      try { clearInterval(timerRef.current) } catch { }
      try { clearInterval(snapshotTimerRef.current) } catch { }
      stopProctoring()

      const merged = {
        ...serverSummary,
        porcentaje: serverSummary?.porcentaje ?? rLocal.porcentaje,
        aprobado: serverSummary?.aprobado ?? rLocal.aprobado,
        puntuacion_obtenida:
          Number.isFinite(serverSummary?.puntuacion_obtenida)
            ? serverSummary.puntuacion_obtenida
            : (Number.isFinite(rLocal.puntosGanados) ? rLocal.puntosGanados : 0),
        puntuacion_maxima:
          Number.isFinite(serverSummary?.puntuacion_maxima)
            ? serverSummary.puntuacion_maxima
            : (Number.isFinite(rLocal.puntosTotal) ? rLocal.puntosTotal : 0),
        respuestas_correctas:
          Number.isFinite(serverSummary?.respuestas_correctas)
            ? serverSummary.respuestas_correctas
            : (Number.isFinite(rLocal.correctas) ? rLocal.correctas : 0),
        respuestas_incorrectas:
          Number.isFinite(serverSummary?.respuestas_incorrectas)
            ? serverSummary.respuestas_incorrectas
            : (Number.isFinite(rLocal.incorrectas) ? rLocal.incorrectas : 0),
        tiempo_empleado_segundos: serverSummary?.tiempo_empleado_segundos ?? rLocal.tiempo_empleado_segundos ?? 0,
        detalle: (() => {
          const mergedDet = mergeDetalles(serverSummary?.detalle || [], rLocal.detalle || []);
          const allowed = new Set((rLocal.detalle || []).map(d => String(d.preguntaId ?? d.pregunta_id ?? d.id ?? "")));
          return mergedDet.filter(d => {
            const k = String(d.preguntaId ?? d.pregunta_id ?? d.questionId ?? d.question_id ?? d.id ?? d.idx ?? "");
            return allowed.has(k);
          });
        })(),

      }

      if (Array.isArray(merged.detalle) && merged.detalle.length) {
        const ptsTot = merged.detalle.reduce((acc, d) => acc + (Number(d.puntos) || 0), 0)
        const ptsOk = merged.detalle.reduce((acc, d) => acc + ((d.correcto === true ? Number(d.puntos) || 0 : 0)), 0)
        const corr = merged.detalle.filter(d => d.correcto === true).length
        const incorr = merged.detalle.filter(d => d.correcto === false).length
        if (!merged.puntuacion_maxima && ptsTot) merged.puntuacion_maxima = ptsTot
        if (!merged.puntuacion_obtenida && ptsOk) merged.puntuacion_obtenida = ptsOk
        if (!merged.respuestas_correctas && corr) merged.respuestas_correctas = corr
        if (!merged.respuestas_incorrectas && incorr) merged.respuestas_incorrectas = incorr
        if ((merged.porcentaje == null || Number.isNaN(merged.porcentaje)) && ptsTot > 0) {
          merged.porcentaje = Math.round((ptsOk / ptsTot) * 100)
        }
      }

      setResultado(merged)
      await kiosk.stop(true)

      // ✅ salir de fullscreen al terminar el examen
      await exitFullscreenSafe()

      // ✅ al final:
      if (metricsSent) {
        localStorage.removeItem(proctorMetricsKey)
      } else {
        // NO borres, queda para reintento
      }

      setEnviado(true)
      try { localStorage.removeItem(kioskLogKey) } catch { }

      try {
        localStorage.removeItem(`${snapshotStateKey}:random-plan:v1`)
      } catch { }

      window.scrollTo({ top: 0, behavior: "smooth" })

    }, [
    enviado,
    examen,
    totalSeconds,
    timeLeft,
    calificar,
    finalizeAttempt,
    flushSave,
    requireProctor,
    requiredSnapshotCount,
    readSnapshotState,
    sendSnapshot,
    buildProctorFinishSummary,
    finalizeMinuteIfPartial,
    stopProctoring,
    kiosk,
    exitFullscreenSafe,
    presentacion?.presentacion_id,
    examId,
    course?.id,
    mySchedule?.horario_id,
    proctorMetricsKey,
    snapshotStateKey,
  ],
  )
  useEffect(() => {
    return () => {
      stopProctoring()
      exitFullscreenSafe()
    }
  }, [stopProctoring, exitFullscreenSafe])


  useEffect(() => {
    if (!hasServerPresentation || !presentacion?.presentacion_id) return

    try {
      const presId = Number(presentacion.presentacion_id)
      if (!presId || presId <= 0) return

      // contador del hook (MISMA key)
      const courseCountKey = `exam:kiosk:curso:${codigoCurso}`
      const presCountKey = `exam:kiosk:${presId}`

      // logs (key aparte)
      const courseLogKey = buildKioskLogKey(courseCountKey)
      const presLogKey = buildKioskLogKey(presCountKey)

      const courseCountRaw = localStorage.getItem(courseCountKey)
      const presCountRaw = localStorage.getItem(presCountKey)

      // ✅ Migra contador si el de presentación aún no existe
      if ((presCountRaw == null || presCountRaw === "") && (courseCountRaw != null && courseCountRaw !== "")) {
        localStorage.setItem(presCountKey, courseCountRaw)
        localStorage.removeItem(courseCountKey)
      }

      // ✅ Migra log si el de presentación aún no existe
      const courseLogRaw = localStorage.getItem(courseLogKey)
      const presLogRaw = localStorage.getItem(presLogKey)

      if ((presLogRaw == null || presLogRaw === "") && (courseLogRaw != null && courseLogRaw !== "")) {
        localStorage.setItem(presLogKey, courseLogRaw)
        localStorage.removeItem(courseLogKey)
      }
    } catch { }
  }, [hasServerPresentation, presentacion?.presentacion_id, codigoCurso])


  useEffect(() => { doSubmitRef.current = doSubmit }, [doSubmit])

  // --------------------- Si el docente deshabilita en medio del examen -> finalizar ---------------------
  const disabledKickRef = useRef(false)

  useEffect(() => {
    if (qaMode) return
    if (!needsTeacherEnable) return

    const inExam = accesoConcedido && examen && !waitingAfterTimeout && !enviado && !resultado

    if (!inExam) {
      disabledKickRef.current = false
      return
    }

    if (mySchedule?.habilitado !== true) {
      if (disabledKickRef.current) return
      disabledKickRef.current = true

      setError("El docente deshabilitó tu examen. Se finalizará automáticamente con 0.")
      queueMicrotask(() => {
        doSubmitRef.current?.(true, "docente", { forceZero: true })
      })
    }
  }, [
    qaMode,
    needsTeacherEnable,
    accesoConcedido,
    examen,
    waitingAfterTimeout,
    enviado,
    resultado,
    mySchedule?.habilitado,
  ])


  const handleSubmit = useCallback(
    (auto = false) => {
      if (auto) return void doSubmit(true)
      askConfirm(
        "Enviar examen",
        "¿Estás seguro de que deseas enviar ahora tus respuestas? Luego no podrás modificarlas.",
        () => { closeConfirm(); doSubmit(false) },
      )
    },
    [doSubmit, askConfirm, closeConfirm],
  )

  useEffect(() => {
    const inExam = accesoConcedido && examen && !enviado && !resultado
    if (!inExam) {
      kiosk.stop().catch(() => { })
    }
  }, [accesoConcedido, examen, enviado, resultado, kiosk, enableKiosk])

  // --------------------- Cargar curso + horario ---------------------
  useEffect(() => {
    let alive = true
    const ctrl = new AbortController()
      ; (async () => {
        try {
          setLoading(true); setError("")
          const c = await fnsRef.current.resolveStudentCourseByCode?.(codigoCurso, { signal: ctrl.signal })
          if (!alive) return
          if (!c?.id) { setError("Curso no encontrado o no estás inscrito."); setCourse(null); return }
          setCourse(c)
          const mineUpcoming = await fnsRef.current.fetchMyExamBookings?.({
            curso_id: Number(c.id), upcoming: true, user_id: user?.id, signal: ctrl.signal,
          })
          let list = Array.isArray(mineUpcoming?.bookings) ? mineUpcoming.bookings : mineUpcoming?.items || []
          let chosen = list.find((b) => !isPresentationCompleted(b)) || list.find(isPresentationCompleted) || list[0] || null
          if (!chosen) {
            const mineAll = await fnsRef.current.fetchMyExamBookings?.({ curso_id: Number(c.id), upcoming: false, user_id: user?.id, signal: ctrl.signal })
            const all = Array.isArray(mineAll?.bookings) ? mineAll.bookings : mineAll?.items || []
            chosen = all.find(isPresentationCompleted) || all[0] || null
          }
          if (!chosen) { setError("No tienes un horario agendado para este curso."); setMySchedule(null); return }
          setMySchedule(bookingToMySchedule(chosen))
        } catch (e) {
          if (!alive) return
          setError(e?.message || "No se pudo cargar tu horario.")
          setMySchedule(null)
        } finally { if (alive) setLoading(false) }
      })()
    return () => { alive = false; ctrl.abort() }
  }, [codigoCurso, user?.id])

  useEffect(() => {
    try {
      sessionStorage.removeItem(`exam:start-intent:${codigoCurso}`)
    } catch { }
  }, [codigoCurso])

  // --------------------- REFRESH "habilitado" en tiempo real (habilitar y deshabilitar) ---------------------
  const enablePollRef = useRef(null)

  useEffect(() => {
    // ✅ Aplica a presencial y virtual (excepto QA)
    if (qaMode) return
    if (!course?.id || !mySchedule?.horario_id) return
    if (mySchedule?.presentacion?.completada) return
    if (enviado || resultado) return

    let alive = true
    let inFlight = false
    let lastSeen = null

    const pollOnce = async () => {
      if (!alive || inFlight) return

      if (
        accessChecking ||
        validateCodeInFlightRef.current ||
        codeLockedRef.current ||
        codeLocked ||
        Date.now() < validateCodeQuietUntilRef.current
      ) {
        return
      }

      inFlight = true
      const ctrl = new AbortController()


      try {
        const mineUpcoming = await fnsRef.current.fetchMyExamBookings?.({
          curso_id: Number(course.id),
          upcoming: true,
          user_id: user?.id,
          limit: 5,
          signal: ctrl.signal,
        })

        const list1 = Array.isArray(mineUpcoming?.bookings)
          ? mineUpcoming.bookings
          : (mineUpcoming?.items || [])

        const merged1 = Array.isArray(list1) ? list1 : []

        let booking =
          merged1.find((b) => Number(b?.horario_examen_id ?? b?.horario_id) === Number(mySchedule.horario_id)) ||
          null

        if (!booking) {
          const mineAll = await fnsRef.current.fetchMyExamBookings?.({
            curso_id: Number(course.id),
            upcoming: false,
            user_id: user?.id,
            limit: 5,
            signal: ctrl.signal,
          })

          const list2 = Array.isArray(mineAll?.bookings)
            ? mineAll.bookings
            : (mineAll?.items || [])

          const merged2 = Array.isArray(list2) ? list2 : []

          booking =
            merged2.find((b) => Number(b?.horario_examen_id ?? b?.horario_id) === Number(mySchedule.horario_id)) ||
            null
        }

        if (!booking) return

        const nextSchedule = bookingToMySchedule(booking)

        const nextKey = [
          nextSchedule?.horario_id,
          nextSchedule?.habilitado,
          nextSchedule?.presentacion?.id,
          nextSchedule?.presentacion?.estado,
          nextSchedule?.presentacion?.completada,
        ].join("|")

        if (nextKey !== lastSeen) {
          lastSeen = nextKey

          setMySchedule((prev) => {
            if (!prev) return nextSchedule

            return {
              ...prev,
              ...nextSchedule,
              presentacion: {
                ...(prev.presentacion || {}),
                ...(nextSchedule.presentacion || {}),
              },
            }
          })
        }
      } catch {
        // silencioso
      } finally {
        inFlight = false
      }
    }

    pollOnce()

    const ms = accesoConcedido ? 2500 : 4000
    enablePollRef.current = setInterval(pollOnce, ms)

    return () => {
      alive = false

      try {
        if (enablePollRef.current) clearInterval(enablePollRef.current)
      } catch { }

      enablePollRef.current = null
    }
  }, [
    qaMode,
    course?.id,
    user?.id,
    mySchedule?.horario_id,
    mySchedule?.presentacion?.completada,
    accesoConcedido,
    enviado,
    resultado,
    accessChecking,
    codeLocked,
  ])

  // --------------------- Examen = Horario ---------------------
  useEffect(() => {
    const scheduleId = Number(mySchedule?.horario_id)
    if (!Number.isFinite(scheduleId) || scheduleId <= 0) {
      setExamId(null)
      return
    }
    setExamId(scheduleId)     // ✅ examId ahora ES horario_examen_id
    setError("")
  }, [mySchedule?.horario_id])

  useEffect(() => {
    const horarioId = Number(mySchedule?.horario_id || 0);
    if (!horarioId) return;

    // reset visual y de ejecución al cambiar de horario
    setResumeOffer(null);
    setPresentacion(null);
    setAccesoConcedido(false);
    setExamen(null);
    setAnswers({});
    setCurrentIndex(0);
    setEnviado(false);
    setResultado(null);
    setWaitingAfterTimeout(false);
    setError("");

    try { stopProctoring(); } catch { }

    // limpia residuos genéricos viejos del curso
    try {
      localStorage.removeItem(`exam:kiosk:curso:${codigoCurso}`);
      localStorage.removeItem(`exam:kiosk:curso:${codigoCurso}:log`);
      localStorage.removeItem(`exam_proctor_metrics:curso:${codigoCurso}`);
    } catch { }
  }, [mySchedule?.horario_id, codigoCurso, stopProctoring]);

  // --------------------- Intentar reanudar ---------------------
  useEffect(() => {
    let alive = true
      ; (async () => {
        if (!course?.id || !mySchedule?.horario_id || !examId) return
        try {
          setError("")
          const running = await resumeRunningExamAttempt(Number(examId), { horario_id: Number(mySchedule.horario_id) })
          if (!alive) return
          if (running && running.presentacion_id) {
            const isFinishedServer =
              String(running.estado || "").toLowerCase() === "completado" ||
              String(running.estado || "").toLowerCase() === "abandonado" ||
              running.finished === true
            const noTimeLeft = Number(running.remaining_seconds || 0) <= 0
            const finishedLocal = !!localStorage.getItem(`exam_final:${running.presentacion_id}`)

            if (noTimeLeft && !isFinishedServer && !finishedLocal) {
              setWaitingAfterTimeout(true)
              setPresentacion(running); setResumeOffer(null)
              try {
                await finishExam?.(running.presentacion_id, { answers: {}, currentIndex: 0, motivo: "timeout" })
                localStorage.setItem(`exam_final:${running.presentacion_id}`, "1")
              } catch { }
              return
            }
            if (!isFinishedServer && !noTimeLeft && !finishedLocal) {
              setResumeOffer(running)
            } else {
              setResumeOffer(null)
            }
          }
        } catch (e) {
          if (!(e?.code === "NO_RUNNING" || e?.status === 404)) {
            setError(e?.message || "No se pudo verificar si tenías un intento en curso.")
          }
        }
      })()
    return () => { alive = false }
  }, [course?.id, mySchedule?.horario_id, examId, resumeRunningExamAttempt, finishExam])

  // --------------------- Timer (pausa si bloqueado) ---------------------
  // --------------------- Timer (pausa si bloqueado) ---------------------
  const inExamRef = useRef(false)
  useEffect(() => {
    inExamRef.current = !!(accesoConcedido && examen && !waitingAfterTimeout && !enviado && !resultado)
  }, [accesoConcedido, examen, waitingAfterTimeout, enviado, resultado])

  useEffect(() => {
    // Solo corre cuando realmente estás “dentro” del examen
    if (!accesoConcedido || !examen || waitingAfterTimeout || enviado || resultado) {
      try { if (timerRef.current) clearInterval(timerRef.current) } catch { }
      timerRef.current = null
      return
    }

    // asegura que no queden intervalos previos
    try { if (timerRef.current) clearInterval(timerRef.current) } catch { }
    timerRef.current = null

    timerRef.current = setInterval(() => {
      // si ya no estás en examen, corta
      if (!inExamRef.current) return

      // pausa si está bloqueado (proctor o kiosk)
      const paused =
        (requireProctor && !!proctorRef.current?.blocked) ||
        (enableKiosk && !!kiosk?.blocked)

      if (paused) return

      setTimeLeft((t) => {
        if (t <= 0) return 0
        if (t === 1) {
          // último tick -> timeout
          queueMicrotask(() => {
            // usa ref para evitar closure vieja
            doSubmitRef.current?.(true, "timeout")
          })
          return 0
        }
        return t - 1
      })
    }, 1000)

    return () => {
      try { if (timerRef.current) clearInterval(timerRef.current) } catch { }
      timerRef.current = null
    }
  }, [
    accesoConcedido,
    examen,
    waitingAfterTimeout,
    enviado,
    resultado,
    requireProctor,
    enableKiosk,
    kiosk?.blocked, // si quieres que pause/reanude “en vivo” (solo afecta pausa, no reinicia el contador)
  ])


  const buildRandomSnapshotPlan = useCallback(({ total, required }) => {
    const totalSec = Number(total || 0)
    const requiredN = Number(required || 0)

    if (totalSec <= 0 || requiredN <= 0) {
      return []
    }

    const totalMs = Math.max(1, totalSec * 1000)

    // No tomar foto instantánea al iniciar, pero tampoco esperar demasiado.
    const startMs = Math.min(
      CAMERA_HEALTH_LIMITS.firstRequiredSnapshotMaxMs,
      Math.max(
        CAMERA_HEALTH_LIMITS.firstRequiredSnapshotMinMs,
        Math.floor(totalMs * 0.06)
      )
    )

    // Evita dejar todas las fotos para el último segundo.
    const endSafeMarginMs = Math.min(
      20000,
      Math.max(5000, Math.floor(totalMs * 0.08))
    )

    const endMs = Math.max(startMs + 1000, totalMs - endSafeMarginMs)

    if (requiredN === 1) {
      const min = startMs
      const max = Math.max(startMs + 1000, endMs)
      return [
        Math.floor(min + Math.random() * Math.max(1, max - min)),
      ]
    }

    const usableMs = Math.max(1, endMs - startMs)
    const segmentMs = usableMs / requiredN

    const times = Array.from({ length: requiredN }, (_, index) => {
      const segStart = startMs + segmentMs * index
      const segEnd = index === requiredN - 1
        ? endMs
        : startMs + segmentMs * (index + 1)

      const randomAt = segStart + Math.random() * Math.max(1, segEnd - segStart)

      return clampNumber(
        Math.floor(randomAt),
        startMs,
        endMs
      )
    })

    return times.sort((a, b) => a - b)
  }, [])

  const getRequiredSnapshotTargetCount = useCallback(({
    total,
    left,
    required,
  }) => {
    const totalSec = Number(total || 0)
    const leftSec = Number(left || 0)
    const requiredN = Number(required || 0)

    if (totalSec <= 0 || leftSec <= 0 || requiredN <= 0) return 0

    const planKey = `${snapshotStateKey}:random-plan:v1`
    const elapsedMs = Math.max(0, (totalSec - leftSec) * 1000)

    let plan = snapshotPlanRef.current

    const planValid =
      plan?.key === planKey &&
      Number(plan?.totalSec || 0) === totalSec &&
      Number(plan?.required || 0) === requiredN &&
      Array.isArray(plan?.timesMs) &&
      plan.timesMs.length === requiredN

    if (!planValid) {
      const cached = safeParse(localStorage.getItem(planKey), null)

      const cachedValid =
        cached &&
        Number(cached.totalSec || 0) === totalSec &&
        Number(cached.required || 0) === requiredN &&
        Array.isArray(cached.timesMs) &&
        cached.timesMs.length === requiredN

      const timesMs = cachedValid
        ? cached.timesMs
        : buildRandomSnapshotPlan({
          total: totalSec,
          required: requiredN,
        })

      plan = {
        key: planKey,
        totalSec,
        required: requiredN,
        timesMs,
      }

      snapshotPlanRef.current = plan

      try {
        localStorage.setItem(planKey, JSON.stringify(plan))
      } catch { }
    }

    return clampNumber(
      plan.timesMs.filter((ms) => elapsedMs >= Number(ms || 0)).length,
      0,
      requiredN
    )
  }, [
    snapshotStateKey,
    buildRandomSnapshotPlan,
  ])

  useEffect(() => {
    const inExam =
      accesoConcedido &&
      examen &&
      requireProctor &&
      !waitingAfterTimeout &&
      !enviado &&
      !resultado

    if (!inExam) {
      if (snapshotTimerRef.current) {
        clearInterval(snapshotTimerRef.current)
        snapshotTimerRef.current = null
      }
      return
    }

    if (requiredSnapshotCount <= 0) {
      if (snapshotTimerRef.current) {
        clearInterval(snapshotTimerRef.current)
        snapshotTimerRef.current = null
      }
      return
    }

    readSnapshotState()

    let sending = false

    const tick = async () => {
      if (sending) return

      const total = Number(totalSecondsRef.current || totalSeconds || 0)
      const left = Number(timeLeftRef.current || timeLeft || 0)

      if (total <= 0 || left <= 0) return

      const targetCount = getRequiredSnapshotTargetCount({
        total,
        left,
        required: requiredSnapshotCount,
      })

      const currentCount = Number(snapshotStateRef.current.count || 0)

      if (currentCount >= requiredSnapshotCount) return
      if (currentCount >= targetCount) return

      sending = true

      try {
        const nextNo = currentCount + 1

        await sendSnapshot({
          tag: `required_random_${nextNo}`,
          force: true,
          countsRequired: true,
        })
      } finally {
        sending = false
      }
    }

    tick()

    snapshotTimerRef.current = setInterval(tick, 1000)

    return () => {
      if (snapshotTimerRef.current) {
        clearInterval(snapshotTimerRef.current)
        snapshotTimerRef.current = null
      }
    }
  }, [
    accesoConcedido,
    examen,
    requireProctor,
    requiredSnapshotCount,
    enviado,
    resultado,
    waitingAfterTimeout,
    totalSeconds,
    timeLeft,
    sendSnapshot,
    readSnapshotState,
    getRequiredSnapshotTargetCount,
  ])

  // --------------------- Guardado diferido ---------------------
  const queueSave = () => {
    if (!accesoConcedido || enviado || resultado || timeLeft <= 0) return
    if (requireProctor && proctorRef.current.blocked) return
    if (saveTimer.current) clearTimeout(saveTimer.current)
    saveTimer.current = setTimeout(() => flushSave(false), 600)
  }
  useEffect(() => {
    if (!accesoConcedido || enviado || resultado) return
    queueSave()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentIndex])

  const setAnswer = (preguntaId, value) => {
    if (enviado || resultado) return
    if (requireProctor && proctorRef.current.blocked) return
    setAnswers((prev) => { const next = { ...prev, [preguntaId]: value }; queueSave(); return next })
  }

  const currentHasAnswer = useMemo(() => {
    const q = examen?.preguntas?.[currentIndex]
    if (!q) return true
    const v = answers?.[q.id]
    return sanitizeForSchema(q, v) !== null
  }, [examen, currentIndex, answers])

  // --------------------- Navegación ---------------------
  const goTo = (idx) => {

    grantExamUiGrace(1200)

    if (!examen || enviado || resultado) return
    if (interactionBlocked) return
    if (requireProctor && proctorRef.current.blocked) return


    const preguntas = Array.isArray(examen?.preguntas) ? examen.preguntas : []
    const maxIdx = Math.max(0, preguntas.length - 1)
    const clamped = Math.min(Math.max(0, Number(idx)), maxIdx)

    if (clamped === currentIndex) return

    // Navegación libre:
    // permite anterior, siguiente y saltar a cualquier pregunta.
    if (permitirNavegacion) {
      setCurrentIndex(clamped)
      return
    }

    // Navegación secuencial:
    // no permite volver atrás ni saltar.
    if (clamped !== currentIndex + 1) {
      return
    }

    // Para avanzar, debe responder la pregunta actual.
    const currentQuestion = preguntas[currentIndex]
    if (!currentQuestion) return

    const currentValue = answers?.[currentQuestion.id]
    const hasAnswer = sanitizeForSchema(currentQuestion, currentValue) !== null

    if (!hasAnswer) {
      setError("Debes responder la pregunta actual antes de continuar.")
      return
    }

    setError("")
    setCurrentIndex(clamped)
  }


  // --------------------- Cerrar pestaña -> save rápido ---------------------
  useEffect(() => {
    const onUnload = () => {
      if (!presentacion?.presentacion_id || enviado || resultado) return
      isUnloadingRef.current = true
      try { kiosk.stop(false) } catch { }

      try {
        const { draft, index } = buildDraftAndPayload()
        finalizeMinuteIfPartial()
        pushKioskLog(kioskLogKey, "beforeunload")
        localStorage.setItem(
          `exam_prog:${presentacion.presentacion_id}`,
          JSON.stringify({ draft, index, ts: Date.now(), pending: true })
        )
      } catch { }
    }

    window.addEventListener("beforeunload", onUnload)
    return () => window.removeEventListener("beforeunload", onUnload)
  }, [presentacion?.presentacion_id, enviado, resultado, buildDraftAndPayload, kiosk])

  // --------------------- Reanudar ---------------------
  const handleAcceptResume = async () => {
    if (!resumeOffer?.presentacion_id) return
    try {
      setLoading(true); setError("")
      if (localStorage.getItem(`exam_final:${resumeOffer.presentacion_id}`)) { setResumeOffer(null); setLoading(false); return }

      const limitN = Number(mySchedule?.numero_preguntas || 0) || undefined;

      const data = await getExamWithQuestions(Number(examId), {
        shuffle: true,
        limit: limitN,
        strictTime: false,
        qa: qaMode,
        presentacionId: Number(resumeOffer.presentacion_id),
      });

      if (!data?.exam || !data?.questions) throw new Error("No se pudieron cargar las preguntas del examen")
      const rawQs = data.questions || []
      const uniqQs = dedupeByCanonicalKey(rawQs)
      servedQuestionIdsRef.current = (uniqQs || []).map(q => String(q.canonical || q.id)).filter(Boolean)
      const examMerged = { ...(data.exam || {}), preguntas: uniqQs }
      setExamen(examMerged)

      const progress = resumeOffer.progress || {}
      let restoredDraft = progress.draft || {}
      let restoredIndex = Number.isInteger(progress.index) ? progress.index : 0
      try {
        const local = JSON.parse(localStorage.getItem(`exam_prog:${resumeOffer.presentacion_id}`) || "null")
        if (local && (!progress.ts || (local.ts && local.ts > progress.ts))) {
          if (local.draft) restoredDraft = local.draft
          if (Number.isInteger(local.index)) restoredIndex = local.index
        } else if (progress.answers) {
          restoredDraft = Object.fromEntries(Object.entries(progress.answers).map(([k, v]) => [String(k), v]))
        }
      } catch { }
      try {
        const allowedIds = new Set(uniqQs.map((q) => String(q?.id ?? q?.pregunta_id ?? q?.source_question_id ?? q?.question_id)))
        const prunedDraft = Object.fromEntries(Object.entries(restoredDraft || {}).filter(([k]) => allowedIds.has(String(k))))
        setAnswers(prunedDraft)
      } catch { setAnswers(restoredDraft) }
      const safeIndex = Math.min(Math.max(0, restoredIndex || 0), Math.max(0, uniqQs.length - 1))
      setCurrentIndex(safeIndex)

      setPresentacion(resumeOffer)
      setEnviado(false)
      setResultado(null)

      const rem = Math.max(0, Number(resumeOffer.remaining_seconds ?? 0))
      const tot = Number(resumeOffer.total_seconds ?? rem)
      setTimeLeft(rem)
      setTotalSeconds(tot > 0 ? tot : rem)

      if (enableKiosk) {
        try { await kiosk.requestFullscreen() } catch { }
        try { await kiosk.start() } catch { }
      }
      setAccesoConcedido(true)
      setResumeOffer(null)

      if (requireProctor) {
        stopProctoring()
        const ok = await startProctoring()

        const proctorMsg = isVirtualExam
          ? "Debes habilitar cámara y micrófono para continuar el examen virtual."
          : "Este examen presencial requiere cámara y micrófono activos."

        if (!ok) {
          setError(proctorRef.current?.reason || proctorMsg)
        }
      }
    } catch (e) {
      setError(e?.message || "No se pudo reanudar el examen.")
    } finally { setLoading(false) }
  }

  const handleDiscardResume = () => {
    if (resumeOffer?.presentacion_id) { localStorage.removeItem(`exam_prog:${resumeOffer.presentacion_id}`) }
    setResumeOffer(null)
  }

  // --------------------- Ventana válida ---------------------



  const startDisabled = useMemo(() => (
    loading ||
    !mySchedule?.horario_id ||
    !examId ||
    mySchedule?.presentacion?.completada === true ||
    (!qaMode && !isWithinSchedule)
  ), [
    loading,
    mySchedule?.horario_id,
    examId,
    mySchedule?.presentacion?.completada,
    qaMode,
    isWithinSchedule,
  ])

  // Chrome (header/footer)
  useEffect(() => {
    const shouldHideChrome = accesoConcedido && examen && !waitingAfterTimeout && !enviado && !resultado
    if (shouldHideChrome) { setHeaderVisible?.(false); setFooterVisible?.(false) }
    else { setHeaderVisible?.(true); setFooterVisible?.(true) }
    return () => { setHeaderVisible?.(true); setFooterVisible?.(true) }
  }, [accesoConcedido, examen, waitingAfterTimeout, enviado, resultado, setHeaderVisible, setFooterVisible])

  useEffect(() => {
    const shouldFocus = accesoConcedido && examen && !waitingAfterTimeout && !enviado && !resultado
    document.body.classList.toggle("exam-focus", !!shouldFocus)

    const prevent = (e) => { try { e.preventDefault(); e.stopPropagation(); } catch { } }
    if (shouldFocus) {
      document.addEventListener("contextmenu", prevent, true)
      document.addEventListener("copy", prevent, true)
      document.addEventListener("cut", prevent, true)
      document.addEventListener("paste", prevent, true)
      document.addEventListener("dragstart", prevent, true)
      document.addEventListener("selectstart", prevent, true)
    }
    return () => {
      document.body.classList.remove("exam-focus")
      document.removeEventListener("contextmenu", prevent, true)
      document.removeEventListener("copy", prevent, true)
      document.removeEventListener("cut", prevent, true)
      document.removeEventListener("paste", prevent, true)
      document.removeEventListener("dragstart", prevent, true)
      document.removeEventListener("selectstart", prevent, true)
    }
  }, [accesoConcedido, examen, waitingAfterTimeout, enviado, resultado])

  useEffect(() => {
    const shouldForceFullscreen =
      accesoConcedido &&
      examen &&
      !waitingAfterTimeout &&
      !enviado &&
      !resultado &&
      enableKiosk

    if (!shouldForceFullscreen) return

    let cancelled = false

    const enforceFullscreen = async () => {
      if (document.fullscreenElement) return

      try {
        await requestFullscreenSafe()
      } catch { }

      if (cancelled) return

      try {
        await kiosk.requestFullscreen?.()
      } catch { }
    }

    const t = setTimeout(enforceFullscreen, 120)

    return () => {
      cancelled = true
      clearTimeout(t)
    }
  }, [
    accesoConcedido,
    examen,
    waitingAfterTimeout,
    enviado,
    resultado,
    enableKiosk,
    requestFullscreenSafe,
    kiosk,
  ])

  useEffect(() => {
    if (!(accesoConcedido && examen) || !requireProctor || enviado || resultado) { stopProctoring(); return }
    ; (async () => { if (!streamRef.current) { await startProctoring() } })()
    return () => { stopProctoring() }
  }, [accesoConcedido, examen, requireProctor, enviado, resultado, startProctoring, stopProctoring])

  // --------------------- UI helpers ---------------------
  const totalQuestions = examen?.preguntas?.length || 0
  const answeredCount = useMemo(() => {
    if (!examen) return 0
    return (examen.preguntas || []).reduce((acc, q) => {
      const v = answers[q.id]
      if (v == null) return acc
      if (Array.isArray(v)) return acc + (v.length > 0 ? 1 : 0)
      return acc + (String(v).trim() ? 1 : 0)
    }, 0)
  }, [examen, answers])

  const answeredPct = totalQuestions > 0 ? Math.max(0, Math.min(100, Math.round((answeredCount / totalQuestions) * 100))) : 0

  const timePct = useMemo(() => {
    const tot = Number(totalSeconds || 0)
    const left = Number(timeLeft || 0)
    if (tot <= 0) return 0
    const used = Math.min(tot, Math.max(0, tot - left))
    return Math.max(0, Math.min(100, Math.round((used / tot) * 100)))
  }, [totalSeconds, timeLeft])



  const minutosSegundos = useMemo(() => {
    const m = String(Math.floor(timeLeft / 60)).padStart(2, "0")
    const s = String(timeLeft % 60).padStart(2, "0")
    return `${m}:${s}`
  }, [timeLeft])

  const proctorPill = (requireProctor && accesoConcedido && examen && !enviado && !resultado) ? (
    <span className={`ex-pill ${proctor.blocked ? "ex-pill-bad" : "ex-pill-ok"}`} title={proctor.reason || (proctor.blocked ? "Cámara/Mic desactivados" : "Cámara y mic activos")}>
      {proctor.blocked ? "Proctoring: BLOQUEADO" : "Proctoring: OK"}
    </span>
  ) : null

  const activeCamLabel = useMemo(() => {
    const activeId = (() => {
      try { return videoTrackRef.current?.getSettings?.()?.deviceId || null; } catch { return null; }
    })();
    return (deviceOpts.cams || []).find(d => d.deviceId === activeId)?.label || "Sin cámara detectada";
  }, [deviceOpts.cams, proctor.cam, proctor.blocked]);

  const activeMicLabel = useMemo(() => {
    const activeId = (() => {
      try { return audioTrackRef.current?.getSettings?.()?.deviceId || null; } catch { return null; }
    })();
    return (deviceOpts.mics || []).find(d => d.deviceId === activeId)?.label || "Sin micrófono detectado";
  }, [deviceOpts.mics, proctor.mic, proctor.blocked]);

  const showLoading = loading && !accesoConcedido && !resumeOffer && !waitingAfterTimeout
  const warnReason = kiosk.lastViolationReason || kiosk.reason

  const cameraWarningText = useMemo(() => {
    if (cameraRuntimeAlert?.message) return cameraRuntimeAlert.message

    if (cameraHealth?.dark) {
      return "La cámara se ve oscura. Mejora la iluminación frontal."
    }

    if (cameraHealth?.noMotion) {
      return "No se detecta movimiento. Muévete ligeramente para mantener activa la verificación."
    }

    if (cameraHealth?.overBright) {
      return "La cámara está sobreexpuesta. Evita luz directa o una ventana detrás/frente a ti."
    }

    if (cameraHealth?.lowContrast) {
      return "La imagen no se ve clara. Mejora la luz o limpia la cámara."
    }

    return ""
  }, [
    cameraRuntimeAlert,
    cameraHealth?.dark,
    cameraHealth?.noMotion,
    cameraHealth?.overBright,
    cameraHealth?.lowContrast,
  ])

  const microphoneWarningText = useMemo(() => {
    if (warnReason === "microphone_noise") {
      return "Se detectó ruido elevado o constante en el micrófono. Reduce el ruido del entorno para evitar penalizaciones."
    }

    return ""
  }, [warnReason])

  const runtimeWarningText = cameraWarningText || microphoneWarningText

  const css = `
    :root{
      --ex-bg:#f6f7fb;
      --ex-card:#ffffff;
      --ex-border:#e5e7eb;
      --ex-text:#111827;
      --ex-muted:#6b7280;
      --ex-primary:#2563eb;
      --ex-success:#16a34a;
      --ex-danger:#dc2626;
      --ex-warn:#f59e0b;
      --ex-shadow: 0 10px 30px rgba(0,0,0,.08);
      --ex-radius: 16px;
      --ex-radius-sm: 12px;
    }

    .ex-page{
      min-height: calc(100vh - 80px);
      background: var(--ex-bg);
      color: var(--ex-text);
    }
    .ex-shell{
      max-width: 1120px;
      margin: 0 auto;
      padding: 18px 12px 40px;
    }

    .ex-top{
      display:flex;
      align-items:flex-start;
      justify-content:space-between;
      gap: 12px;
      padding: 16px;
      border-radius: var(--ex-radius);
      border: 1px solid var(--ex-border);
      background: linear-gradient(135deg, rgba(37,99,235,.10), rgba(16,185,129,.10));
      box-shadow: 0 6px 18px rgba(0,0,0,.05);
      margin-bottom: 14px;
    }
    .ex-title{
      margin:0;
      font-size: clamp(18px, 2.4vw, 22px);
      display:flex;
      align-items:center;
      gap:10px;
      font-weight: 900;
      letter-spacing: -.2px;
    }
    .ex-sub{
      margin: 6px 0 0;
      color: var(--ex-muted);
      font-size: 14px;
      line-height: 1.4;
    }

    .ex-pill{
      margin-left: 6px;
      display:inline-flex;
      align-items:center;
      justify-content:center;
      padding: 6px 10px;
      border-radius: 999px;
      font-weight: 900;
      font-size: 12px;
      border: 1px solid var(--ex-border);
      background: #fff;
      white-space: nowrap;
    }
    .ex-pill-ok{ background:#dcfce7; border-color:#a7f3d0; color:#065f46; }
    .ex-pill-bad{ background:#fee2e2; border-color:#fecaca; color:#991b1b; }

    .ex-card{
      background: var(--ex-card);
      border: 1px solid var(--ex-border);
      border-radius: var(--ex-radius);
      box-shadow: 0 8px 22px rgba(0,0,0,.05);
      padding: 16px;
    }
    .ex-card + .ex-card{ margin-top: 12px; }

    .ex-row{ display:flex; gap: 10px; align-items:center; flex-wrap: wrap; }
    .ex-row-between{ display:flex; gap: 10px; align-items:center; justify-content:space-between; flex-wrap: wrap; }
    .ex-muted{ color: var(--ex-muted); }
    .ex-strong{ font-weight: 900; }

    .ex-input{
      width: 100%;
      min-width: 180px;
      padding: 12px 12px;
      border-radius: 12px;
      border: 1px solid var(--ex-border);
      background: #fff;
      outline: none;
      font-size: 14px;
    }
    .ex-input:focus{ border-color: rgba(37,99,235,.6); box-shadow: 0 0 0 4px rgba(37,99,235,.12); }

    .ex-btn{
      appearance: none;
      border: 1px solid var(--ex-border);
      background: #fff;
      color: var(--ex-text);
      font-weight: 900;
      padding: 10px 14px;
      border-radius: 12px;
      cursor: pointer;
      display:inline-flex;
      align-items:center;
      justify-content:center;
      gap: 8px;
      transition: transform .06s ease, box-shadow .12s ease, background .12s ease, border-color .12s ease;
      user-select: none;
    }
    .ex-btn:hover{ box-shadow: 0 8px 18px rgba(0,0,0,.08); }
    .ex-btn:active{ transform: translateY(1px); }
    .ex-btn:disabled{ opacity: .55; cursor: not-allowed; box-shadow: none; }
    .ex-btn-primary{ background: var(--ex-primary); border-color: rgba(37,99,235,.35); color: #fff; }
    .ex-btn-success{ background: var(--ex-success); border-color: rgba(22,163,74,.35); color: #fff; }
    .ex-btn-danger{ background: #fff1f2; border-color: #fecaca; color: #991b1b; }
    .ex-btn-ghost{ background: #fff; }

    .ex-banner{
      padding: 12px 14px;
      border-radius: 14px;
      border: 1px solid var(--ex-border);
      background: #fff;
    }
    .ex-banner-info{ background: #ecfeff; border-color: #a5f3fc; }
    .ex-banner-warn{ background: #fff7ed; border-color: #fdba74; color: #9a3412; }
    .ex-banner-ok{ background: #ecfdf5; border-color: #a7f3d0; color: #065f46; }
    .ex-banner-bad{ background: #fff1f2; border-color: #fecaca; color: #991b1b; }

    /* Layout examen */
    .ex-layout{
      display:grid;
      grid-template-columns: 1fr 320px;
      gap: 14px;
      position: relative;
      align-items:start;
    }
    .ex-main{ min-width: 0; }
    .ex-aside{
      position: sticky;
      top: 12px;
      align-self:start;
      height: fit-content;
    }

    /* Status bar */
    .ex-status{
      border-radius: 14px;
      border: 1px solid var(--ex-border);
      background: rgba(255,255,255,.9);
      backdrop-filter: blur(6px);
      padding: 12px;
      box-shadow: 0 10px 25px rgba(0,0,0,.06);
    }
    .ex-timebox{
      display:flex;
      align-items:center;
      gap: 10px;
      flex-wrap: wrap;
    }
    .ex-timer{
      font-variant-numeric: tabular-nums;
      font-weight: 900;
      padding: 6px 10px;
      border-radius: 12px;
      border: 1px solid var(--ex-border);
      background: #eef2ff;
    }
    .ex-timer.ex-urgent{ background: #fee2e2; border-color: #fecaca; }
    .ex-progress{
      height: 10px;
      width: 100%;
      background: #e5e7eb;
      border-radius: 999px;
      overflow: hidden;
      border: 1px solid rgba(0,0,0,.03);
    }
    .ex-progress > div{
      height: 100%;
      background: #10b981;
      transition: width .5s ease;
    }
    .ex-meta{
      display:flex;
      justify-content:space-between;
      gap: 10px;
      margin-top: 6px;
      font-size: 12px;
      color: var(--ex-muted);
    }

    .ex-navline{
      display:flex;
      justify-content:space-between;
      align-items:center;
      gap: 10px;
      margin: 10px 0;
      flex-wrap: wrap;
    }
    .ex-qpos{
      font-weight: 900;
      letter-spacing: -.2px;
    }

    .ex-questionCard{
      border-radius: 16px;
      border: 1px solid var(--ex-border);
      background: #fff;
      box-shadow: 0 10px 25px rgba(0,0,0,.05);
      padding: 14px;
    }

    /* Navegación preguntas */
    .ex-navgrid{
      display:grid;
      grid-template-columns: repeat(6, minmax(0, 1fr));
      gap: 8px;
    }
    .ex-qbtn{
      height: 38px;
      border-radius: 12px;
      border: 1px solid var(--ex-border);
      background: #fff;
      font-weight: 900;
      cursor: pointer;
      transition: transform .06s ease, box-shadow .12s ease, background .12s ease;
    }
    .ex-qbtn:hover{ box-shadow: 0 8px 18px rgba(0,0,0,.08); }
    .ex-qbtn:active{ transform: translateY(1px); }
    .ex-qbtn:disabled{ opacity: .6; cursor:not-allowed; box-shadow:none; }
    .ex-qbtn.is-current{ background: #eef2ff; color:#3730a3; border-color: #c7d2fe; }
    .ex-qbtn.is-done{ background: #ecfdf5; color:#065f46; border-color: #a7f3d0; }

    /* Proctor */
    .ex-proctorVideo{
      width: 100%;
      border-radius: 12px;
      background: #000;
      border: 1px solid var(--ex-border);
      aspect-ratio: 4/3;
      object-fit: cover;
    }
    .ex-micbar{
      margin-top: 10px;
      height: 10px;
      background: #e5e7eb;
      border-radius: 999px;
      overflow:hidden;
      border: 1px solid rgba(0,0,0,.03);
    }
    .ex-micbar > div{
      height: 100%;
      background: #2563eb;
      transition: width .2s ease;
    }
    .ex-micmeta{
      margin-top: 6px;
      font-size: 12px;
      color: var(--ex-muted);
      display:flex;
      justify-content:space-between;
      gap: 10px;
    }

    /* Overlays / modals */
    .ex-overlay{
      position:absolute;
      inset:0;
      background: rgba(0,0,0,.52);
      backdrop-filter: blur(2px);
      z-index: 20;
      display:flex;
      align-items:center;
      justify-content:center;
      padding: 12px;
    }
    .ex-modal{
      width: min(560px, 96vw);
      background:#fff;
      border-radius: 16px;
      border: 1px solid var(--ex-border);
      box-shadow: 0 18px 50px rgba(0,0,0,.35);
      padding: 14px;
    }
    .ex-modalTitle{
      font-weight: 950;
      font-size: 18px;
      margin: 0 0 6px;
      letter-spacing: -.2px;
    }
    .ex-modalBody{
      color: #374151;
      line-height: 1.5;
      font-size: 14px;
    }
    .ex-modalList{
      margin: 10px 0 0;
      padding-left: 18px;
      color: #4b5563;
      font-size: 13px;
      line-height: 1.45;
    }

    .ex-fixedOverlay{
      position: fixed;
      inset: 0;
      background: rgba(0,0,0,.40);
      display:flex;
      align-items:center;
      justify-content:center;
      z-index: 50;
      padding: 12px;
    }
    .ex-fixedModal{
      width: min(520px, 95vw);
      border-radius: 16px;
      border: 1px solid var(--ex-border);
      background: #fff;
      padding: 14px;
      box-shadow: 0 18px 50px rgba(0,0,0,.35);
    }

    /* Resultado */
    .ex-resultWrap{ max-width: 980px; margin: 0 auto; padding: 18px 12px 60px; }
    .ex-resultCard{
      background:#fff;
      border:1px solid var(--ex-border);
      border-radius: 18px;
      padding: 22px;
      max-width: 620px;
      margin: 26px auto 14px;
      box-shadow: var(--ex-shadow);
      text-align:center;
    }
    .ex-score{
      font-size: 52px;
      font-weight: 1000;
      letter-spacing: -1px;
      margin: 12px 0 4px;
    }
    .ex-scoreBadge{
      display:inline-block;
      padding: 8px 12px;
      border-radius: 14px;
      background: #eef2ff;
      border: 1px solid #c7d2fe;
      color:#3730a3;
      font-size: 22px;
      font-weight: 950;
    }
    .ex-kpis{
      display:grid;
      grid-template-columns: repeat(5, minmax(0, 1fr));
      gap: 10px;
      margin-top: 12px;
      text-align:left;
    }
    .ex-kpi{
      border:1px solid var(--ex-border);
      border-radius: 14px;
      padding: 10px;
      background: #fff;
    }
    .ex-kpi b{ display:block; font-size: 18px; }
    .ex-kpi span{ color: var(--ex-muted); font-size: 12px; }

    .ex-review{
      max-width: 980px;
      margin: 0 auto;
      background:#fff;
      border:1px solid var(--ex-border);
      border-radius: 16px;
      padding: 14px;
      box-shadow: 0 10px 25px rgba(0,0,0,.04);
    }
    .ex-reviewTop{
      display:flex;
      align-items:center;
      justify-content:space-between;
      gap: 10px;
      flex-wrap: wrap;
      margin-bottom: 10px;
    }
    .ex-qitem{
      border: 1px solid var(--ex-border);
      border-radius: 14px;
      padding: 12px;
      margin: 8px 0;
      background: #fcfcfc;
    }
    .ex-qitem.bad{
      border-color:#fecaca;
      background:#fff7f7;
    }
    .ex-qhead{
      display:flex;
      flex-wrap: wrap;
      gap: 8px;
      align-items:center;
      font-weight: 950;
      margin-bottom: 6px;
      line-height: 1.35;
    }

    .ex-proctor-note{
  margin-top:8px;
  font-size:12px;
  font-weight:800;
  color:#2563eb;
}

.ex-proctor-note-warn {
  margin-top: 8px;
  padding: 8px 10px;
  border-radius: 10px;
  background: rgba(245, 158, 11, 0.12);
  border: 1px solid rgba(245, 158, 11, 0.35);
  color: #b45309;
  font-size: 13px;
  line-height: 1.35;
}
    .ex-tag{
      display:inline-flex;
      align-items:center;
      justify-content:center;
      padding: 4px 10px;
      border-radius: 999px;
      border: 1px solid var(--ex-border);
      font-size: 12px;
      font-weight: 900;
      background: #fff;
      color: #374151;
    }
    .ex-tag.ok{ background:#ecfdf5; border-color:#a7f3d0; color:#065f46; }
    .ex-tag.bad{ background:#fff1f2; border-color:#fecaca; color:#991b1b; }
    .ex-tag.neu{ background:#f3f4f6; border-color:#e5e7eb; color:#374151; }

    /* Mobile */
    @media (max-width: 980px){
      .ex-layout{ grid-template-columns: 1fr; }
      .ex-aside{ position: static; }
      .ex-navgrid{ grid-template-columns: repeat(8, minmax(42px, 1fr)); }
    }
    @media (max-width: 560px){
      .ex-shell{ padding: 14px 10px 34px; }
      .ex-top{ padding: 14px; }
      .ex-kpis{ grid-template-columns: repeat(2, minmax(0, 1fr)); }
      .ex-navgrid{
        grid-template-columns: none;
        display:flex;
        overflow:auto;
        gap: 8px;
        padding-bottom: 4px;
        scrollbar-width: thin;
      }
      .ex-qbtn{ min-width: 44px; }
      .ex-timebox{ gap: 8px; }
    }

    /* Accesibilidad / focus ring */
    .ex-btn:focus, .ex-qbtn:focus, .ex-input:focus{
      outline: none;
    }
    .ex-btn:focus-visible, .ex-qbtn:focus-visible, .ex-input:focus-visible{
      box-shadow: 0 0 0 4px rgba(37,99,235,.18);
      border-color: rgba(37,99,235,.55);
    }

    .ex-secureWaitShell{
  width:100%;
  min-height:calc(100vh - 120px);
  display:flex;
  align-items:center;
  justify-content:center;
  padding:28px;
  background:#f3f4f6;
}

.ex-secureWaitCard{
  width:min(880px, 100%);
  background:#fff;
  border:1px solid #dbe3ef;
  border-radius:14px;
  box-shadow:0 16px 34px rgba(15,23,42,.14);
  overflow:hidden;
}

.ex-secureWaitHead{
  display:flex;
  gap:18px;
  align-items:center;
  padding:32px 28px;
  border-bottom:1px solid #e5e7eb;
}

.ex-secureResumeNotice{
  margin:0 28px;
  margin-top:18px;
  padding:16px 18px;
  border-radius:16px;
  border:1px solid #bbf7d0;
  background:linear-gradient(135deg,#f0fdf4 0%,#ecfeff 100%);
  display:grid;
  gap:10px;
}

.ex-secureResumeTop{
  display:flex;
  align-items:flex-start;
  justify-content:space-between;
  gap:16px;
}

.ex-secureResumeMeta{
  display:flex;
  flex-wrap:wrap;
  gap:10px;
}

.ex-secureResumeChip{
  display:inline-flex;
  align-items:center;
  gap:6px;
  min-height:30px;
  padding:6px 10px;
  border-radius:999px;
  background:#fff;
  border:1px solid #d1fae5;
  color:#065f46;
  font-size:12px;
  font-weight:900;
}

.ex-secureResumeActions{
  display:flex;
  flex-wrap:wrap;
  gap:10px;
}

.ex-secureIcon{
  width:44px;
  height:44px;
  border-radius:12px;
  display:flex;
  align-items:center;
  justify-content:center;
  background:#eff6ff;
  border:1px solid #bfdbfe;
  color:#2563eb;
  font-size:22px;
}

.ex-secureKicker{
  font-size:10px;
  letter-spacing:.18em;
  font-weight:900;
  color:#64748b;
  text-transform:uppercase;
}

.ex-secureWaitHead h1{
  margin:6px 0 0;
  font-size:22px;
  font-weight:950;
  color:#1f2937;
}

.ex-secureWaitBody{
  display:grid;
  grid-template-columns:1fr 1fr;
  gap:28px;
  padding:28px;
}

.ex-stepTitle{
  display:flex;
  align-items:center;
  gap:12px;
  font-size:14px;
  font-weight:950;
  color:#1f2937;
  margin-bottom:18px;
}

.ex-stepTitle span{
  width:22px;
  height:22px;
  border-radius:7px;
  display:inline-flex;
  align-items:center;
  justify-content:center;
  background:#eff6ff;
  border:1px solid #bfdbfe;
  color:#2563eb;
  font-size:12px;
}

.ex-hardwareBox{
  background:#f8fafc;
  border:1px solid #e5e7eb;
  border-radius:16px;
  padding:20px;
  display:grid;
  gap:18px;
}

.ex-hardwareRow{
  display:flex;
  align-items:center;
  justify-content:space-between;
  gap:16px;
  font-size:14px;
  font-weight:800;
  color:#334155;
}

.ex-hardwareRow b{
  font-size:12px;
}

.ex-hardwareRow b.ok{ color:#059669; }
.ex-hardwareRow b.bad{ color:#dc2626; }
.ex-hardwareRow b.muted{ color:#94a3b8; }

.ex-secureSmallBtn{
  height:40px;
  border:none;
  border-radius:12px;
  background:#e0ecff;
  color:#2563eb;
  font-weight:900;
  cursor:pointer;
}

.ex-approvalBox{
  display:flex;
  gap:12px;
  align-items:center;
  border-radius:16px;
  padding:16px;
  margin-bottom:12px;
  border:1px solid #a7f3d0;
  background:#ecfdf5;
}

.ex-approvalBox.bad{
  border-color:#fecaca;
  background:#fef2f2;
}

.ex-approvalIcon{
  width:18px;
  height:18px;
  border-radius:999px;
  display:flex;
  align-items:center;
  justify-content:center;
  border:1px solid currentColor;
  color:#10b981;
  font-size:11px;
  font-weight:950;
}

.ex-approvalBox.bad .ex-approvalIcon{
  color:#ef4444;
}

.ex-approvalBox b{
  font-size:13px;
  color:#1f2937;
}

.ex-approvalBox p{
  margin:4px 0 0;
  font-size:12px;
  color:#64748b;
}

.ex-codeBox{
  border:1px solid #dbe3ef;
  border-radius:14px;
  padding:16px;
}

.ex-codeBox label{
  display:block;
  font-size:13px;
  font-weight:950;
  color:#1f2937;
  margin-bottom:10px;
}

.ex-codeRow{
  display:grid;
  grid-template-columns:1fr auto;
  gap:8px;
}

.ex-codeRow input{
  height:36px;
  border:1px solid #dbe3ef;
  border-radius:12px;
  padding:0 12px;
  outline:none;
  font-weight:700;
}

.ex-codeRow button{
  height:36px;
  border:none;
  border-radius:12px;
  padding:0 18px;
  background:#e8effb;
  color:#2563eb;
  font-weight:950;
  cursor:pointer;
}

.ex-codeRow button:disabled{
  opacity:.55;
  cursor:not-allowed;
}

.ex-codeOk{
  margin-top:8px;
  color:#059669;
  font-size:12px;
  font-weight:900;
}

.ex-secureError{
  margin:0 28px 18px;
  padding:12px 14px;
  border-radius:12px;
  background:#fef2f2;
  color:#b91c1c;
  border:1px solid #fecaca;
  font-size:13px;
  font-weight:800;
}

.ex-secureFooter{
  display:flex;
  justify-content:flex-end;
  padding:28px;
  border-top:1px solid #e5e7eb;
  background:#f8fafc;
}

.ex-secureEnterBtn{
  min-width:238px;
  height:46px;
  border:none;
  border-radius:14px;
  background:#1f2937;
  color:#fff;
  font-weight:950;
  cursor:pointer;
  display:inline-flex;
  align-items:center;
  justify-content:center;
  gap:10px;
}

.ex-secureEnterBtn:disabled{
  background:#dbe3ef;
  color:#94a3b8;
  cursor:not-allowed;
}

.ex-securityDark{
  position:fixed;
  inset:0;
  z-index:9999;
  background:#0f172a;
  color:#e5e7eb;
  display:flex;
  align-items:center;
  justify-content:center;
  padding:24px;
}

.ex-privacyCenter{
  text-align:center;
}

.ex-lockIcon{
  font-size:62px;
  opacity:.45;
  margin-bottom:18px;
}

.ex-privacyCenter h2{
  margin:0;
  color:#8190aa;
  letter-spacing:.22em;
  font-size:15px;
  font-weight:950;
}

.ex-privacyCenter p{
  margin:10px 0 28px;
  color:#6b7d99;
  font-size:12px;
}

.ex-focusLostBox{
  width:min(340px, 90vw);
  border:1px solid rgba(239,68,68,.35);
  background:rgba(127,29,29,.18);
  border-radius:14px;
  padding:22px;
  display:grid;
  gap:12px;
  color:#fca5a5;
}

.ex-focusLostBox strong{
  font-size:44px;
  color:#ef4444;
  letter-spacing:.03em;
}

.ex-strikeModal,
.ex-kickedModal{
  width:min(345px, 92vw);
  position:relative;
  overflow:hidden;
  border-radius:18px;
  padding:34px 28px 28px;
  text-align:center;
  background:#f8fafc;
  color:#1f2937;
  box-shadow:0 24px 70px rgba(0,0,0,.45);
}

.ex-strikeTop,
.ex-kickedTop{
  position:absolute;
  top:0;
  left:0;
  right:0;
  height:7px;
  background:linear-gradient(90deg,#fb923c,#ef4444);
}

.ex-strikeIcon,
.ex-kickedIcon{
  font-size:48px;
  color:#f97316;
  margin-bottom:18px;
}

.ex-strikeModal h2,
.ex-kickedModal h2{
  margin:0 0 10px;
  font-size:22px;
  font-weight:950;
}

.ex-strikeModal p,
.ex-kickedModal p{
  color:#475569;
  font-size:14px;
  line-height:1.55;
  font-weight:700;
}

.ex-strikeBadge{
  display:inline-flex;
  margin-top:10px;
  padding:8px 16px;
  border-radius:10px;
  background:#fff7ed;
  border:1px solid #fed7aa;
  color:#c2410c;
  font-size:12px;
  font-weight:950;
}

.ex-strikeCountdown{
  margin:18px 0;
  font-size:44px;
  font-weight:950;
  color:#ef4444;
}

.ex-strikeModal button,
.ex-kickedModal button{
  width:100%;
  height:44px;
  border:none;
  border-radius:10px;
  background:#1f2937;
  color:#fff;
  font-size:12px;
  font-weight:950;
  cursor:pointer;
}

.ex-kickedModal{
  background:#172235;
  border:1px solid rgba(239,68,68,.35);
  color:#fff;
}

.ex-kickedTop{
  background:#dc2626;
}

.ex-kickedIcon{
  color:#ef4444;
}

.ex-kickedModal p{
  color:#cbd5e1;
}

.ex-kickedModal button{
  background:#dc2626;
}

@media (max-width: 760px){
  .ex-secureWaitBody{
    grid-template-columns:1fr;
  }

  .ex-secureFooter{
    justify-content:stretch;
  }

  .ex-secureEnterBtn{
    width:100%;
  }
}


.ex-devicePermsHint{
  display:grid;
  gap:6px;
  padding:10px 12px;
  border-radius:12px;
  background:#fff;
  border:1px solid #e5e7eb;
  color:#64748b;
  font-size:12px;
  font-weight:800;
}

.ex-devicePermsHint b{
  color:#1f2937;
}

.ex-devicePermsHint .bad{
  color:#b91c1c;
  background:#fef2f2;
  border:1px solid #fecaca;
  border-radius:10px;
  padding:8px 10px;
}

.ex-codeStatusArea{
  margin-top:10px;
  display:grid;
  gap:10px;
}

.ex-codeStatus{
  display:flex;
  gap:10px;
  align-items:flex-start;
  padding:11px 12px;
  border-radius:14px;
  font-size:12px;
  line-height:1.35;
}

.ex-codeStatus span{
  width:20px;
  height:20px;
  border-radius:999px;
  display:flex;
  align-items:center;
  justify-content:center;
  flex:0 0 auto;
  font-size:11px;
  font-weight:950;
  border:1px solid currentColor;
}

.ex-codeStatus b{
  display:block;
  font-size:12.5px;
  font-weight:950;
  margin-bottom:2px;
}

.ex-codeStatus p{
  margin:0;
  font-weight:700;
}

.ex-codeStatus.checking{
  background:#eff6ff;
  border:1px solid #bfdbfe;
  color:#1d4ed8;
}

.ex-codeStatus.ok{
  background:#ecfdf5;
  border:1px solid #a7f3d0;
  color:#047857;
}

.ex-codeStatus.bad{
  background:#fef2f2;
  border:1px solid #fecaca;
  color:#b91c1c;
}

.ex-codeStatus.locked{
  background:#fff7ed;
  border:1px solid #fed7aa;
  color:#c2410c;
}

.ex-codeAttempts{
  padding:11px 12px;
  border-radius:14px;
  background:#f8fafc;
  border:1px solid #e5e7eb;
}

.ex-codeAttemptsTop{
  display:flex;
  justify-content:space-between;
  align-items:center;
  gap:10px;
  color:#64748b;
  font-size:12px;
  font-weight:850;
}

.ex-codeAttemptsTop b{
  color:#1f2937;
  font-weight:950;
}

.ex-codeDots{
  margin-top:9px;
  display:flex;
  gap:7px;
}

.ex-codeDots i{
  width:100%;
  height:7px;
  border-radius:999px;
  display:block;
  background:#e5e7eb;
}

.ex-codeDots i.on{
  background:#2563eb;
}

.ex-codeDots i.off{
  background:#fecaca;
}


.ex-securityOverlay{
  position:fixed;
  inset:0;
  z-index:99999;
  display:flex;
  align-items:center;
  justify-content:center;
  padding:24px;
  background:#0f172a;
}

.ex-securityOverlay.blur{
  background:rgba(15,23,42,.72);
  backdrop-filter:blur(8px);
}

.ex-securityCard{
  width:min(345px, 92vw);
  border-radius:20px;
  background:#f8fafc;
  color:#1e293b;
  text-align:center;
  padding:24px 28px 28px;
  box-shadow:0 24px 80px rgba(0,0,0,.35);
  position:relative;
  overflow:hidden;
}

.ex-securityCard.warn{
  border:1px solid #fed7aa;
}

.ex-securityCard.warn::before{
  content:"";
  position:absolute;
  inset:0 0 auto 0;
  height:7px;
  background:linear-gradient(90deg,#fb923c,#ef4444);
}

.ex-securityCard.danger{
  border:1px solid #fecaca;
}

.ex-securityCard.danger::before{
  content:"";
  position:absolute;
  inset:0 0 auto 0;
  height:7px;
  background:#ef4444;
}

.ex-securityIcon{
  width:54px;
  height:54px;
  border-radius:999px;
  margin:0 auto 18px;
  display:flex;
  align-items:center;
  justify-content:center;
  font-size:32px;
  font-weight:950;
  background:#fff;
}

.ex-securityIcon.warn{
  color:#f97316;
  border:4px solid #f97316;
}

.ex-securityIcon.danger{
  color:#ef4444;
  border:4px solid #ef4444;
}

.ex-securityCard h2{
  margin:0 0 10px;
  font-size:23px;
  line-height:1.1;
  font-weight:950;
  letter-spacing:-.03em;
  color:#1e293b;
}

.ex-securityCard p{
  margin:0 auto 16px;
  max-width:275px;
  font-size:13.5px;
  line-height:1.55;
  font-weight:750;
  color:#475569;
}

.ex-securityStrike{
  display:inline-flex;
  align-items:center;
  justify-content:center;
  padding:8px 15px;
  border-radius:10px;
  background:#fff7ed;
  border:1px solid #fdba74;
  color:#c2410c;
  font-size:12px;
  font-weight:950;
  margin:4px 0 18px;
}

.ex-securityTimer{
  font-size:42px;
  line-height:1;
  font-weight:950;
  letter-spacing:-.04em;
  color:#ef4444;
  margin-bottom:18px;
}

.ex-securityBtn{
  width:100%;
  border:none;
  border-radius:10px;
  min-height:44px;
  font-size:12px;
  font-weight:950;
  cursor:pointer;
  box-shadow:0 10px 22px rgba(15,23,42,.18);
}

.ex-securityBtn.dark{
  background:#1e293b;
  color:#fff;
}

.ex-securityBtn.danger{
  background:#e7272d;
  color:#fff;
}

.ex-securityBtn:hover{
  filter:brightness(.96);
}

.ex-deviceModal {
  width: min(760px, 96vw);
}

.ex-devicePreviewBox {
  margin: 12px 0 16px;
  padding: 16px;
  border-radius: 22px;
  border: 1px solid #dbe3ef;
  background:
    radial-gradient(circle at 20% 0%, rgba(59,130,246,.10), transparent 34%),
    linear-gradient(180deg, #ffffff 0%, #f8fafc 100%);
  box-shadow: 0 18px 45px rgba(15,23,42,.10);
}

.ex-facePreviewLayout {
  display: grid;
  grid-template-columns: 260px 1fr;
  gap: 18px;
  align-items: center;
}

.ex-facePreviewRing {
  width: 240px;
  height: 240px;
  justify-self: center;
  position: relative;
  border-radius: 999px;
  overflow: hidden;
  background: #020617;
  border: 5px solid #cbd5e1;
  box-shadow:
    0 20px 45px rgba(15,23,42,.24),
    inset 0 0 0 1px rgba(255,255,255,.08);
}

.ex-facePreviewRing.ok {
  border-color: #10b981;
  box-shadow:
    0 18px 42px rgba(16,185,129,.22),
    0 0 0 7px rgba(16,185,129,.10);
}

.ex-facePreviewRing.bad {
  border-color: #ef4444;
  box-shadow:
    0 18px 42px rgba(239,68,68,.20),
    0 0 0 7px rgba(239,68,68,.09);
}

.ex-facePreviewRing.checking {
  border-color: #3b82f6;
  box-shadow:
    0 18px 42px rgba(59,130,246,.22),
    0 0 0 7px rgba(59,130,246,.10);
}

.ex-devicePreviewVideo {
  width: 100%;
  height: 100%;
  display: block;
  border: none;
  border-radius: 999px;
  background: #020617;
  object-fit: cover;
  transform: scaleX(-1);
}

.ex-faceGuide {
  position: absolute;
  inset: 18px;
  border-radius: 999px;
  border: 1px dashed rgba(255,255,255,.72);
  pointer-events: none;
}

.ex-faceCenterDot {
  position: absolute;
  left: 50%;
  top: 50%;
  width: 6px;
  height: 6px;
  transform: translate(-50%, -50%);
  border-radius: 999px;
  background: rgba(255,255,255,.65);
  box-shadow: 0 0 0 5px rgba(255,255,255,.12);
  pointer-events: none;
}

.ex-faceScanning {
  position: absolute;
  inset: 0;
  pointer-events: none;
  background: linear-gradient(
    180deg,
    transparent 0%,
    rgba(59,130,246,.18) 48%,
    transparent 100%
  );
  animation: exScanFace 1.4s ease-in-out infinite;
}

.ex-faceScanning span {
  position: absolute;
  left: 18%;
  right: 18%;
  top: 50%;
  height: 2px;
  border-radius: 999px;
  background: rgba(147,197,253,.95);
  box-shadow: 0 0 18px rgba(59,130,246,.8);
}

@keyframes exScanFace {
  0% { transform: translateY(-42%); opacity: .35; }
  50% { transform: translateY(42%); opacity: 1; }
  100% { transform: translateY(-42%); opacity: .35; }
}

.ex-facePreviewInfo {
  min-width: 0;
  display: grid;
  gap: 12px;
}

.ex-devicePreviewStatus {
  display: grid;
  gap: 5px;
  font-size: 12px;
  color: #64748b;
  font-weight: 800;
  line-height: 1.35;
}

.ex-devicePreviewStatus b {
  font-size: 14px;
  color: #334155;
}

.ex-devicePreviewStatus.ok b {
  color: #047857;
}

.ex-devicePreviewStatus.bad b {
  color: #b91c1c;
}

.ex-devicePreviewStatus.checking b {
  color: #1d4ed8;
}

.ex-cameraChecks {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 8px;
}

.ex-cameraCheck {
  display: flex;
  align-items: center;
  gap: 7px;
  min-height: 34px;
  padding: 8px 10px;
  border-radius: 12px;
  border: 1px solid #e2e8f0;
  background: #fff;
  color: #64748b;
  font-size: 12px;
  font-weight: 850;
}

.ex-cameraCheck span {
  width: 9px;
  height: 9px;
  border-radius: 999px;
  background: #cbd5e1;
  flex: 0 0 auto;
}

.ex-cameraCheck.ok {
  border-color: #bbf7d0;
  background: #f0fdf4;
  color: #047857;
}

.ex-cameraCheck.ok span {
  background: #10b981;
}

.ex-cameraCheck.bad {
  border-color: #fecaca;
  background: #fef2f2;
  color: #b91c1c;
}

.ex-cameraCheck.bad span {
  background: #ef4444;
}

.ex-deviceReason {
  margin: 0 0 12px;
  padding: 10px 12px;
  border-radius: 14px;
  border: 1px solid #bfdbfe;
  background: #eff6ff;
  color: #1d4ed8;
  font-size: 12px;
  font-weight: 850;
  line-height: 1.35;
}

.ex-deviceReason.bad {
  border-color: #fecaca;
  background: #fef2f2;
  color: #b91c1c;
}

.ex-deviceActions {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  margin-top: 16px;
  flex-wrap: wrap;
}

.ex-btn-secondary {
  background: #1e293b;
  color: #fff;
  border-color: #1e293b;
}

.ex-btn-secondary:hover {
  filter: brightness(.96);
}

@media (max-width: 720px) {
  .ex-facePreviewLayout {
    grid-template-columns: 1fr;
  }

  .ex-facePreviewRing {
    width: 220px;
    height: 220px;
  }

  .ex-cameraChecks {
    grid-template-columns: 1fr;
  }

  .ex-deviceActions {
    justify-content: stretch;
  }

  .ex-deviceActions .ex-btn {
    flex: 1 1 100%;
  }
}

.ex-cameraSecurityBackdrop {
  z-index: 9998;
  background: rgba(15, 23, 42, .72);
  backdrop-filter: blur(8px);
}

.ex-cameraSecurityModal {
  width: min(520px, 94vw);
  border-radius: 24px;
  padding: 22px;
  background:
    radial-gradient(circle at 12% 0%, rgba(251,146,60,.18), transparent 34%),
    #ffffff;
  box-shadow: 0 28px 80px rgba(15,23,42,.38);
  border: 1px solid rgba(254,215,170,.95);
  display: grid;
  gap: 14px;
  text-align: center;
  color: #7c2d12;
}

.ex-cameraSecurityModal.no_motion {
  background:
    radial-gradient(circle at 12% 0%, rgba(59,130,246,.18), transparent 34%),
    #ffffff;
  border-color: rgba(191,219,254,.95);
  color: #1d4ed8;
}

.ex-cameraSecurityIcon {
  width: 58px;
  height: 58px;
  margin: 0 auto;
  border-radius: 999px;
  display: grid;
  place-items: center;
  font-size: 28px;
  background: #fff7ed;
  box-shadow: inset 0 0 0 1px rgba(251,146,60,.22);
}

.ex-cameraSecurityModal.no_motion .ex-cameraSecurityIcon {
  background: #eff6ff;
  box-shadow: inset 0 0 0 1px rgba(59,130,246,.22);
}

.ex-cameraSecurityContent {
  display: grid;
  gap: 8px;
}

.ex-cameraSecurityContent h2 {
  margin: 0;
  font-size: 20px;
  font-weight: 950;
  letter-spacing: -.02em;
}

.ex-cameraSecurityContent p {
  margin: 0;
  color: #334155;
  font-size: 14px;
  line-height: 1.45;
  font-weight: 800;
}

.ex-cameraSecurityDetail {
  margin: 4px auto 0;
  padding: 8px 11px;
  border-radius: 999px;
  background: rgba(255,247,237,.95);
  color: #9a3412;
  font-size: 12px;
  font-weight: 950;
}

.ex-cameraSecurityModal.no_motion .ex-cameraSecurityDetail {
  background: rgba(239,246,255,.95);
  color: #1d4ed8;
}

.ex-cameraSecurityHint {
  padding: 10px 12px;
  border-radius: 14px;
  background: #f8fafc;
  color: #64748b;
  font-size: 12px;
  font-weight: 800;
  line-height: 1.35;
}

  `

  // ===================== RENDER =====================
  if (resultado && !policyLimitVisible) {
    const detalle = Array.isArray(resultado.detalle) ? resultado.detalle : []
    const canShowReview = !!mostrarResumenFinal

    const visibleItems = canShowReview
      ? detalle.filter(it => (showOnlyWrong ? it.correcto !== true : true))
      : []

    return (
      <div className="ex-page">
        <style>{css}</style>

        <div className="ex-resultWrap">
          <AssessmentResultCard
            title="Resultado del examen"
            subtitle={
              autoClosedByPolicy
                ? `El examen se finalizó automáticamente por superar el límite de incumplimientos del modo seguro (${kioskMaxViolations}).`
                : "Tu resultado final"
            }
            resultado={resultado}
            primaryActionText="Volver al curso"
            onPrimaryAction={async () => {
              stopProctoring();
              await exitFullscreenSafe();
              navigate(`/curso/${codigoCurso}`);
            }}
            showCoins
            extraContent={null}
          />

          {canShowReview && (
            <div className="ex-review">
              {/* dejas aquí tu revisión detallada actual */}
            </div>
          )}
        </div>
      </div>
    )
  }


  // 2) Flujo normal
  const kioskFriendlyText = reasonMap[kiosk.reason] || "Vuelve al modo seguro para continuar."
  const timerUrgent = timeLeft <= 60

  const kioskTotalViolations = kiosk.getCount()
  const kioskRemainingTotal = hardKiosk
    ? Math.max(0, kioskMaxViolations - kioskTotalViolations)
    : null


  const renderTermsModal = () => {
    if (!termsModal.open) return null

    return (
      <div className="ex-modal-backdrop">
        <div className="ex-modal" style={{ maxWidth: 760 }}>
          <div className="ex-row-between" style={{ marginBottom: 12 }}>
            <div>
              <h2 style={{ margin: 0 }}>Términos y condiciones del examen</h2>
              <div className="ex-muted" style={{ marginTop: 4 }}>
                Este examen requiere cámara y micrófono. Debes aceptar antes de iniciar.
              </div>
            </div>

            <button
              type="button"
              className="ex-btn ex-btn-ghost"
              onClick={() => {
                setTermsModal({
                  open: false,
                  loading: false,
                  accepting: false,
                  data: null,
                  pendingStart: null,
                })
              }}
              disabled={termsModal.accepting}
            >
              Cerrar
            </button>
          </div>

          <div
            style={{
              maxHeight: "48vh",
              overflow: "auto",
              border: "1px solid #e5e7eb",
              borderRadius: 14,
              padding: 14,
              background: "#fff",
              whiteSpace: "pre-wrap",
              lineHeight: 1.55,
              fontSize: 14,
            }}
          >
            {termsModal.data?.terms?.contenido || "No se pudo cargar el contenido de los términos."}
          </div>

          {termsModal.data?.terms?.version ? (
            <div className="ex-muted" style={{ marginTop: 10, fontSize: 12 }}>
              Versión: {termsModal.data.terms.version}
            </div>
          ) : null}

          <div className="ex-row" style={{ justifyContent: "flex-end", marginTop: 16 }}>
            <button
              type="button"
              className="ex-btn ex-btn-ghost"
              onClick={() => {
                setTermsModal({
                  open: false,
                  loading: false,
                  accepting: false,
                  data: null,
                  pendingStart: null,
                })
              }}
              disabled={termsModal.accepting}
            >
              No acepto
            </button>

            <button
              type="button"
              className="ex-btn ex-btn-success"
              disabled={termsModal.accepting}
              onClick={async () => {
                try {
                  setTermsModal((p) => ({ ...p, accepting: true }))

                  const horarioId = Number(mySchedule?.horario_id)
                  await acceptExamTerms(horarioId)

                  const pending = termsModal.pendingStart

                  setTermsModal({
                    open: false,
                    loading: false,
                    accepting: false,
                    data: null,
                    pendingStart: null,
                  })

                  if (typeof pending === "function") {
                    await pending()
                  }
                } catch (err) {
                  setTermsModal((p) => ({ ...p, accepting: false }))
                  setError(err?.message || "No se pudo registrar la aceptación de términos.")
                }
              }}
            >
              {termsModal.accepting ? "Aceptando..." : "Acepto e iniciar examen"}
            </button>
          </div>
        </div>
      </div>
    )
  }

  const enterButtonDisabled =
    loading ||
    accessChecking ||
    codeLocked ||
    codeLockedRef.current ||
    (needsTeacherEnable && mySchedule?.habilitado !== true) ||
    (requiereCodigo && !accessVerified)

  const resumeRemainingSeconds = Math.max(0, Number(resumeOffer?.remaining_seconds || 0))
  const resumeRemainingLabel = `${Math.floor(resumeRemainingSeconds / 60)}:${String(resumeRemainingSeconds % 60).padStart(2, "0")}`


  return (
    <>
      {renderTermsModal()}

      {waitingSecurityState === "privacy" && !accesoConcedido ? (
        <div
          className="ex-securityDark"
          onClick={async () => {
            try {
              await requestFullscreenSafe()
            } catch { }

            setWaitingSecurityState("idle")
          }}
        >
          <div className="ex-privacyCenter">
            <div className="ex-lockIcon">🔒</div>
            <h2>PROTECCIÓN DE PRIVACIDAD</h2>
            <p>Contenido oculto temporalmente</p>

            <div className="ex-focusLostBox">
              <b>⚠ ¡Foco Perdido!</b>
              <span>Haz clic aquí y regresa al entorno o serás penalizado.</span>
              <strong>00:09</strong>
            </div>
          </div>
        </div>
      ) : null}

      {waitingSecurityState === "strike_alert" && !accesoConcedido ? (
        <div className="ex-securityDark">
          <div className="ex-strikeModal">
            <div className="ex-strikeTop" />
            <div className="ex-strikeIcon">⚠</div>
            <h2>¡ALERTA DE SEGURIDAD!</h2>
            <p>
              Has recibido un strike adicional por no confirmar a tiempo la alerta anterior.
            </p>

            <div className="ex-strikeBadge">
              Strike {waitingWarnings} de {waitingMaxWarnings}
            </div>

            <div className="ex-strikeCountdown">
              00:{String(waitingCountdown).padStart(2, "0")}
            </div>

            <button
              type="button"
              onClick={async () => {
                try {
                  await requestFullscreenSafe()
                } catch { }

                setWaitingSecurityState("idle")
              }}
            >
              REGRESA A LA PESTAÑA PARA CONFIRMAR
            </button>
          </div>
        </div>
      ) : null}

      {waitingSecurityState === "kicked" && !accesoConcedido ? (
        <div className="ex-securityDark">
          <div className="ex-kickedModal">
            <div className="ex-kickedTop" />
            <div className="ex-kickedIcon">⚠</div>
            <h2>Expulsado de Sala de Espera</h2>
            <p>
              Has superado el límite de advertencias ({waitingMaxWarnings}) antes de iniciar el examen debido a pérdida de foco,
              ruido o salida de pantalla completa.
              <br /><br />
              Debes preparar bien tu entorno y volver a ingresar.
            </p>

            <button
              type="button"
              onClick={() => {
                setWaitingWarnings(0)
                setWaitingCountdown(0)
                setWaitingStrikeReason("")
                setWaitingSecurityState("idle")
                setAccessVerified(false)
              }}
            >
              VOLVER A INTENTAR
            </button>
          </div>
        </div>
      ) : null}

      <div className="ex-page">
        <style>{css}</style>

        <div className="ex-shell">
          {showLoading ? (
            <div className="ex-card" style={{ textAlign: "center", padding: 26 }}>
              <div className="ex-strong">Cargando…</div>
              <div className="ex-muted" style={{ marginTop: 6 }}>Preparando tu examen</div>
            </div>
          ) : (
            <>

              {/* Tiempo agotado */}
              {waitingAfterTimeout && !accesoConcedido && (
                <div className="ex-card ex-banner ex-banner-warn">
                  <div className="ex-strong" style={{ fontSize: 18 }}>⏱️ Tiempo agotado</div>
                  <div style={{ marginTop: 4 }}>
                    Tu examen fue enviado automáticamente y está <b>pendiente de calificación</b>.
                  </div>
                  {mySchedule?.presentacion?.id && (
                    <div className="ex-muted" style={{ marginTop: 8 }}>
                      Presentación #{mySchedule.presentacion.id}
                    </div>
                  )}
                </div>
              )}

              {/* Reanudar intento */}
              {resumeOffer && !accesoConcedido && !waitingAfterTimeout && (
                <div className="ex-secureWaitShell">
                  <div className="ex-secureWaitCard">
                    <div className="ex-secureWaitHead">
                      <div className="ex-secureIcon">↺</div>
                      <div>
                        <div className="ex-secureKicker" style={{ color: "#047857" }}>INTENTO EN CURSO</div>
                        <h1>Continuar examen</h1>
                      </div>
                    </div>

                    <div className="ex-secureResumeNotice" style={{ marginBottom: 28 }}>
                      <div className="ex-secureResumeTop">
                        <div>
                          <div className="ex-strong" style={{ fontSize: 18 }}>
                            Retoma tu examen exactamente donde lo dejaste
                          </div>
                          <div className="ex-muted" style={{ marginTop: 6 }}>
                            Como este intento ya fue iniciado, no volveremos a pedir código, aprobación ni validación previa de cámara en esta pantalla.
                          </div>
                        </div>

                        <div className="ex-secureResumeMeta">
                          <span className="ex-secureResumeChip">
                            Presentación #{resumeOffer.presentacion_id}
                          </span>
                          <span className="ex-secureResumeChip">
                            Tiempo restante {resumeRemainingLabel}
                          </span>
                        </div>
                      </div>

                      <div className="ex-secure-steps">
                        <div className="ex-secure-step">
                          <span>1</span>
                          <span>Al continuar, volverás directo al examen en curso.</span>
                        </div>
                        <div className="ex-secure-step">
                          <span>2</span>
                          <span>El proctoring seguirá registrando fotos y evidencias durante el intento.</span>
                        </div>
                      </div>

                      <div className="ex-secureResumeActions">
                        <button className="ex-btn ex-btn-success" onClick={handleAcceptResume}>
                          Continuar intento
                        </button>
                        <button className="ex-btn ex-btn-danger" onClick={handleDiscardResume}>
                          Descartar e iniciar nuevo
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Acceso nuevo */}
              {!accesoConcedido && !resumeOffer && (
                <div className="ex-secureWaitShell">
                  <div className="ex-secureWaitCard">
                    <div className="ex-secureWaitHead">
                      <div className="ex-secureIcon">🛡️</div>
                      <div>
                        <div className="ex-secureKicker">SALA DE ESPERA SEGURA</div>
                        <h1>{course?.nombre || examen?.nombre || mySchedule?.curso_nombre || "Examen"}</h1>
                      </div>
                    </div>

                    <div className="ex-secureWaitBody">
                      <section className="ex-secureCol">
                        <div className="ex-stepTitle">
                          <span>1</span>
                          Hardware Validado
                        </div>

                        <div className="ex-hardwareBox">
                          <div className="ex-hardwareRow">
                            <span>📷 Cámara Web</span>
                            <b className={waitingCamOk ? "ok" : requireCamera ? "bad" : "muted"}>
                              {requireCamera
                                ? waitingCamOk ? "Activa" : "Requerida"
                                : optionalCamera ? "Opcional" : "No requerida"}
                            </b>
                          </div>

                          <div className="ex-hardwareRow">
                            <span>🎙️ Micrófono</span>
                            <b className={waitingMicOk ? "ok" : requireMicrophone ? "bad" : "muted"}>
                              {requireMicrophone
                                ? waitingMicOk ? "Activo" : "Requerido"
                                : optionalMicrophone ? "Opcional" : "No requerido"}
                            </b>
                          </div>

                          {requireProctor ? (
                            <div className="ex-devicePermsHint">
                              <div>
                                Permiso cámara: <b>{mediaPerms.camState}</b>
                              </div>
                              <div>
                                Permiso micrófono: <b>{mediaPerms.micState}</b>
                              </div>
                              {mediaPerms.error ? (
                                <div className="bad">{mediaPerms.error}</div>
                              ) : null}
                            </div>
                          ) : null}

                          {requireProctor ? (
                            <button
                              type="button"
                              className="ex-secureSmallBtn"
                              onClick={() => openPermissionAndDeviceSetup({ force: true })}
                              disabled={loading || mediaPerms.loading}
                            >
                              {mediaPerms.loading
                                ? "Solicitando permisos…"
                                : requireCamera && requireMicrophone
                                  ? "Habilitar cámara y micrófono"
                                  : requireCamera
                                    ? "Habilitar cámara"
                                    : requireMicrophone
                                      ? "Habilitar micrófono"
                                      : "Configurar dispositivos"}
                            </button>
                          ) : null}
                        </div>
                      </section>

                      <section className="ex-secureCol">
                        <div className="ex-stepTitle">
                          <span>2</span>
                          Autorización de Ingreso
                        </div>

                        <div className={`ex-approvalBox ${needsTeacherEnable && mySchedule?.habilitado !== true ? "bad" : "ok"}`}>
                          <div className="ex-approvalIcon">
                            {needsTeacherEnable && mySchedule?.habilitado !== true ? "!" : "✓"}
                          </div>
                          <div>
                            <b>Aprobación del Docente</b>
                            <p>
                              {needsTeacherEnable
                                ? mySchedule?.habilitado === true
                                  ? "Acceso verificado"
                                  : "Pendiente de habilitación"
                                : "No requerida"}
                            </p>
                          </div>
                        </div>

                        {toBool(mySchedule?.requiere_codigo) ? (
                          <div className="ex-codeBox">
                            <label>Código de Verificación</label>
                            <div className="ex-codeRow">
                              <input
                                value={codigo}
                                onChange={(e) => {
                                  const next = e.target.value
                                    .toUpperCase()
                                    .replace(/[^A-Z0-9]/g, "")
                                    .slice(0, 10)

                                  setCodigo(next)
                                  setAccessVerified(false)
                                  lastValidatedCodeRef.current = ""

                                  if (!codeLockedRef.current && !codeLocked) {
                                    setCodeValidationState("idle")
                                    setCodeValidationMessage("")
                                  }
                                }}
                                placeholder="Escribe el código..."
                                disabled={
                                  loading ||
                                  accessChecking ||
                                  codeLocked ||
                                  codeLockedRef.current ||
                                  waitingTeacherEnable
                                }
                                maxLength={10}
                              />

                              <button
                                type="button"
                                onClick={() => verifySecureAccess({ force: true })}
                                disabled={
                                  accessChecking ||
                                  validateCodeInFlightRef.current ||
                                  codeLockedRef.current ||
                                  codeLocked ||
                                  accessVerified ||
                                  waitingTeacherEnable ||
                                  !String(codigo || "").trim()
                                }
                              >
                                {accessChecking || validateCodeInFlightRef.current
                                  ? "Validando…"
                                  : codeLocked || codeLockedRef.current
                                    ? codeLockLabel
                                    : accessVerified
                                      ? "Validado"
                                      : "Validar"}
                              </button>
                            </div>

                            <div className="ex-codeStatusArea">
                              {waitingTeacherEnable ? (
                                <div className="ex-codeStatus checking">
                                  <span>…</span>
                                  <div>
                                    <b>Esperando habilitación</b>
                                    <p>Cuando el docente te habilite en la sala de espera, aquí podrás validar tu código.</p>
                                  </div>
                                </div>
                              ) : null}

                              {codeValidationState === "checking" ? (
                                <div className="ex-codeStatus checking">
                                  <span>…</span>
                                  <div>
                                    <b>Validando código</b>
                                    <p>Estamos verificando el código de acceso.</p>
                                  </div>
                                </div>
                              ) : null}

                              {codeValidationState === "ok" ? (
                                <div className="ex-codeStatus ok">
                                  <span>✓</span>
                                  <div>
                                    <b>Código validado</b>
                                    <p>{codeValidationMessage || "Puedes continuar con el ingreso al examen."}</p>
                                  </div>
                                </div>
                              ) : null}

                              {codeValidationState === "error" ? (
                                <div className="ex-codeStatus bad">
                                  <span>!</span>
                                  <div>
                                    <b>Código incorrecto</b>
                                    <p>{codeValidationMessage}</p>
                                  </div>
                                </div>
                              ) : null}

                              {codeValidationState === "locked" || codeLocked || codeLockedRef.current ? (
                                <div className="ex-codeStatus locked">
                                  <span>⏱</span>
                                  <div>
                                    <b>Validación bloqueada</b>
                                    <p>
                                      Podrás volver a intentar en <strong>{codeLockLabel}</strong>.
                                    </p>
                                  </div>
                                </div>
                              ) : null}

                              {codeValidationState === "idle" && !accessVerified && !codeLocked && !codeLockedRef.current ? (
                                <div className="ex-codeAttempts">
                                  <div className="ex-codeAttemptsTop">
                                    <span>Intentos disponibles</span>
                                    <b>{codeAttemptsRemaining}/3</b>
                                  </div>

                                  <div className="ex-codeDots">
                                    {[1, 2, 3].map((n) => (
                                      <i
                                        key={n}
                                        className={n <= codeAttemptsRemaining ? "on" : "off"}
                                      />
                                    ))}
                                  </div>
                                </div>
                              ) : null}
                            </div>
                          </div>
                        ) : (
                          <div className="ex-codeBox">
                            <label>Código de Verificación</label>
                            <div className="ex-codeOk">Este horario no requiere código.</div>
                          </div>
                        )}
                      </section>
                    </div>

                    {error ? <div className="ex-secureError">{error}</div> : null}

                    <div className="ex-secureFooter">
                      <button
                        type="button"
                        className="ex-secureEnterBtn"
                        onClick={enterSecureExam}
                        disabled={enterButtonDisabled}
                      >
                        {loading ? "Preparando…" : "INGRESAR AL EXAMEN"} <span>▷</span>
                      </button>
                    </div>
                  </div>
                </div>
              )}


              {/* Ejecución */}
              {accesoConcedido && examen && !waitingAfterTimeout && (
                <div className="ex-layout" style={{ position: "relative", overflow: "hidden" }}>
                  {/* Watermark de seguridad */}
                  <div
                    style={{
                      position: "absolute",
                      top: "-20%", left: "-20%", right: "-20%", bottom: "-20%",
                      pointerEvents: "none",
                      zIndex: 9999,
                      overflow: "hidden",
                      display: "flex",
                      flexWrap: "wrap",
                      justifyContent: "center",
                      alignContent: "center",
                      gap: "70px",
                      opacity: 0.04,
                      transform: "rotate(-25deg)",
                      userSelect: "none",
                    }}
                  >
                    {Array.from({ length: 300 }).map((_, i) => (
                      <span key={i} style={{
                        fontSize: "24px",
                        fontWeight: "900",
                        whiteSpace: "nowrap",
                        color: "#000",
                        fontFamily: "monospace",
                        letterSpacing: "1px",
                        textTransform: "uppercase"
                      }}>
                        {effectiveStudentIdentity.codigo || effectiveStudentIdentity.email || effectiveStudentIdentity.fullName}
                      </span>
                    ))}
                  </div>
                  {/* Overlay bloqueo por proctor/kiosk */}
                  {interactionBlocked && blockedByProctor && !isUnloadingRef.current && (
                    <div className="ex-overlay">
                      <div className="ex-modal" role="dialog" aria-modal="true">
                        <div className="ex-modalTitle" style={{ color: "#991b1b" }}>
                          {cameraWarningText
                            ? "⚠️ Advertencia de cámara"
                            : microphoneWarningText
                              ? "⚠️ Advertencia de micrófono"
                              : "🛑 Cámara o micrófono desactivados"}
                        </div>
                        <div className="ex-modalBody">
                          {cameraWarningText ? (
                            <>
                              Se detectó un problema con la cámara durante el examen.
                              <br />
                              Motivo: <em>{cameraWarningText}</em>
                            </>
                          ) : microphoneWarningText ? (
                            <>
                              Se detectó un problema con el micrófono durante el examen.
                              <br />
                              Motivo: <em>{microphoneWarningText}</em>
                            </>
                          ) : (
                            <>
                              Para continuar el examen virtual, debes mantener <b>cámara y micrófono activos</b>.
                              {proctor.reason ? <> <br />Motivo: <em>{proctor.reason}</em></> : null}
                            </>
                          )}
                        </div>

                        {/* Selector de dispositivos si bloqueo viene del proctor */}
                        {requireProctor && proctor.blocked && (
                          <div style={{ marginTop: 12, display: "grid", gap: 10 }}>
                            {blockedByProctor && (
                              <button type="button" className="ex-btn ex-btn-ghost" onClick={openDeviceModal}>
                                Elegir cámara y micrófono
                              </button>
                            )}
                          </div>
                        )}

                        <ul className="ex-modalList">
                          {blockedByProctor ? (
                            <>
                              {cameraWarningText ? (
                                <>
                                  <li>Mejora la iluminación del lugar si la imagen se ve oscura.</li>
                                  <li>Muévete ligeramente frente a la cámara para confirmar que no está congelada.</li>
                                  <li>Evita tapar la cámara o dejarla apuntando a una zona sin rostro.</li>
                                </>
                              ) : microphoneWarningText ? (
                                <>
                                  <li>Reduce el ruido del entorno para evitar nuevas penalizaciones.</li>
                                  <li>Evita conversaciones paralelas, música o audio externo cerca del micrófono.</li>
                                  <li>Verifica que el micrófono correcto esté seleccionado antes de continuar.</li>
                                </>
                              ) : (
                                <>
                                  <li>Activa permisos de cámara y micrófono en tu navegador.</li>
                                  <li>Verifica que no estén siendo usados por otra app (Zoom/Meet/Discord).</li>
                                  <li>Selecciona los dispositivos correctos y pulsa “Reintentar dispositivos”.</li>
                                </>
                              )}
                            </>
                          ) : (
                            <>
                              <li>Mantén el examen en pantalla completa.</li>
                              <li>No cambies de pestaña/ventana ni minimices.</li>
                              <li>No uses atajos de teclado de navegación/copia.</li>
                              <li>Pulsa “Volver a pantalla completa” para continuar.</li>
                            </>
                          )}
                        </ul>

                        <div className="ex-row-between" style={{ marginTop: 12 }}>
                          <div className="ex-muted" style={{ fontSize: 12 }}>
                            {blockedByKiosk ? (
                              <>
                                Incumplimientos: <b>{kioskTotalViolations}</b>
                              </>
                            ) : (
                              <>Proctoring requiere cámara y micrófono activos.</>
                            )}
                          </div>

                          <div className="ex-row" style={{ justifyContent: "flex-end" }}>
                            {blockedByKiosk && (
                              <button
                                className="ex-btn ex-btn-primary"
                                onClick={async () => {
                                  try {
                                    await kiosk.requestFullscreen()
                                    kiosk.grantGrace(1500)
                                    setKioskWarn(false)
                                  } catch { }
                                }}
                              >
                                Volver a pantalla completa
                              </button>
                            )}
                            {blockedByProctor && (
                              <button className="ex-btn ex-btn-primary" onClick={retryProctoring}>
                                Reintentar dispositivos
                              </button>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Columna principal */}
                  <div className="ex-main" aria-hidden={interactionBlocked} style={{ filter: interactionBlocked ? "grayscale(0.2)" : "none" }}>
                    {/* Barra de estado */}
                    <div className="ex-status">
                      <div className="ex-row-between">
                        <div className="ex-timebox">
                          <span className={`ex-timer ${timerUrgent ? "ex-urgent" : ""}`}>{minutosSegundos}</span>
                          <span className="ex-muted" style={{ fontSize: 12 }}>
                            {answeredCount}/{totalQuestions} respondidas
                          </span>
                        </div>


                        <button
                          className="ex-btn ex-btn-success"
                          onClick={() => askConfirm("Enviar ahora", "¿Estás seguro de enviar ahora? No podrás editar tus respuestas.", () => { closeConfirm(); doSubmit(false) })}
                          disabled={enviado || interactionBlocked}
                        >
                          {enviado ? "Enviado" : "Enviar ahora"}
                        </button>
                      </div>

                      <div style={{ marginTop: 10 }}>
                        {/* Progreso de respuestas */}
                        <div style={{ marginTop: 10 }}>
                          <div className="ex-muted" style={{ fontSize: 12, marginBottom: 6 }}>
                            Progreso de respuestas
                          </div>
                          <div className="ex-progress">
                            <div style={{ width: `${answeredPct}%` }} />
                          </div>
                          <div className="ex-meta">
                            <span>Respondidas: {answeredCount}/{totalQuestions}</span>
                            <span className="ex-muted">
                              {enableKiosk
                                ? `Modo seguro ${kiosk.blocked ? "• bloqueado" : "• activo"}`
                                : "Modo seguro • inactivo"}
                            </span>
                          </div>
                        </div>

                      </div>
                    </div>

                    {/* Navegación superior */}
                    <div className="ex-navline">
                      <button
                        type="button"
                        className="ex-btn ex-btn-ghost"
                        onMouseDown={() => grantExamUiGrace(1200)}
                        onTouchStart={() => grantExamUiGrace(1200)}
                        onClick={() => goTo(currentIndex - 1)}
                        disabled={enviado || currentIndex <= 0 || interactionBlocked || !permitirNavegacion}
                      >
                        ← Anterior
                      </button>

                      <div className="ex-qpos">
                        Pregunta {currentIndex + 1} de {totalQuestions}
                      </div>

                      <button
                        type="button"
                        className="ex-btn ex-btn-ghost"
                        onMouseDown={() => grantExamUiGrace(1200)}
                        onTouchStart={() => grantExamUiGrace(1200)}
                        onClick={() => goTo(currentIndex + 1)}
                        disabled={
                          enviado ||
                          currentIndex >= totalQuestions - 1 ||
                          interactionBlocked ||
                          (!permitirNavegacion && !currentHasAnswer)
                        }
                      >
                        Siguiente →
                      </button>

                    </div>

                    {/* Pregunta actual */}
                    {examen?.preguntas?.[currentIndex] && (
                      <div className="ex-questionCard">
                        <PreguntaExamTestRenderer
                          key={`${examen.preguntas[currentIndex].id}__${currentIndex}`}
                          question={examen.preguntas[currentIndex]}
                          value={answers[examen.preguntas[currentIndex].id]}
                          onChange={(val) => setAnswer(examen.preguntas[currentIndex].id, val)}
                        />
                      </div>
                    )}
                  </div>

                  {/* Columna lateral */}
                  <aside className="ex-aside">
                    <div className="ex-card" style={{ padding: 12 }}>
                      <div className="ex-strong" style={{ marginBottom: 10 }}>Navegación</div>
                      <div className="ex-navgrid">
                        {examen?.preguntas?.map((q, i) => {
                          const v = answers[q.id]
                          const done = Array.isArray(v)
                            ? v.length > 0
                            : (v !== null && v !== undefined && String(v).trim() !== "")

                          const cls = [
                            "ex-qbtn",
                            i === currentIndex ? "is-current" : "",
                            done ? "is-done" : "",
                          ].filter(Boolean).join(" ")
                          return (
                            <button
                              key={`${q.id}__${i}`}
                              className={cls}
                              onClick={() => goTo(i)}
                              disabled={interactionBlocked || (!permitirNavegacion && i !== currentIndex)}
                              title={
                                permitirNavegacion
                                  ? `Ir a pregunta ${i + 1}`
                                  : i === currentIndex
                                    ? `Pregunta actual ${i + 1}`
                                    : "La navegación libre está deshabilitada para este examen"
                              }
                            >
                              {i + 1}
                            </button>
                          )
                        })}
                      </div>

                      <div className="ex-muted" style={{ fontSize: 12, marginTop: 10 }}>
                        {permitirNavegacion
                          ? "Navegación libre: puedes volver, avanzar o saltar a cualquier pregunta."
                          : "Navegación secuencial: responde y avanza con “Siguiente”. No podrás volver a preguntas anteriores."}
                      </div>

                    </div>



                    {/* Proctor preview */}
                    {requireProctor && (
                      <div className="ex-card" style={{ marginTop: 12, padding: 12 }}>
                        <div className="ex-row-between">
                          <div className="ex-strong">Vigilancia y dispositivos</div>
                          <span className={`ex-tag ${proctor.blocked ? "bad" : "ok"}`}>
                            {proctor.blocked ? "Requiere atención" : "Activo"}
                          </span>
                        </div>

                        <div style={{ marginTop: 10 }}>
                          <video ref={proctorVideoRef} className="ex-proctorVideo" muted playsInline />

                          {runtimeWarningText ? (
                            <div className="ex-proctor-note ex-proctor-note-warn">
                              {runtimeWarningText}
                              {cameraHealth.avgLuma != null ? ` · Luz: ${cameraHealth.avgLuma}/255` : ""}
                              {cameraHealth.motionScore != null ? ` · Movimiento: ${cameraHealth.motionScore}` : ""}
                            </div>
                          ) : null}

                          <div className="ex-micbar" aria-label="Nivel de micrófono">
                            <div style={{ width: `${Math.max(0, Math.min(100, micLevel || 0))}%` }} />
                          </div>
                          <div className="ex-micmeta">
                            <span>Mic: <b>{micLevel}%</b></span>
                            <span>{proctor.cam ? "Cam ✅" : "Cam ❌"} · {proctor.mic ? "Mic ✅" : "Mic ❌"}</span>
                            {proctor.reason ? (
                              <div className="ex-proctor-note" style={{ marginTop: 6, color: "#b45309" }}>
                                {proctor.reason}
                              </div>
                            ) : null}
                          </div>

                          <div className="ex-proctor-note">
                            Cámara activa: <b>{activeCamLabel}</b>
                          </div>
                          <div className="ex-proctor-note" style={{ marginTop: 4 }}>
                            Micrófono activo: <b>{activeMicLabel}</b>
                          </div>

                        </div>
                      </div>
                    )}
                  </aside>
                </div>
              )}

              <ConfirmActionModal
                open={confirm.open}
                title={confirm.title}
                message={confirm.message}
                confirmText="Sí, enviar"
                cancelText="Cancelar"
                tone="success"
                onClose={closeConfirm}
                onConfirm={() => {
                  const fn = confirm.onConfirm;
                  closeConfirm();
                  fn?.();
                }}
              />

              {/* Aviso suave (kioskWarn) */}
              {kioskWarn && !autoClosedByPolicy ? (
                <div className="ex-securityOverlay">
                  <div className="ex-securityCard warn">
                    <div className="ex-securityIcon warn">!</div>

                    <h2>{kioskWarnModeRef.current === "camera" ? "¡ATENCIÓN: CÁMARA!" : "¡ALERTA DE SEGURIDAD!"}</h2>

                    <p>
                      {kioskWarnModeRef.current === "camera"
                        ? (kioskWarnReason === "camera_dark" ? "La cámara se ve muy oscura. Por favor, mejora la iluminación."
                          : kioskWarnReason === "camera_no_motion" ? "No se detecta movimiento. Muévete ligeramente para verificar tu presencia."
                            : "Asegúrate de mirar de frente a la cámara y que tu rostro esté visible.")
                        : kioskWarnReason === "microphone_noise"
                          ? "Se detectó ruido elevado o constante en el micrófono. Reduce el ruido del entorno para evitar penalizaciones."
                          : "Has perdido el foco de la pantalla, intentado abrir otra ventana o activado el menú de sistema."}
                    </p>

                    <div className="ex-securityStrike">
                      Strike {Math.min(kiosk.getCount?.() || 1, kioskMaxViolations)} de {kioskMaxViolations}
                    </div>

                    <div className="ex-securityTimer">
                      00:{String(Math.max(0, kioskWarnCountdown || 0)).padStart(2, "0")}
                    </div>

                    <button
                      type="button"
                      className="ex-securityBtn dark"
                      onMouseDown={() => {
                        try {
                          kiosk.grantGrace?.(1000)
                        } catch { }
                      }}
                      onClick={async () => {
                        try {
                          kiosk.grantGrace?.(2000)
                          kioskWarnReturningRef.current = Date.now() + 2000
                        } catch { }

                        try {
                          await kiosk.requestFullscreen?.()
                        } catch { }

                        closeKioskWarning()
                      }}
                    >
                      REGRESA A LA PESTAÑA PARA CONFIRMAR
                    </button>
                  </div>
                </div>
              ) : null}

              {policyLimitVisible ? (
                <div className="ex-securityOverlay">
                  <div className="ex-securityCard danger">
                    <div className="ex-securityIcon danger">×</div>

                    <h2>Límite de Infracciones</h2>

                    <p>
                      Has superado el número máximo de advertencias permitidas ({kioskMaxViolations}).
                    </p>

                    <p>
                      Tu sesión será <b>anulada y reprobada</b> automáticamente por el sistema de seguridad.
                    </p>

                    <button
                      type="button"
                      className="ex-securityBtn danger"
                      disabled={policySubmitting}
                      onClick={confirmPolicyLimitAndSubmit}
                    >
                      {policySubmitting ? "FINALIZANDO…" : "ACEPTAR Y VER RESULTADOS"}
                    </button>
                  </div>
                </div>
              ) : null}

              {deviceModalOpen && (
                <div className="ex-fixedOverlay">
                  <div className="ex-fixedModal ex-deviceModal" role="dialog" aria-modal="true">
                    <div className="ex-modalTitle">Elegir cámara y micrófono</div>

                    <div className="ex-modalBody" style={{ marginBottom: 12 }}>
                      Selecciona tu cámara y micrófono. Primero revisa la vista circular, luego valida la cámara. Cuando la validación sea correcta, podrás usar estos dispositivos para ingresar al examen.
                    </div>

                    <div className="ex-devicePreviewBox">
                      <div className="ex-facePreviewLayout">
                        <div
                          className={[
                            "ex-facePreviewRing",
                            getCameraValidationTone(cameraHealth),
                          ].filter(Boolean).join(" ")}
                        >
                          <video
                            ref={devicePreviewVideoRef}
                            className="ex-devicePreviewVideo"
                            muted
                            playsInline
                            autoPlay
                          />

                          <div className="ex-faceGuide" />
                          <div className="ex-faceCenterDot" />

                          {cameraHealth?.validating ? (
                            <div className="ex-faceScanning">
                              <span />
                            </div>
                          ) : null}
                        </div>

                        <div className="ex-facePreviewInfo">
                          <div className={[
                            "ex-devicePreviewStatus",
                            getCameraValidationTone(cameraHealth),
                          ].filter(Boolean).join(" ")}>
                            <b>{getCameraValidationLabel(cameraHealth)}</b>

                            <span>
                              Coloca tu rostro dentro del círculo, mira de frente y muévete ligeramente.
                            </span>
                          </div>

                          <div className="ex-cameraChecks">
                            <div className={[
                              "ex-cameraCheck",
                              cameraCheckOk(cameraHealth?.videoReady) ? "ok" : "",
                              cameraCheckBad(cameraHealth?.videoReady) ? "bad" : "",
                            ].join(" ")}>
                              <span />
                              Imagen activa
                            </div>

                            <div className={[
                              "ex-cameraCheck",
                              cameraHealth?.avgLuma != null && cameraHealth.avgLuma >= CAMERA_HEALTH_LIMITS.stableLumaMin && !cameraHealth.dark ? "ok" : "",
                              cameraHealth?.dark ? "bad" : "",
                            ].join(" ")}>
                              <span />
                              Luz suficiente
                              {cameraHealth?.avgLuma != null ? ` (${cameraHealth.avgLuma}/255)` : ""}
                            </div>

                            <div className={[
                              "ex-cameraCheck",
                              cameraHealth?.contrast != null && cameraHealth.contrast >= CAMERA_HEALTH_LIMITS.contrastMin && !cameraHealth.lowContrast ? "ok" : "",
                              cameraHealth?.lowContrast ? "bad" : "",
                            ].join(" ")}>
                              <span />
                              Imagen clara
                              {cameraHealth?.contrast != null ? ` (${cameraHealth.contrast})` : ""}
                            </div>

                            <div className={[
                              "ex-cameraCheck",
                              cameraHealth?.faceVisible === true ? "ok" : "",
                              cameraHealth?.faceChecked && cameraHealth?.faceVisible === false ? "bad" : "",
                            ].join(" ")}>
                              <span />
                              {String(cameraHealth?.faceSource || "").includes("quality_fallback")
                                ? "Encuadre visual aprobado"
                                : "Rostro visible"}
                            </div>

                            <div className={[
                              "ex-cameraCheck",
                              Number(cameraHealth?.motionScore || 0) >= CAMERA_HEALTH_LIMITS.motionDiffMin || Number(cameraHealth?.motionHits || 0) > 0 ? "ok" : "",
                            ].join(" ")}>
                              <span />
                              Movimiento leve
                              {cameraHealth?.motionScore != null ? ` (${cameraHealth.motionScore})` : ""}
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>

                    {proctorSetupReason ? (
                      <div className="ex-deviceReason">
                        {proctorSetupReason}
                      </div>
                    ) : null}

                    {error ? (
                      <div className="ex-deviceReason bad">
                        {error}
                      </div>
                    ) : null}

                    <div style={{ display: "grid", gap: 12 }}>
                      <label style={{ fontSize: 13, display: "grid", gap: 6 }}>
                        Cámara
                        <select
                          className="ex-input"
                          value={deviceDraft.camId}
                          onChange={(e) => {
                            const next = {
                              ...deviceDraft,
                              camId: e.target.value,
                            }

                            invalidateCurrentDeviceValidation();
                            setCameraValidationStep("preview")
                            setProctorSetupReason("Cambiando cámara. Cuando veas tu rostro dentro del círculo, valida la cámara.")
                            setDeviceDraft(next)
                            scheduleDevicePreviewChange(next)
                          }}
                          disabled={deviceSwitching.cam || deviceSwitching.mic}
                        >
                          <option value="">Automática (predeterminada)</option>
                          {(deviceOpts.cams || []).map((d) => (
                            <option key={d.deviceId} value={d.deviceId}>
                              {d.label || "Cámara"}
                            </option>
                          ))}
                        </select>
                      </label>

                      <label style={{ fontSize: 13, display: "grid", gap: 6 }}>
                        Micrófono
                        <select
                          className="ex-input"
                          value={deviceDraft.micId}
                          onChange={(e) => {
                            const next = {
                              ...deviceDraft,
                              micId: e.target.value,
                            }

                            invalidateCurrentDeviceValidation();
                            setCameraValidationStep("preview")
                            setProctorSetupReason("Cambiando dispositivos. Espera la vista previa antes de validar.")
                            setDeviceDraft(next)
                            scheduleDevicePreviewChange(next)
                          }}
                          disabled={deviceSwitching.cam || deviceSwitching.mic}
                        >
                          <option value="">Automático (predeterminado)</option>
                          {(deviceOpts.mics || []).map((d) => (
                            <option key={d.deviceId} value={d.deviceId}>
                              {d.label || "Micrófono"}
                            </option>
                          ))}
                        </select>
                      </label>
                    </div>

                    <div className="ex-deviceActions">
                      <button
                        type="button"
                        className="ex-btn ex-btn-ghost"
                        onClick={() => setDeviceModalOpen(false)}
                        disabled={deviceSwitching.cam || deviceSwitching.mic || cameraHealth?.validating}
                      >
                        Cancelar
                      </button>

                      <button
                        type="button"
                        className="ex-btn ex-btn-secondary"
                        onClick={validateCameraFromModal}
                        disabled={
                          deviceSwitching.cam ||
                          deviceSwitching.mic ||
                          cameraHealth?.validating ||
                          !cameraHealth?.videoReady
                        }
                      >
                        {cameraHealth?.validating
                          ? "Validando…"
                          : cameraHealth?.validated
                            ? "Validar otra vez"
                            : "Validar cámara"}
                      </button>

                      <button
                        type="button"
                        className="ex-btn ex-btn-primary"
                        onClick={confirmDeviceModal}
                        disabled={
                          deviceSwitching.cam ||
                          deviceSwitching.mic ||
                          cameraHealth?.validating ||
                          (requireCamera && cameraHealth?.validated !== true)
                        }
                        title={
                          requireCamera && cameraHealth?.validated !== true
                            ? "Primero valida la cámara"
                            : "Usar estos dispositivos"
                        }
                      >
                        Usar estos dispositivos
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {/* Confirm modal */}
              {confirm.open && (
                <div className="ex-fixedOverlay">
                  <div className="ex-fixedModal" role="dialog" aria-modal="true">
                    <div className="ex-modalTitle">{confirm.title}</div>
                    <div className="ex-modalBody" style={{ marginBottom: 12 }}>{confirm.message}</div>
                    <div className="ex-row" style={{ justifyContent: "flex-end" }}>
                      <button onClick={() => setConfirm((c) => ({ ...c, open: false }))} className="ex-btn ex-btn-ghost">
                        Cancelar
                      </button>
                      <button onClick={() => confirm.onConfirm?.()} className="ex-btn ex-btn-success">
                        Confirmar
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </>
  )
}
